import { initializeApp } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-app.js";
import { initializeAppCheck, ReCaptchaV3Provider } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-app-check.js";
import { getAuth, setPersistence, browserSessionPersistence, signInWithPopup, signInWithCredential, GoogleAuthProvider, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, EmailAuthProvider, PhoneAuthProvider, linkWithCredential, reauthenticateWithCredential, reauthenticateWithPopup, updatePassword, deleteUser, signOut, sendPasswordResetEmail, RecaptchaVerifier, signInWithPhoneNumber, fetchSignInMethodsForEmail } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-auth.js";
import { getFirestore, doc, getDoc, setDoc, query, where, collection, getDocs, deleteDoc, updateDoc, onSnapshot, getDocFromServer, limit, deleteField, addDoc, serverTimestamp } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-firestore.js";
import { getStorage, ref, uploadString, getDownloadURL, deleteObject } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-storage.js";
import { getFunctions, httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-functions.js";
import { getAnalytics } from "https://www.gstatic.com/firebasejs/10.12.1/firebase-analytics.js";
import { registerPasskey, authenticatePasskey, isPasskeySupported, storeBiometricVaultKey, retrieveBiometricVaultKey, clearBiometricVaultKey } from "./webauthn-service.js";
import CosmoCryptoEngine from "./crypto-engine.js";

// Enable App Check debug token in local development (localhost / 127.0.0.1)
if (typeof location !== 'undefined' && (location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname === '')) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

const firebaseConfig = {
    apiKey: "AIzaSyBw3PjMI2XK9utWuq7LEItJAy1hDaHh_GA",
    authDomain: "cosmocrypt.com",
    projectId: "cosmo-crypt",
    storageBucket: "cosmo-crypt.firebasestorage.app",
    messagingSenderId: "937571060072",
    appId: "1:937571060072:web:bb492ff244247fc0942edd",
    measurementId: "G-MTPFVZSCZ0"
};

const app = initializeApp(firebaseConfig);

// 🛡️ Initialize Firebase App Check (uses Debug Provider in local dev, reCAPTCHA v3 in production)
let appCheck = null;
try {
    appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider("6Ld-kZAtAAAAAAT3Gj7n7vbsdg6vgU5_HDYFyRsv"),
        isTokenAutoRefreshEnabled: true
    });
    window.suiteAppCheck = appCheck;
} catch (e) {
    console.warn("Firebase App Check initialization warning:", e);
}

const analytics = getAnalytics(app);
const auth = getAuth(app);

// 🔒 Session Persistence: Ensure session is cleared when browser/tab is closed
try {
    setPersistence(auth, browserSessionPersistence).catch((err) => {
        console.warn("Session persistence note:", err);
    });
} catch (e) {
    console.warn("Session persistence initialization warning:", e);
}
const db = getFirestore(app);
const storage = getStorage(app);
window.suiteStorage = storage;
window.suiteStorageMethods = { ref, uploadString, getDownloadURL };
const functions = getFunctions(app, 'us-central1');

let _volatileSessionKey = null;

function _getAppSecret() {
    try {
        if (typeof window !== 'undefined') {
            if (typeof _volatileSessionKey !== 'undefined' && _volatileSessionKey) return _volatileSessionKey;
            if (false) {
                return null;
            }
            if (window.parent && window.parent !== window && window.parent._getAppSecret && window.parent._getAppSecret !== _getAppSecret) {
                return window.parent._getAppSecret();
            }
        }

        if (typeof sessionStorage !== 'undefined') {
            let sessionKey = sessionStorage.getItem('__cc_session_token');
            if (sessionKey) {
                _volatileSessionKey = sessionKey;
                return sessionKey;
            }
        }

        let enc = null; // Issue 8: disabled reading insecure localStorage
        if (enc) {
            try {
                let dec = atob(enc.split('').reverse().join(''));
                let key = dec.replace(navigator.userAgent || '', '');
                if (key && key.length >= 16) {
                    _volatileSessionKey = key;
                    return key;
                }
            } catch (e) { }
        }

        let newEntropy = '';
        if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
            const bytes = new Uint8Array(32);
            window.crypto.getRandomValues(bytes);
            newEntropy = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        } else {
            newEntropy = Math.random().toString(36).substring(2) + Date.now().toString(36);
        }

        if (typeof sessionStorage !== 'undefined') {
            try { sessionStorage.setItem('__cc_session_token', newEntropy); } catch (e) { }
        }
        if (typeof localStorage !== 'undefined') {
            try {
                localStorage.removeItem('__cc_sys_hash'); // Issue 8: Purged insecure localStorage key
            } catch (e) { }
        }
        _volatileSessionKey = newEntropy;
        return newEntropy;
    } catch (e) {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
}
if (typeof window !== 'undefined') {
    // window._getAppSecret removed: keep module-scoped to prevent global extraction
}

// ── ZERO-KNOWLEDGE CLOUD ENCRYPTION ENGINE (AES-GCM-256) ──
function getVaultMasterSecret(user) {
    const uid = (user && user.uid) ? user.uid : ((typeof auth !== 'undefined' && auth.currentUser) ? auth.currentUser.uid : null);
    if (uid) {
        return `cosmo_vault_mek_${uid.toLowerCase()}`;
    }
    return typeof _getAppSecret === 'function' ? _getAppSecret() : "cosmo_crypt_fallback_session_key";
}

function setSafeUserData(data) {
    if (!data) {
        window.userData = null;
        if (window.parent && window.parent !== window) {
            try { window.parent.userData = null; } catch (e) { }
        }
        return;
    }
    if (data.template_vaults) {
        try {
            Object.defineProperty(data, 'template_vaults', {
                value: data.template_vaults,
                writable: true,
                enumerable: false,
                configurable: true
            });
        } catch (e) { }
    }
    window.userData = data;
    if (window.parent && window.parent !== window) {
        try { window.parent.userData = data; } catch (e) { }
    }
}

function getVaultUserSalt(user) {
    const uid = (user && user.uid) ? user.uid : ((typeof auth !== 'undefined' && auth.currentUser) ? auth.currentUser.uid : null);
    if (uid) {
        return `cosmo_user_salt_${uid.toLowerCase()}`;
    }
    return "cosmo_crypt_vault_salt_v2";
}

async function encryptVaultPayload(data, user = null) {
    if (data === null || data === undefined) return data;
    try {
        const jsonStr = typeof data === 'string' ? data : JSON.stringify(data);
        const secret = getVaultMasterSecret(user);
        const salt = getVaultUserSalt(user);
        const engine = CosmoCryptoEngine || (typeof window !== 'undefined' && window.CosmoCryptoEngine) || (typeof window !== 'undefined' && window.parent && window.parent.CosmoCryptoEngine);
        if (engine && typeof engine.encryptAESGCM === 'function') {
            const cipher = await engine.encryptAESGCM(jsonStr, secret, salt);
            if (cipher) return cipher;
        }
        if (typeof CryptoJS !== 'undefined') {
            return CryptoJS.AES.encrypt(jsonStr, secret).toString();
        }
    } catch (e) {
        console.warn("[CosmoCrypt] Cloud vault encryption fallback:", e);
    }
    return data;
}

async function decryptVaultPayload(payload, user = null) {
    if (!payload) return payload;
    // If it's already an array or object, it's plaintext legacy data: return as-is immediately (0ms)
    if (typeof payload !== 'string') {
        return payload;
    }
    const isGCM = payload.startsWith("cc_gcm256:");
    const isCryptoJS = payload.startsWith("U2FsdGVkX1");
    if (!isGCM && !isCryptoJS) {
        try {
            return JSON.parse(payload);
        } catch (e) {
            return payload;
        }
    }

    const secret = getVaultMasterSecret(user);
    const salt = getVaultUserSalt(user);
    const engine = CosmoCryptoEngine || (typeof window !== 'undefined' && window.CosmoCryptoEngine) || (typeof window !== 'undefined' && window.parent && window.parent.CosmoCryptoEngine);

    const secretsToTry = [secret];
    const appSec = typeof _getAppSecret === 'function' ? _getAppSecret() : null;
    if (appSec && !secretsToTry.includes(appSec)) secretsToTry.push(appSec);
    const vaultUid = (user && user.uid) ? user.uid : ((typeof auth !== 'undefined' && auth.currentUser) ? auth.currentUser.uid : null);
    if (vaultUid) {
        const legacyMek = `cosmo_vault_mek_${vaultUid.toLowerCase()}`;
        if (!secretsToTry.includes(legacyMek)) secretsToTry.push(legacyMek);
    }

    if (vaultUid) {
        if (!secretsToTry.includes(vaultUid)) secretsToTry.push(vaultUid);
        if (!secretsToTry.includes(vaultUid.toLowerCase())) secretsToTry.push(vaultUid.toLowerCase());
    }
    if (user && user.email) {
        if (!secretsToTry.includes(user.email)) secretsToTry.push(user.email);
        if (!secretsToTry.includes(user.email.toLowerCase())) secretsToTry.push(user.email.toLowerCase());
    }
    secretsToTry.push("cosmo_crypt_fallback_session_key");
    const saltsToTry = [salt, "cosmo_crypt_vault_salt_v2", null];

    for (const s of secretsToTry) {
        for (const saltVal of saltsToTry) {
            try {
                if (engine && typeof engine.decryptAESGCM === 'function') {
                    const dec = await engine.decryptAESGCM(payload, s, saltVal);
                    if (dec && dec !== payload) {
                        try { return JSON.parse(dec); } catch (e) { return dec; }
                    }
                }
            } catch (e) { }
        }
        if (typeof CryptoJS !== 'undefined') {
            try {
                const bytes = CryptoJS.AES.decrypt(payload, s);
                const dec = bytes.toString(CryptoJS.enc.Utf8);
                if (dec) {
                    try { return JSON.parse(dec); } catch (e) { return dec; }
                }
            } catch (e) { }
        }
    }

    // Fail-safe: if could not decrypt, return payload so data is never deleted or corrupted
    return payload;
}

if (typeof window !== 'undefined') {
    window.encryptVaultPayload = encryptVaultPayload;
    window.decryptVaultPayload = decryptVaultPayload;
}

// ── Google reCAPTCHA v3 Background Service ──
export const RECAPTCHA_SITE_KEY = "6Ld-kZAtAAAAAAT3Gj7n7vbsdg6vgU5_HDYFyRsv";

export async function executeRecaptcha(action = 'auth_action') {
    return new Promise((resolve) => {
        try {
            if (typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
                        .then((token) => resolve(token))
                        .catch((err) => {
                            console.warn("reCAPTCHA v3 execution warning:", err);
                            resolve(null);
                        });
                });
            } else {
                resolve(null);
            }
        } catch (e) {
            console.warn("reCAPTCHA v3 execution failed:", e);
            resolve(null);
        }
    });
}

if (typeof window !== 'undefined') {
    window.RECAPTCHA_SITE_KEY = RECAPTCHA_SITE_KEY;
    window.executeRecaptcha = executeRecaptcha;
}

// --- RFC 6238 TOTP & 2FA CRYPTO HELPERS ---
function _base32Decode(base32) {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
    let bits = "";
    const clean = (base32 || "").replace(/=+$/, '').toUpperCase().replace(/[\s-]/g, '');
    for (let i = 0; i < clean.length; i++) {
        const val = alphabet.indexOf(clean[i]);
        if (val === -1) continue;
        bits += val.toString(2).padStart(5, '0');
    }
    const bytes = new Uint8Array(Math.floor(bits.length / 8));
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(bits.substr(i * 8, 8), 2);
    }
    return bytes;
}

async function _calculateTotpToken(secret, timeStepOffset = 0) {
    try {
        const keyBytes = _base32Decode(secret);
        const epoch = Math.floor(Date.now() / 1000);
        const timeStep = Math.floor(epoch / 30) + timeStepOffset;

        const buffer = new ArrayBuffer(8);
        const view = new DataView(buffer);
        view.setUint32(0, 0, false);
        view.setUint32(4, timeStep, false);

        const cryptoKey = await crypto.subtle.importKey(
            "raw",
            keyBytes,
            { name: "HMAC", hash: { name: "SHA-1" } },
            false,
            ["sign"]
        );

        const signature = await crypto.subtle.sign("HMAC", cryptoKey, buffer);
        const hmacResult = new Uint8Array(signature);

        const offset = hmacResult[hmacResult.length - 1] & 0x0f;
        const code = ((hmacResult[offset] & 0x7f) << 24) |
            ((hmacResult[offset + 1] & 0xff) << 16) |
            ((hmacResult[offset + 2] & 0xff) << 8) |
            (hmacResult[offset + 3] & 0xff);

        const strCode = (code % 1000000).toString().padStart(6, '0');
        return strCode;
    } catch (e) {
        console.error("TOTP calc error:", e);
        return null;
    }
}

async function _verifyTotpToken(secret, code) {
    if (!secret || !code) return false;
    const cleanCode = code.toString().trim();
    for (let offset = -1; offset <= 1; offset++) {
        const token = await _calculateTotpToken(secret, offset);
        if (token === cleanCode) return true;
    }
    return false;
}

function _hashEmailOtp(code) {
    const clean = (code || '').toString().trim();
    return CryptoJS.SHA256(clean).toString(CryptoJS.enc.Hex);
}

function _updateLocal2FASession(method) {
    const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
    if (enc) {
        try {
            const bytes = CryptoJS.AES.decrypt(enc, _getAppSecret());
            const user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            user.two_factor_enabled = true;
            user.two_factor_type = method;
            sessionStorage.setItem('userStoreEnc', CryptoJS.AES.encrypt(JSON.stringify(user), _getAppSecret()).toString()); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }
        } catch (e) { }
    }
}

function _clearLocal2FASession() {
    const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
    if (enc) {
        try {
            const bytes = CryptoJS.AES.decrypt(enc, _getAppSecret());
            const user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            user.two_factor_enabled = false;
            delete user.two_factor_type;
            sessionStorage.setItem('userStoreEnc', CryptoJS.AES.encrypt(JSON.stringify(user), _getAppSecret()).toString()); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }
        } catch (e) { }
    }
}

window.saveSecurePdfConfigToFirebase = async function (enabled, password) {
    let finalPass = password || "";
    if (finalPass && !finalPass.startsWith('pbkdf2_')) {
        const saltFn = window.generateAppLockSalt || (window.parent && window.parent.generateAppLockSalt);
        const hashFn = window.hashAppLockSecret || (window.parent && window.parent.hashAppLockSecret);
        if (saltFn && hashFn) {
            const salt = saltFn();
            finalPass = `pbkdf2_${salt}_${await hashFn(finalPass, salt)}`;
        }
    }
    const config = { enabled: !!enabled, password: finalPass };

    // 1. Sync across all active window layers
    const contexts = [window, window.parent, window.top].filter(w => w && w.userData);
    contexts.forEach(ctx => {
        if (!ctx.userData.settings) ctx.userData.settings = {};
        ctx.userData.settings.secure_pdf_config = config;
    });

    // 2. Broadcast to all active iframes
    try {
        document.querySelectorAll('.view-layer iframe, iframe').forEach(iframe => {
            try {
                if (iframe.contentWindow) {
                    if (iframe.contentWindow.userData) {
                        if (!iframe.contentWindow.userData.settings) iframe.contentWindow.userData.settings = {};
                        iframe.contentWindow.userData.settings.secure_pdf_config = config;
                    }
                    if (typeof iframe.contentWindow.updateSettingsSecurePdfCardState === 'function') {
                        iframe.contentWindow.updateSettingsSecurePdfCardState();
                    }
                }
            } catch (e) { }
        });
    } catch (e) { }

    if (typeof window.updateSettingsSecurePdfCardState === 'function') {
        window.updateSettingsSecurePdfCardState();
    }
    if (window.parent && typeof window.parent.updateSettingsSecurePdfCardState === 'function') {
        window.parent.updateSettingsSecurePdfCardState();
    }

    // 3. Keep encrypted session store in sync
    try {
        const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
        if (enc && typeof CryptoJS !== 'undefined') {
            const bytes = CryptoJS.AES.decrypt(enc, _getAppSecret());
            const user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            if (user) {
                if (!user.settings) user.settings = {};
                user.settings.secure_pdf_config = config;
                sessionStorage.setItem('userStoreEnc', CryptoJS.AES.encrypt(JSON.stringify(user), _getAppSecret()).toString()); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }
            }
        }
    } catch (e) { }

    const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser) || (window.suiteAuth && window.suiteAuth.currentUser);
    if (!user || !user.uid) return false;
    try {
        await setDoc(doc(db, "users", user.uid), {
            settings: {
                secure_pdf_config: config
            }
        }, { merge: true });
        return true;
    } catch (err) {
        console.error("Firebase Secure PDF sync failed:", err);
        return false;
    }
};

window.saveCardMutationConfigToFirebase = async function (enabled, password) {
    let finalPass = password || "";
    if (finalPass && !finalPass.startsWith('pbkdf2_')) {
        const saltFn = window.generateAppLockSalt || (window.parent && window.parent.generateAppLockSalt);
        const hashFn = window.hashAppLockSecret || (window.parent && window.parent.hashAppLockSecret);
        if (saltFn && hashFn) {
            const salt = saltFn();
            finalPass = `pbkdf2_${salt}_${await hashFn(finalPass, salt)}`;
        }
    }
    const config = { enabled: !!enabled, password: finalPass };

    // 1. Sync across all active window layers
    const contexts = [window, window.parent, window.top].filter(w => w && w.userData);
    contexts.forEach(ctx => {
        if (!ctx.userData.settings) ctx.userData.settings = {};
        ctx.userData.settings.card_mutation_config = config;
    });

    // 2. Broadcast to all active iframes
    try {
        document.querySelectorAll('.view-layer iframe, iframe').forEach(iframe => {
            try {
                if (iframe.contentWindow) {
                    if (iframe.contentWindow.userData) {
                        if (!iframe.contentWindow.userData.settings) iframe.contentWindow.userData.settings = {};
                        iframe.contentWindow.userData.settings.card_mutation_config = config;
                    }
                    if (typeof iframe.contentWindow.updateSettingsCardMutationCardState === 'function') {
                        iframe.contentWindow.updateSettingsCardMutationCardState();
                    }
                }
            } catch (e) { }
        });
    } catch (e) { }

    if (typeof window.updateSettingsCardMutationCardState === 'function') {
        window.updateSettingsCardMutationCardState();
    }
    if (window.parent && typeof window.parent.updateSettingsCardMutationCardState === 'function') {
        window.parent.updateSettingsCardMutationCardState();
    }

    // 3. Keep encrypted session store in sync
    try {
        const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
        if (enc && typeof CryptoJS !== 'undefined') {
            const bytes = CryptoJS.AES.decrypt(enc, _getAppSecret());
            const user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            if (user) {
                if (!user.settings) user.settings = {};
                user.settings.card_mutation_config = config;
                sessionStorage.setItem('userStoreEnc', CryptoJS.AES.encrypt(JSON.stringify(user), _getAppSecret()).toString()); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }
            }
        }
    } catch (e) { }

    const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser) || (window.suiteAuth && window.suiteAuth.currentUser);
    if (!user || !user.uid) return false;
    try {
        await setDoc(doc(db, "users", user.uid), {
            settings: {
                card_mutation_config: config
            }
        }, { merge: true });
        return true;
    } catch (err) {
        console.error("Firebase Card Mutation sync failed:", err);
        return false;
    }
};

if (window.parent && window.parent !== window) {
    window.parent.saveSecurePdfConfigToFirebase = window.saveSecurePdfConfigToFirebase;
    window.parent.saveCardMutationConfigToFirebase = window.saveCardMutationConfigToFirebase;
}

let rootLockoutUnsubscribe = null;
let userLockoutUnsubscribe = null;

function getActiveUserIdentifier() {
    try {
        const ud = window.userData || (window.parent && window.parent.userData);
        if (ud && ud.email) return ud.email.toLowerCase().trim();
        if (ud && ud.uid) return ud.uid;

        const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc') || (window.parent && window.parent.sessionStorage && window.parent.sessionStorage.getItem('userStoreEnc'));
        const secret = typeof _getAppSecret === 'function'
            ? _getAppSecret()
            : (window.parent && typeof window.parent._getAppSecret === 'function' ? window.parent._getAppSecret() : null);
        if (secret) {
            const bytes = CryptoJS.AES.decrypt(enc, secret);
            const data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            if (data && data.email) return data.email.toLowerCase().trim();
            if (data && data.uid) return data.uid;
        }

        const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser);
        if (user && user.email) return user.email.toLowerCase().trim();
        if (user && user.uid) return user.uid;

        const lastEmail = localStorage.getItem('last_active_email') || (window.parent && window.parent.localStorage && window.parent.localStorage.getItem('last_active_email'));
        if (lastEmail) return lastEmail.toLowerCase().trim();

        const lastUid = localStorage.getItem('last_active_uid') || (window.parent && window.parent.localStorage && window.parent.localStorage.getItem('last_active_uid'));
        if (lastUid) return lastUid;
    } catch (e) { }
    return null;
}

window.getActiveUserIdentifier = getActiveUserIdentifier;

window.initLockoutRealtimeSync = function (userOrIdent) {
    if (rootLockoutUnsubscribe) {
        try { rootLockoutUnsubscribe(); } catch (e) { }
        rootLockoutUnsubscribe = null;
    }
    if (userLockoutUnsubscribe) {
        try { userLockoutUnsubscribe(); } catch (e) { }
        userLockoutUnsubscribe = null;
    }

    const ident = getActiveUserIdentifier();
    const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser);

    const handleSnapshot = (snap) => {
        if (!snap || !snap.exists()) return;
        const data = snap.data() || {};
        const now = Date.now();

        // 🚨 Instant Panic Wipe Kill-Switch on Lockout Signal
        if (data.panicWipeAt || data.panicEpoch || data.forceLogoutAll || data.reason === 'PANIC_WIPE') {
            const incomingPanicTs = data.panicWipeAt || data.panicEpoch || 0;
            // Only act on real timestamps — boolean-only fields from stale data are ignored
            if (incomingPanicTs > 0) {
                const lastPanicAck = Math.max(
                    parseInt(sessionStorage.getItem('__cc_last_panic_ack') || '0', 10),
                    window._lastPanicAck || 0
                );
                if (incomingPanicTs > lastPanicAck) {
                    const ackStr = incomingPanicTs.toString();
                    window._lastPanicAck = incomingPanicTs;
                    try { sessionStorage.setItem('__cc_last_panic_ack', ackStr); } catch (e) { }
                    try { localStorage.removeItem('__cc_last_panic_ack'); } catch (e) { }
                    if (typeof window.executePanicWipe === 'function') {
                        window.executePanicWipe(true);
                    } else if (window.parent && typeof window.parent.executePanicWipe === 'function') {
                        window.parent.executePanicWipe(true);
                    } else {
                        try { localStorage.clear(); } catch (e) { }
                        try { sessionStorage.clear(); } catch (e) { }
                        window.location.replace('/');
                    }
                    return;
                }
            }
        }
        const scopes = ['account_verify', 'card_mutation', 'secure_pdf', 'reset_plan_verify'];

        window._cosmoLockoutState = window._cosmoLockoutState || {};
        if (window.parent) window.parent._cosmoLockoutState = window.parent._cosmoLockoutState || window._cosmoLockoutState;

        for (const scopeKey of scopes) {
            const item = data[scopeKey];
            if (item && item.lockoutUntil && item.lockoutUntil > now) {
                window._cosmoLockoutState[scopeKey] = item.lockoutUntil;
                try { sessionStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); } catch (e) { }
                try { if (item && item.lockoutUntil && item.lockoutUntil > now) localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
                try { if (window.parent && window.parent.localStorage) { if (item && item.lockoutUntil && item.lockoutUntil > now) window.parent.localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else window.parent.localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } } catch (e) { }
            } else if (item && item.lockoutUntil === 0) {
                delete window._cosmoLockoutState[scopeKey];
                try { sessionStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
                try { if (item && item.lockoutUntil && item.lockoutUntil > now) localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
                try { if (window.parent && window.parent.localStorage) { if (item && item.lockoutUntil && item.lockoutUntil > now) window.parent.localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else window.parent.localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } } catch (e) { }
            }
        }

        const limiter = window.CosmoSecurityLimiter || (window.parent && window.parent.CosmoSecurityLimiter);
        if (limiter && typeof limiter.refreshActiveBindings === 'function') {
            limiter.refreshActiveBindings();
        }

        try {
            const iframes = document.querySelectorAll('iframe');
            iframes.forEach(ifr => {
                try {
                    if (ifr.contentWindow) {
                        ifr.contentWindow.postMessage({
                            type: 'COSMO_CLOUD_LOCKOUT_UPDATE',
                            lockouts: data
                        }, '*');
                    }
                } catch (err) { }
            });
        } catch (e) { }

        try {
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_cloud_lockout_sync', val: Date.now() } }));
            if (window.parent && window.parent !== window) {
                window.parent.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_cloud_lockout_sync', val: Date.now() } }));
            }
        } catch (e) { }
    };

    if (user && user.uid) {
        try {
            rootLockoutUnsubscribe = onSnapshot(doc(db, "security_lockouts", user.uid), handleSnapshot, err => console.warn("Root lockout listener:", err));
        } catch (e) { }
    }

    if (user && user.uid) {
        try {
            userLockoutUnsubscribe = onSnapshot(doc(db, "users", user.uid, "security", "lockout_status"), handleSnapshot, err => console.warn("User lockout listener:", err));
        } catch (e) { }
    }
};

window.syncLockoutToCloud = async function (scopeKey, lockoutUntil) {
    const ident = getActiveUserIdentifier();
    const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser);
    const payload = {
        [scopeKey]: {
            lockoutUntil: lockoutUntil || 0,
            updatedAt: new Date().toISOString()
        }
    };

    let p1 = Promise.resolve();
    let p2 = Promise.resolve();

    if (user && user.uid) {
        p1 = setDoc(doc(db, "security_lockouts", user.uid), payload, { merge: true }).catch(err => console.warn("Sync root lockout error:", err));
    }
    if (user && user.uid) {
        p2 = setDoc(doc(db, "users", user.uid, "security", "lockout_status"), payload, { merge: true }).catch(err => console.warn("Sync user lockout error:", err));
    }

    await Promise.all([p1, p2]);
    return true;
};

window.loadLockoutFromCloud = async function () {
    const ident = getActiveUserIdentifier();
    const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser);
    const now = Date.now();
    const scopes = ['account_verify', 'card_mutation', 'secure_pdf', 'reset_plan_verify'];

    let cloudData = {};

    try {
        if (user && user.uid) {
            const rootSnap = await getDoc(doc(db, "security_lockouts", user.uid));
            if (rootSnap.exists()) {
                cloudData = { ...cloudData, ...rootSnap.data() };
            }
        }
    } catch (e) { }

    try {
        if (user && user.uid) {
            const userSnap = await getDoc(doc(db, "users", user.uid, "security", "lockout_status"));
            if (userSnap.exists()) {
                cloudData = { ...cloudData, ...userSnap.data() };
            }
        }
    } catch (e) { }

    window._cosmoLockoutState = window._cosmoLockoutState || {};
    if (window.parent) window.parent._cosmoLockoutState = window.parent._cosmoLockoutState || window._cosmoLockoutState;

    for (const scopeKey of scopes) {
        const item = cloudData[scopeKey];
        if (item && item.lockoutUntil && item.lockoutUntil > now) {
            window._cosmoLockoutState[scopeKey] = item.lockoutUntil;
            try { sessionStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); } catch (e) { }
            try { if (item && item.lockoutUntil && item.lockoutUntil > now) localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
            try { if (window.parent && window.parent.localStorage) { if (item && item.lockoutUntil && item.lockoutUntil > now) window.parent.localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else window.parent.localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } } catch (e) { }
        } else if (item && item.lockoutUntil === 0) {
            delete window._cosmoLockoutState[scopeKey];
            try { sessionStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
            try { if (item && item.lockoutUntil && item.lockoutUntil > now) localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
            try { if (window.parent && window.parent.localStorage) { if (item && item.lockoutUntil && item.lockoutUntil > now) window.parent.localStorage.setItem(`cosmo_lockout_${scopeKey}`, item.lockoutUntil.toString()); else window.parent.localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } } catch (e) { }
        }
    }

    const limiter = window.CosmoSecurityLimiter || (window.parent && window.parent.CosmoSecurityLimiter);
    if (limiter && typeof limiter.refreshActiveBindings === 'function') {
        limiter.refreshActiveBindings();
    }
};

if (window.parent && window.parent !== window) {
    window.verifyLockoutWithCloud = async function (scopeKey = 'account_verify') {
        const user = (auth && auth.currentUser) || (window.parent && window.parent.auth && window.parent.auth.currentUser) || (window.suiteAuth && window.suiteAuth.currentUser);
        const uid = (user && user.uid) || (window.userData && window.userData.uid) || (window.parent && window.parent.userData && window.parent.userData.uid) || localStorage.getItem('last_active_uid');
        const now = Date.now();
        let cloudLockoutUntil = 0;
        if (uid && db) {
            try {
                const rSnap = await getDoc(doc(db, "security_lockouts", uid));
                if (rSnap.exists() && rSnap.data() && rSnap.data()[scopeKey]) {
                    cloudLockoutUntil = Math.max(cloudLockoutUntil, Number(rSnap.data()[scopeKey].lockoutUntil) || 0);
                }
            } catch (e) { }
            try {
                const uSnap = await getDoc(doc(db, "users", uid, "security", "lockout_status"));
                if (uSnap.exists() && uSnap.data() && uSnap.data()[scopeKey]) {
                    cloudLockoutUntil = Math.max(cloudLockoutUntil, Number(uSnap.data()[scopeKey].lockoutUntil) || 0);
                }
            } catch (e) { }
        }
        const localMax = Math.max(
            (window._cosmoLockoutState && window._cosmoLockoutState[scopeKey]) || 0,
            (window.parent && window.parent._cosmoLockoutState && window.parent._cosmoLockoutState[scopeKey]) || 0,
            parseInt(sessionStorage.getItem(`cosmo_lockout_${scopeKey}`) || '0', 10),
            parseInt(localStorage.getItem(`cosmo_lockout_${scopeKey}`) || '0', 10),
            parseInt((window.parent && window.parent.localStorage && window.parent.localStorage.getItem(`cosmo_lockout_${scopeKey}`)) || '0', 10)
        );
        const effectiveUntil = Math.max(cloudLockoutUntil, localMax);
        if (effectiveUntil > now) {
            window._cosmoLockoutState = window._cosmoLockoutState || {};
            window._cosmoLockoutState[scopeKey] = effectiveUntil;
            if (window.parent) {
                window.parent._cosmoLockoutState = window.parent._cosmoLockoutState || {};
                window.parent._cosmoLockoutState[scopeKey] = effectiveUntil;
            }
            try { sessionStorage.setItem(`cosmo_lockout_${scopeKey}`, effectiveUntil.toString()); } catch (e) { }
            try { localStorage.setItem(`cosmo_lockout_${scopeKey}`, effectiveUntil.toString()); } catch (e) { }
            if (window.parent && window.parent.localStorage) {
                try { window.parent.localStorage.setItem(`cosmo_lockout_${scopeKey}`, effectiveUntil.toString()); } catch (e) { }
            }
            const limiter = window.CosmoSecurityLimiter || (window.parent && window.parent.CosmoSecurityLimiter);
            if (limiter && typeof limiter.refreshActiveBindings === 'function') limiter.refreshActiveBindings();
            return { locked: true, remainingSeconds: Math.max(0, Math.ceil((effectiveUntil - now) / 1000)) };
        } else {
            if (window._cosmoLockoutState) delete window._cosmoLockoutState[scopeKey];
            if (window.parent && window.parent._cosmoLockoutState) delete window.parent._cosmoLockoutState[scopeKey];
            try { sessionStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
            try { localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
            if (window.parent && window.parent.localStorage) {
                try { window.parent.localStorage.removeItem(`cosmo_lockout_${scopeKey}`); } catch (e) { }
            }
            return { locked: false, remainingSeconds: 0 };
        }
    };

    window.parent.syncLockoutToCloud = window.syncLockoutToCloud;
    window.parent.loadLockoutFromCloud = window.loadLockoutFromCloud;
    window.parent.initLockoutRealtimeSync = window.initLockoutRealtimeSync;
    window.parent.getActiveUserIdentifier = window.getActiveUserIdentifier;
    window.parent.verifyLockoutWithCloud = window.verifyLockoutWithCloud;
}


async function detectLocation() {
    try {
        const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: AbortSignal.timeout(2000) });
        if (!res.ok) throw new Error('geojs failed');
        const data = await res.json();
        if (data && data.city) {
            const parts = [];
            if (data.city && data.city !== '-') parts.push(data.city);
            if (data.region && data.region !== '-') parts.push(data.region);
            if (data.country && data.country !== '-') parts.push(data.country);
            return parts.join(', ');
        }
    } catch (e) {
        console.warn("geojs failed, trying ipapi.co", e.message);
        try {
            const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(2000) });
            if (!res.ok) throw new Error('ipapi.co failed');
            const text = await res.text();
            const data = JSON.parse(text);
            if (data && data.city) {
                const parts = [];
                if (data.city) parts.push(data.city);
                if (data.region) parts.push(data.region);
                if (data.country_name) parts.push(data.country_name);
                return parts.join(', ');
            }
        } catch (e2) {
            console.warn("ipapi.co failed", e2.message);
        }
    }
    return 'Unknown Location';
}

function getCleanDeviceAndBrowser(ua) {
    if (!ua) return 'Unknown Device';
    let browser = "Browser";
    let os = "OS";

    // Detect OS
    if (/Windows/i.test(ua)) os = "Windows Platform";
    else if (/Android/i.test(ua)) os = "Android Mobile";
    else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS Device";
    else if (/Macintosh|Mac OS X/i.test(ua)) os = "MacOS Environment";
    else if (/Linux/i.test(ua)) os = "Linux Distribution";
    else os = "Corporate Workstation";

    // Detect Browser
    if (/Edg/i.test(ua)) browser = "Microsoft Edge";
    else if (/Chrome/i.test(ua)) browser = "Google Core";
    else if (/Firefox/i.test(ua)) browser = "Red Fox Architecture";
    else if (/Safari/i.test(ua)) browser = "Safari";
    else browser = "Software Suite";

    return `${browser} on ${os}`;
}

function formatLoginTimeClient(date = new Date()) {
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const kolkataDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const month = months[kolkataDate.getMonth()];
    const day = kolkataDate.getDate();
    const year = kolkataDate.getFullYear();

    let hours = kolkataDate.getHours();
    const minutes = String(kolkataDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${month} ${day} , ${year} AT ${hours}:${minutes} ${ampm} IST`;
}

// 📱 REAL MULTI-DEVICE SESSION MANAGEMENT ENGINE (FLIPKART / NETFLIX / WHATSAPP ARCHITECTURE)
function getOrCreateDeviceId(forceNew = false) {
    let id = forceNew ? null : localStorage.getItem('cosmo_device_id');
    if (forceNew) {
        // Save old ID so registerCurrentDeviceSession can clean up the old entry
        const oldId = localStorage.getItem('cosmo_device_id');
        if (oldId) window._previousDeviceId = oldId;
    }
    if (!id) {
        // Clear any stale/revoked device ID before generating a fresh one
        try { localStorage.removeItem('cosmo_device_id'); } catch (e) { }

        const ua = navigator.userAgent || '';
        let prefix = 'dev_win_';
        if (/iPhone/i.test(ua)) prefix = 'dev_ios_';
        else if (/iPad/i.test(ua)) prefix = 'dev_pad_';
        else if (/Android/i.test(ua)) prefix = 'dev_and_';
        else if (/Macintosh|Mac OS X/i.test(ua)) prefix = 'dev_mac_';
        else if (/Linux/i.test(ua)) prefix = 'dev_lin_';

        const rand = Array.from(crypto.getRandomValues(new Uint8Array(6)))
            .map(b => b.toString(16).padStart(2, '0')).join('');
        id = prefix + rand;
        try { localStorage.setItem('cosmo_device_id', id); } catch (e) { }
    }
    return id;
}
window.getOrCreateDeviceId = getOrCreateDeviceId;

function getDetailedClientInfo() {
    const ua = navigator.userAgent || '';
    let browser = "Google Chrome";
    let os = "Windows 11 / Windows Platform";
    let icon = "laptop_mac";
    let type = "laptop";
    let deviceName = "Windows 11 PC";

    // Detect Android model if present in User Agent
    let androidModel = null;
    if (/Android/i.test(ua)) {
        const match = ua.match(/;\s*([^;)]+?)\s*(?:Build\/|\))/i);
        if (match && match[1] && !match[1].startsWith('K') && !match[1].startsWith('wv')) {
            androidModel = match[1].trim();
        }
    }

    // Detect OS & Specific Device Hardware Name
    if (/Windows NT 10.0/i.test(ua)) {
        os = "Windows 11 / 10 Pro 64-bit";
        deviceName = "Windows 11 PC";
    } else if (/Windows NT 6.3/i.test(ua)) {
        os = "Windows 8.1 64-bit";
        deviceName = "Windows 8 PC";
    } else if (/Windows/i.test(ua)) {
        os = "Windows Platform";
        deviceName = "Windows PC";
    } else if (/iPhone/i.test(ua)) {
        const match = ua.match(/OS (\d+_\d+)/);
        const ver = match ? match[1].replace('_', '.') : '18.0';
        os = `iOS ${ver} (Apple iPhone)`;
        deviceName = "Apple iPhone";
        icon = "smartphone";
        type = "smartphone";
    } else if (/iPad/i.test(ua)) {
        os = "iPadOS (Apple iPad)";
        deviceName = "Apple iPad";
        icon = "tablet_mac";
        type = "tablet";
    } else if (/Android/i.test(ua)) {
        const match = ua.match(/Android\s([0-9\.]+)/);
        const ver = match ? match[1] : '15';
        if (/Mobile/i.test(ua)) {
            os = `Android ${ver} Mobile`;
            deviceName = androidModel ? `Android (${androidModel})` : "Android Smartphone";
            icon = "smartphone";
            type = "smartphone";
        } else {
            os = `Android ${ver} Tablet`;
            deviceName = androidModel ? `Android Tablet (${androidModel})` : "Android Tablet";
            icon = "tablet_mac";
            type = "tablet";
        }
    } else if (/Macintosh|Mac OS X/i.test(ua)) {
        os = "macOS Sonoma (Apple Silicon / Intel)";
        deviceName = "Apple MacBook / Mac";
        icon = "laptop_mac";
        type = "laptop";
    } else if (/Linux/i.test(ua)) {
        os = "Linux Distribution (x86_64)";
        deviceName = "Linux Workstation";
        icon = "laptop_mac";
        type = "laptop";
    }

    // Detect Browser
    if (/Edg/i.test(ua)) browser = "Microsoft Edge";
    else if (/Chrome/i.test(ua)) browser = "Google Chrome";
    else if (/Firefox/i.test(ua)) browser = "Mozilla Firefox";
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = "Apple Safari";
    else if (/Opera|OPR/i.test(ua)) browser = "Opera Browser";

    return {
        deviceName: deviceName,
        name: `${deviceName} (${browser})`,
        fullName: `${deviceName} • ${browser}`,
        browser,
        os,
        icon,
        type
    };
}
window.getDetailedClientInfo = getDetailedClientInfo;

let _cachedNetInfo = null;
let _cachedNetPromise = null;
async function detectLocationAndNetwork() {
    if (_cachedNetInfo) return _cachedNetInfo;
    if (_cachedNetPromise) return _cachedNetPromise;

    _cachedNetPromise = (async () => {
        let net = {
            location: 'Detecting Location...',
            ip: 'Detecting IP...',
            isp: 'Identifying Hardware Gateway...'
        };

        try {
            let publicIp = null;
            try {
                const ip4Res = await fetch('https://api4.ipify.org?format=json', { signal: AbortSignal.timeout(2500) });
                if (ip4Res.ok) {
                    const ipData = await ip4Res.json();
                    if (ipData.ip) publicIp = ipData.ip;
                }
            } catch (e) { }

            const geoEndpoint = publicIp ? `https://get.geojs.io/v1/ip/geo/${publicIp}.json` : 'https://get.geojs.io/v1/ip/geo.json';
            const res = await fetch(geoEndpoint);
            if (res.ok) {
                const data = await res.json();
                const parts = [];
                if (data.city && data.city !== '-') {
                    parts.push(data.city);
                } else if (data.region && data.region !== '-') {
                    parts.push(data.region);
                }
                if (data.country && data.country !== '-') parts.push(data.country);
                net.location = parts.join(', ') || 'Global Node';
                net.city = (data.city && data.city !== '-') ? data.city : '';
                net.region = (data.region && data.region !== '-') ? data.region : '';
                net.country = (data.country && data.country !== '-') ? data.country : '';
                net.country_code = data.country_code || '';
                net.ip = data.ip || publicIp || 'Secured Node Relay';
                net.isp = data.organization_name || data.organization || 'Broadband ISP Tier';
                _cachedNetInfo = net;
                return net;
            }
        } catch (e) {
            try {
                const res2 = await fetch('https://ipapi.co/json/');
                if (res2.ok) {
                    const data2 = await res2.json();
                    const parts2 = [];
                    if (data2.city) {
                        parts2.push(data2.city);
                    } else if (data2.region) {
                        parts2.push(data2.region);
                    }
                    if (data2.country_name) parts2.push(data2.country_name);
                    net.location = parts2.join(', ') || 'Global Node';
                    net.city = data2.city || '';
                    net.region = data2.region || '';
                    net.country = data2.country_name || '';
                    net.country_code = data2.country_code || '';
                    net.ip = data2.ip || publicIp || 'Secured Node Relay';
                    net.isp = data2.org || 'Broadband ISP Tier';
                    _cachedNetInfo = net;
                    return net;
                }
            } catch (e2) { }
        }

        net.location = 'Could Not Determine Region (Secured Relay)';
        net.city = '';
        net.region = '';
        net.country = '';
        net.country_code = '';
        net.ip = publicIp || 'Secured Relay Node';
        net.isp = 'Hardware Security Relay';
        _cachedNetInfo = net;
        return net;
    })();

    return _cachedNetPromise;
}
window.detectLocationAndNetwork = detectLocationAndNetwork;

window.registerCurrentDeviceSession = async function (optionalUid) {
    const user = auth.currentUser;
    const uid = optionalUid || (user ? user.uid : null);
    if (!uid) return null;

    // Debounce rapid multiple calls within 3 seconds for the same user to avoid duplicate Firestore writes
    if (window._lastDeviceRegTime && (Date.now() - window._lastDeviceRegTime < 3000) && window._lastDeviceRegUid === uid) {
        return window._lastRegisteredSessionObj || null;
    }
    window._lastDeviceRegTime = Date.now();
    window._lastDeviceRegUid = uid;

    let deviceId = getOrCreateDeviceId();
    const info = getDetailedClientInfo();

    // Fast network info fallback so registration NEVER hangs or gets blocked by slow external APIs
    let net = _cachedNetInfo || {
        location: 'Could Not Determine Region (Secured Relay)',
        city: '',
        region: '',
        country: '',
        country_code: '',
        ip: 'Secured Relay Node',
        isp: 'Hardware Security Relay'
    };

    try {
        const fastNetPromise = detectLocationAndNetwork();
        net = await Promise.race([
            fastNetPromise,
            new Promise(r => setTimeout(() => r(net), 300))
        ]);
    } catch (e) { }

    // Determine Auth Method with comprehensive checks
    let detectedProvider = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('__cc_auth_provider')) ||
        (window.userData && window.userData.authProvider) ||
        (auth.currentUser && auth.currentUser.providerData && auth.currentUser.providerData[0] && auth.currentUser.providerData[0].providerId) ||
        null;

    let authMethod = 'Email & Master Password';
    let authDesc = 'Email & Master Password (Argon2id Enclave)';

    if (detectedProvider === 'google.com' || (auth.currentUser && auth.currentUser.providerData && auth.currentUser.providerData.some(p => p.providerId === 'google.com'))) {
        authMethod = 'Google Account';
        authDesc = 'Google OAuth 2.0 (Verified Cloud Identity)';
    } else if (detectedProvider === 'google_one_tap') {
        authMethod = 'Google One Tap';
        authDesc = 'Google One Tap (Instant Token)';
    } else if (detectedProvider === 'passkey') {
        authMethod = 'Biometric Passkey';
        authDesc = 'FIDO2 / WebAuthn Hardware Authenticator';
    } else if (detectedProvider === 'phone' || (auth.currentUser && auth.currentUser.providerData && auth.currentUser.providerData.some(p => p.providerId === 'phone'))) {
        authMethod = 'Phone Number (SMS)';
        authDesc = 'Phone OTP (Carrier Authenticated)';
    }

    const sessionObj = {
        id: deviceId,
        name: info.name,
        fullName: info.fullName,
        defaultName: info.fullName || info.name,
        icon: info.icon,
        type: info.type,
        os: info.os,
        browser: info.browser,
        location: net.location,
        isp: net.isp,
        ip: net.ip,
        loginTime: formatLoginTimeClient(new Date()),
        loginTimestamp: Date.now(),
        lastActive: "This Device (Active Now)",
        lastActiveTs: Date.now(),
        authMethod: authMethod,
        authDesc: authDesc,
        cipher: 'TLS 1.3 / AES-256-GCM / ECDH x25519'
    };

    try {
        const userDocRef = doc(db, "users", uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
            const data = userSnap.data();

            // Re-verify auth method against Firestore profile and active providerData
            if (data.authProvider === 'google.com' || (auth.currentUser && auth.currentUser.providerData && auth.currentUser.providerData.some(p => p.providerId === 'google.com'))) {
                sessionObj.authMethod = 'Google Account';
                sessionObj.authDesc = 'Google OAuth 2.0 (Verified Cloud Identity)';
            } else if (data.authProvider === 'google_one_tap') {
                sessionObj.authMethod = 'Google One Tap';
                sessionObj.authDesc = 'Google One Tap (Instant Token)';
            } else if (data.authProvider === 'passkey') {
                sessionObj.authMethod = 'Biometric Passkey';
                sessionObj.authDesc = 'FIDO2 / WebAuthn Hardware Authenticator';
            } else if (data.authProvider === 'phone' || (auth.currentUser && auth.currentUser.providerData && auth.currentUser.providerData.some(p => p.providerId === 'phone'))) {
                sessionObj.authMethod = 'Phone Number (SMS)';
                sessionObj.authDesc = 'Phone OTP (Carrier Authenticated)';
            }

            // 🛡️ CRITICAL: If this device ID was previously revoked (e.g. tab was closed
            // during remote revocation so the deauth handler never ran), force a fresh ID.
            // This prevents zombie revoked sessions from reappearing in activeDevices.
            let revokedList = Array.isArray(data.revokedDeviceIds) ? [...data.revokedDeviceIds] : [];
            if (revokedList.includes(deviceId)) {
                console.warn("🔄 [SESSION] Device ID was revoked — generating fresh session ID.");
                deviceId = getOrCreateDeviceId(true);
                sessionObj.id = deviceId;
            }

            let currentDevices = Array.isArray(data.activeDevices) ? [...data.activeDevices] : [];

            // 🧹 Deduplicate & replace any previous session for the same physical device/browser
            const isSamePhysicalClient = (d) => {
                if (!d) return false;
                if (d.id === deviceId || d.deviceId === deviceId) return true;
                if (window._previousDeviceId && (d.id === window._previousDeviceId || d.deviceId === window._previousDeviceId)) return true;
                const sameType = d.type && sessionObj.type && d.type === sessionObj.type;
                const sameOs = d.os && sessionObj.os && d.os === sessionObj.os;
                const sameBrowser = d.browser && sessionObj.browser && d.browser === sessionObj.browser;
                if (sameType && sameOs && sameBrowser) {
                    const sameIp = d.ip && sessionObj.ip && d.ip === sessionObj.ip;
                    const sameName = (d.name && sessionObj.name && d.name === sessionObj.name) ||
                        (d.fullName && sessionObj.fullName && d.fullName === sessionObj.fullName);
                    if (sameIp || sameName || (!d.ip && !sessionObj.ip)) return true;
                }
                return false;
            };

            // Remove all existing sessions matching this physical client
            const replacedEntries = currentDevices.filter(isSamePhysicalClient);
            const replacedIds = replacedEntries.map(d => d.id || d.deviceId).filter(Boolean);
            currentDevices = currentDevices.filter(d => !isSamePhysicalClient(d));

            // Preserve custom name from cloud session if previously assigned
            const prevMatching = replacedEntries.find(d => (d.customName !== undefined && d.customName !== null));
            if (prevMatching && prevMatching.customName && prevMatching.customName.trim() !== '') {
                sessionObj.name = prevMatching.customName.trim();
                sessionObj.fullName = prevMatching.customName.trim();
                sessionObj.customName = prevMatching.customName.trim();
            }

            // Clean up replaced IDs from revokedList so they don't linger as revoked
            if (window._previousDeviceId) {
                replacedIds.push(window._previousDeviceId);
                window._previousDeviceId = null;
            }
            revokedList = revokedList.filter(id => !replacedIds.includes(id) && id !== deviceId);

            // Globally deduplicate remaining currentDevices to heal any previous database duplicates
            const seenDeviceSigs = new Set();
            const deduplicated = [];
            for (const dev of currentDevices) {
                const devKey = `${dev.type || ''}|${dev.os || ''}|${dev.browser || ''}|${dev.ip || dev.name || ''}`;
                if (seenDeviceSigs.has(devKey) && devKey.length > 5) {
                    continue;
                }
                seenDeviceSigs.add(devKey);
                deduplicated.push(dev);
            }
            currentDevices = deduplicated;

            // Prepend fresh session for this device
            currentDevices.unshift(sessionObj);

            if (currentDevices.length > 100) currentDevices = currentDevices.slice(0, 100);

            // Clean up revokedDeviceIds in Firestore for this newly authenticated device session
            revokedList = revokedList.filter(id => id !== deviceId);

            await updateDoc(userDocRef, {
                activeDevices: currentDevices,
                revokedDeviceIds: revokedList,
                lastLoginDevice: info.name
            });

            if (window.userData) {
                window.userData.activeDevices = currentDevices;
                window.userData.revokedDeviceIds = revokedList;
            }
            if (window.parent && window.parent.userData) {
                window.parent.userData.activeDevices = currentDevices;
                window.parent.userData.revokedDeviceIds = revokedList;
            }

            // 🚀 Broadcast real-time session update to all frames & listeners
            const devSyncDetail = { activeDevices: currentDevices, revokedDeviceIds: revokedList, currentDeviceId: deviceId };
            try { window.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            try { window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            if (window.parent && window.parent !== window) {
                try { window.parent.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                try { window.parent.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            }
            document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                try {
                    if (ifr.contentWindow) {
                        if (ifr.contentWindow.userData) {
                            ifr.contentWindow.userData.activeDevices = currentDevices;
                            ifr.contentWindow.userData.revokedDeviceIds = revokedList;
                        }
                        ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail }));
                        ifr.contentWindow.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail }));
                        ifr.contentWindow.postMessage({ type: 'session-update', activeDevices: currentDevices }, '*');
                        if (typeof ifr.contentWindow.initLiveSessions === 'function') {
                            ifr.contentWindow.initLiveSessions();
                        }
                    }
                } catch (e) { }
            });

            // Asynchronously enrich location/IP if detected fresh later
            detectLocationAndNetwork().then(freshNet => {
                if (freshNet && (freshNet.location !== sessionObj.location || freshNet.ip !== sessionObj.ip)) {
                    sessionObj.location = freshNet.location;
                    sessionObj.ip = freshNet.ip;
                    sessionObj.isp = freshNet.isp;
                    const idx = currentDevices.findIndex(d => (d.id === deviceId || d.deviceId === deviceId));
                    if (idx !== -1) {
                        currentDevices[idx].location = freshNet.location;
                        currentDevices[idx].ip = freshNet.ip;
                        currentDevices[idx].isp = freshNet.isp;
                        updateDoc(userDocRef, { activeDevices: currentDevices }).catch(() => { });
                    }
                }
            }).catch(() => { });
        }
    } catch (err) {
        console.warn("⚠️ Device session sync notice:", err);
    }

    window._lastRegisteredSessionObj = sessionObj;
    return sessionObj;
};

window.revokeDeviceSession = async function (targetDeviceId) {
    const user = auth.currentUser;
    const uid = user ? user.uid : (window.userData ? window.userData.uid : null);
    if (!uid) return { success: false, error: "No active user session." };

    try {
        const userDocRef = doc(db, "users", uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
            const data = userSnap.data();
            let currentDevices = Array.isArray(data.activeDevices) ? [...data.activeDevices] : [];
            const targetDev = currentDevices.find(d => (d.id === targetDeviceId || d.deviceId === targetDeviceId));

            const idsToRevoke = [targetDeviceId];
            currentDevices = currentDevices.filter(d => {
                if (d.id === targetDeviceId || d.deviceId === targetDeviceId) return false;
                if (targetDev && d.type === targetDev.type && d.os === targetDev.os && d.browser === targetDev.browser && (d.ip === targetDev.ip || d.name === targetDev.name)) {
                    if (d.id) idsToRevoke.push(d.id);
                    if (d.deviceId) idsToRevoke.push(d.deviceId);
                    return false;
                }
                return true;
            });

            let revokedList = Array.isArray(data.revokedDeviceIds) ? [...data.revokedDeviceIds] : [];
            idsToRevoke.forEach(id => {
                if (id && !revokedList.includes(id)) revokedList.push(id);
            });

            await updateDoc(userDocRef, {
                activeDevices: currentDevices,
                revokedDeviceIds: revokedList
            });

            if (window.userData) {
                window.userData.activeDevices = currentDevices;
                window.userData.revokedDeviceIds = revokedList;
            }
            if (window.parent && window.parent.userData) {
                window.parent.userData.activeDevices = currentDevices;
                window.parent.userData.revokedDeviceIds = revokedList;
            }

            // 🚀 Broadcast real-time revocation to all frames & listeners
            const devSyncDetail = { activeDevices: currentDevices, revokedDeviceIds: revokedList };
            try { window.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            try { window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            if (window.parent && window.parent !== window) {
                try { window.parent.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                try { window.parent.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            }
            document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                try {
                    if (ifr.contentWindow) {
                        if (ifr.contentWindow.userData) {
                            ifr.contentWindow.userData.activeDevices = currentDevices;
                            ifr.contentWindow.userData.revokedDeviceIds = revokedList;
                        }
                        ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail }));
                        ifr.contentWindow.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail }));
                        ifr.contentWindow.postMessage({ type: 'session-update', activeDevices: currentDevices }, '*');
                        if (typeof ifr.contentWindow.initLiveSessions === 'function') {
                            ifr.contentWindow.initLiveSessions();
                        }
                    }
                } catch (e) { }
            });

            return { success: true };
        }
    } catch (err) {
        console.error("Failed to revoke device session:", err);
        return { success: false, error: err.message };
    }
};

window.revokeAllOtherDeviceSessions = async function () {
    const user = auth.currentUser;
    const uid = user ? user.uid : (window.userData ? window.userData.uid : null);
    if (!uid) return { success: false, error: "No active user session." };

    const currentDeviceId = getOrCreateDeviceId();

    try {
        const userDocRef = doc(db, "users", uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
            const data = userSnap.data();
            let currentDevices = Array.isArray(data.activeDevices) ? [...data.activeDevices] : [];
            const otherDeviceIds = currentDevices
                .filter(d => (d.id !== currentDeviceId && d.deviceId !== currentDeviceId))
                .map(d => (d.id || d.deviceId));

            const kept = currentDevices.filter(d => (d.id === currentDeviceId || d.deviceId === currentDeviceId));

            let revokedList = Array.isArray(data.revokedDeviceIds) ? [...data.revokedDeviceIds] : [];
            otherDeviceIds.forEach(id => {
                if (id && !revokedList.includes(id)) revokedList.push(id);
            });

            await updateDoc(userDocRef, {
                activeDevices: kept,
                revokedDeviceIds: revokedList
            });

            if (window.userData) {
                window.userData.activeDevices = kept;
                window.userData.revokedDeviceIds = revokedList;
            }
            if (window.parent && window.parent.userData) {
                window.parent.userData.activeDevices = kept;
                window.parent.userData.revokedDeviceIds = revokedList;
            }

            // 🚀 Broadcast real-time global revocation to all frames & listeners
            const devSyncDetail = { activeDevices: kept, revokedDeviceIds: revokedList };
            try { window.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            try { window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            if (window.parent && window.parent !== window) {
                try { window.parent.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                try { window.parent.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
            }
            document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                try {
                    if (ifr.contentWindow) {
                        if (ifr.contentWindow.userData) {
                            ifr.contentWindow.userData.activeDevices = kept;
                            ifr.contentWindow.userData.revokedDeviceIds = revokedList;
                        }
                        ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail }));
                        ifr.contentWindow.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail }));
                        ifr.contentWindow.postMessage({ type: 'session-update', activeDevices: kept }, '*');
                        if (typeof ifr.contentWindow.initLiveSessions === 'function') {
                            ifr.contentWindow.initLiveSessions();
                        }
                    }
                } catch (e) { }
            });

            return { success: true, count: otherDeviceIds.length };
        }
    } catch (err) {
        console.error("Failed to revoke all other device sessions:", err);
        return { success: false, error: err.message };
    }
};

window.renameDeviceSession = async function (targetDeviceId, newName, isReset = false) {
    const user = auth.currentUser;
    const uid = user ? user.uid : (window.userData ? window.userData.uid : null);
    if (!uid || !targetDeviceId || !newName) return { success: false, error: "Missing parameters" };

    try {
        const userDocRef = doc(db, "users", uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
            const data = userSnap.data();
            let currentDevices = Array.isArray(data.activeDevices) ? [...data.activeDevices] : [];
            let updated = false;
            currentDevices = currentDevices.map(d => {
                if (d.id === targetDeviceId || d.deviceId === targetDeviceId) {
                    updated = true;
                    return {
                        ...d,
                        name: newName,
                        fullName: newName,
                        customName: isReset ? '' : newName
                    };
                }
                return d;
            });

            if (updated) {
                await updateDoc(userDocRef, { activeDevices: currentDevices });

                if (window.userData) window.userData.activeDevices = currentDevices;
                if (window.parent && window.parent.userData) window.parent.userData.activeDevices = currentDevices;

                const syncDetail = { activeDevices: currentDevices };
                try { window.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: syncDetail })); } catch (e) { }
                try { window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: syncDetail })); } catch (e) { }
                if (window.parent && window.parent !== window) {
                    try { window.parent.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: syncDetail })); } catch (e) { }
                    try { window.parent.dispatchEvent(new CustomEvent('userDataUpdated', { detail: syncDetail })); } catch (e) { }
                }
                document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                    try {
                        if (ifr.contentWindow) {
                            if (ifr.contentWindow.userData) ifr.contentWindow.userData.activeDevices = currentDevices;
                            ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: syncDetail }));
                            ifr.contentWindow.dispatchEvent(new CustomEvent('userDataUpdated', { detail: syncDetail }));
                            ifr.contentWindow.postMessage({ type: 'session-update', activeDevices: currentDevices }, '*');
                            if (typeof ifr.contentWindow.initLiveSessions === 'function') {
                                ifr.contentWindow.initLiveSessions();
                            }
                        }
                    } catch (e) { }
                });
            }

            return { success: true };
        }
    } catch (err) {
        console.error("Failed to rename device session:", err);
        return { success: false, error: err.message };
    }
};

window.cosmoDeviceManager = {
    getDeviceId: getOrCreateDeviceId,
    getClientInfo: getDetailedClientInfo,
    getNetworkInfo: detectLocationAndNetwork,
    registerCurrentSession: window.registerCurrentDeviceSession,
    revokeDevice: window.revokeDeviceSession,
    revokeAllOthers: window.revokeAllOtherDeviceSessions,
    renameDevice: window.renameDeviceSession
};
if (window.parent && window.parent !== window) {
    window.parent.cosmoDeviceManager = window.cosmoDeviceManager;
}


// 🕒 Standard 24-Hour Date & Time Formatter for Multi-Device Consistency
export function getStandard24HDateTime(d = new Date()) {
    const date = (d instanceof Date && !isNaN(d)) ? d : (d ? new Date(d) : new Date());
    if (isNaN(date.getTime())) return typeof d === 'string' ? d : 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
}
window.getStandard24HDateTime = getStandard24HDateTime;

export function formatTo24H(val) {
    if (!val || val === 'FIRST SESSION' || val === 'RESTORED SESSION' || val === 'NEW_BOOTSTRAP' || val === 'INITIALIZING...') {
        return val || 'FIRST SESSION';
    }
    if (typeof val === 'string') {
        const ddmmyyyyMatch = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})[,\s]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\s*(AM|PM))?$/i);
        if (ddmmyyyyMatch) {
            let [, p1, p2, year, hr, min, sec, ampm] = ddmmyyyyMatch;
            let hour = parseInt(hr, 10);
            if (ampm) {
                if (ampm.toUpperCase() === 'PM' && hour < 12) hour += 12;
                if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
            }
            let day = parseInt(p1, 10);
            let month = parseInt(p2, 10);
            if (month > 12 && day <= 12) {
                const temp = day; day = month; month = temp;
            }
            const sDay = String(day).padStart(2, '0');
            const sMonth = String(month).padStart(2, '0');
            const sHr = String(hour).padStart(2, '0');
            const sMin = String(min).padStart(2, '0');
            const sSec = String(sec || '00').padStart(2, '0');
            return `${sDay}/${sMonth}/${year}, ${sHr}:${sMin}:${sSec}`;
        }
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
            return getStandard24HDateTime(d);
        }
        return val;
    } else if (val instanceof Date || typeof val === 'number') {
        return getStandard24HDateTime(val);
    }
    return String(val);
}
window.formatTo24H = formatTo24H;

const ownerEmails = ['pramodchandrabajpai@gmail.com', 'support.fusionforge@gmail.com'];
let isSettingsSyncPending = false;
let settingsSyncTimeout = null;

const FONT_MAP = {
    "1": "Space Grotesk",
    "2": "Montserrat",
    "3": "Inter",
    "4": "Lato",
    "5": "Merriweather",
    "6": "Lora",
    "7": "Clash Display",
    "8": "Bricolage Grotesque"
};

const REVERSE_FONT_MAP = {
    "Space Grotesk": "1",
    "Montserrat": "2",
    "Inter": "3",
    "Lato": "4",
    "Merriweather": "5",
    "Lora": "6",
    "Clash Display": "7",
    "Bricolage Grotesque": "8"
};

// Export for Debugging
window.suiteAuth = auth;
window.suiteApp = app;
window.suiteDb = db;
window.suiteDoc = doc;
window.suiteGetDoc = getDoc;
window.suiteUpdateDoc = updateDoc;
window.suiteDeleteField = deleteField;
window.suiteFunctions = functions;
window.suiteHttpsCallable = httpsCallable;
window.suiteSetDoc = setDoc;
window.suiteGetDocs = getDocs;
window.suiteCollection = collection;
window.suiteDeleteDoc = deleteDoc;
window.suiteQuery = query;
window.suiteWhere = where;
window.suiteLimit = limit;
window.suiteOnSnapshot = onSnapshot;
if (window.parent && window.parent !== window) {
    window.parent.suiteOnSnapshot = onSnapshot;
    window.parent.suiteFunctions = functions;
    window.parent.suiteHttpsCallable = httpsCallable;
}
window.createRazorpayOrder = async (data) => {
    const createOrderFn = httpsCallable(functions, 'createRazorpayOrder');
    const res = await createOrderFn(data);
    return res.data;
};
window.verifyRazorpayPayment = async (data) => {
    const verifyPaymentFn = httpsCallable(functions, 'verifyRazorpayPayment');
    const res = await verifyPaymentFn(data);
    return res.data;
};
window.sendTemplateEmail = async (to, templateId, variables, subject) => {
    try {
        const sendTemplateEmailFn = httpsCallable(functions, 'sendTemplateEmail');
        const res = await sendTemplateEmailFn({ to, templateId, variables, subject });
        return res.data;
    } catch (error) {
        console.error("Error calling sendTemplateEmail:", error);
        throw error;
    }
};

// Specific template email helpers matching index.js templates
window.sendWelcomeEmail = async (to, userName) => {
    return window.sendTemplateEmail(to, 'welcome-email-1', {
        USER_NAME: userName || 'User'
    }, 'Welcome to Cosmo Crypt !');
};

window.sendLoginAlert = async (to, device, location) => {
    const devVal = device && device !== 'Unknown Device' ? getCleanDeviceAndBrowser(device) : getCleanDeviceAndBrowser(navigator.userAgent);
    return window.sendTemplateEmail(to, 'new-login-detected', {
        DEVICE: devVal,
        LOCATION: location || 'Unknown Location',
        TIME: formatLoginTimeClient()
    }, 'Security Alert: New Login Detected');
};

window.sendPasswordResetEmailCustom = async (to, resetLink) => {
    return window.sendTemplateEmail(to, 'password-reset', {
        RESET_LINK: resetLink
    }, 'Reset Your Password');
};

window.sendPaymentConfirmation = async (to, userName, amount, currency) => {
    return window.sendTemplateEmail(to, 'payment-confirmation', {
        USER_NAME: userName || 'User',
        AMOUNT: amount,
        CURRENCY: currency || 'INR'
    }, 'Payment Confirmation');
};

window.sendPaymentFailed = async (to, userName, amount) => {
    return window.sendTemplateEmail(to, 'payment-failed-alert', {
        USER_NAME: userName || 'User',
        AMOUNT: amount
    }, 'Action Required: Payment Failed');
};

window.sendSubscriptionCancelled = async (to, userName, isReverted) => {
    return window.sendTemplateEmail(to, 'subscription-cancelled', {
        USER_NAME: userName || 'User',
        isReverted: !!isReverted
    }, isReverted ? 'Subscription Reverted / Downgraded' : 'Subscription Cancelled');
};

window.sendAccountDeleted = async (to, userName) => {
    return window.sendTemplateEmail(to, 'account-deleted', {
        USER_NAME: userName || 'User'
    }, 'Account Deleted');
};

window.sendTwoFactorEnabledEmail = async (to, userName, method) => {
    return window.sendTemplateEmail(to, 'two-factor-enabled', {
        USER_NAME: userName || 'User',
        TWO_FACTOR_TYPE: method || 'Google Authenticator (TOTP)'
    }, '2-Step Verification Activated');
};

window.sendTwoFactorDisabledEmail = async (to, userName, method) => {
    return window.sendTemplateEmail(to, 'two-factor-disabled', {
        USER_NAME: userName || 'User',
        TWO_FACTOR_TYPE: method || 'Google Authenticator (TOTP)'
    }, '2-Step Verification Turned Off');
};

window.sendPasswordChangedEmail = async (to, location, ip) => {
    try {
        const sendPasswordChangedFn = httpsCallable(functions, 'sendPasswordChangedEmail');
        const res = await sendPasswordChangedFn({ email: to, location, ip, time: formatLoginTimeClient() });
        return res.data;
    } catch (error) {
        console.warn("Direct sendPasswordChangedEmail failed, using sendTemplateEmail fallback:", error);
        return window.sendTemplateEmail(to, 'password-changed', {
            location: location,
            ip: ip,
            time: formatLoginTimeClient()
        }, 'Password Changed Successfully — Cosmo Crypt Security');
    }
};



const GOOGLE_CLIENT_ID = "937571060072-sor5fepl144pt33ub9tg388ro02tam1d.apps.googleusercontent.com";

async function handleGoogleOneTapResponse(response) {
    try {
        if (!response || !response.credential) {
            throw new Error("No credential received from Google One Tap.");
        }
        const credential = GoogleAuthProvider.credential(response.credential);
        const result = await signInWithCredential(auth, credential);
        const user = result.user;

        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        let profileData;

        const localChoices = JSON.parse(localStorage.getItem('user_choices') || '{}');
        const localAppLock = localStorage.getItem('\x61\x70\x70\x5f\x6c\x6f\x63\x6b\x5f\x63\x6f\x6e\x66\x69\x67');

        const gPhoto = user.photoURL || (user.providerData && user.providerData[0] && user.providerData[0].photoURL) || null;
        if (userSnap.exists()) {
            const existingData = userSnap.data();
            const updates = {
                choices: { ...(existingData.choices || {}), ...localChoices },
                lastLogin: existingData.currentLogin ? formatTo24H(existingData.currentLogin) : "FIRST SESSION",
                currentLogin: getStandard24HDateTime()
            };
            if (gPhoto) {
                updates.photoURL = gPhoto;
            }
            if (localAppLock && !existingData.app_lock_config) {
                try { updates.app_lock_config = JSON.parse(localAppLock); } catch (e) { }
            }
            // 🔐 2-STEP VERIFICATION GATE (GOOGLE ONE TAP)
            if (existingData.two_factor_enabled === true) {
                const is2FAVerified = sessionStorage.getItem('__cc_2fa_passed_' + user.uid) === 'true';
                if (!is2FAVerified) {
                    sessionStorage.removeItem('__cc_2fa_passed_' + user.uid);
                    localStorage.removeItem('userStoreEnc');
                    window.__pending2FAUser = {
                        user,
                        profileData: existingData,
                        pendingUpdates: updates,
                        authProvider: 'google_one_tap'
                    };

                    // Inform Account iframe or modal to open the 2FA Challenge immediately
                    const iframes = document.querySelectorAll('iframe');
                    let challenged = false;
                    iframes.forEach(ifr => {
                        try {
                            if (ifr.contentWindow && typeof ifr.contentWindow.openTwoFactorChallenge === 'function') {
                                ifr.contentWindow.openTwoFactorChallenge(existingData.two_factor_type || 'totp', user.email);
                                challenged = true;
                            }
                        } catch (crossOriginErr) {
                            // Safely ignore cross-origin iframes (e.g. Google One Tap GSI iframe, reCAPTCHA)
                        }
                    });

                    if (!challenged && typeof window.openTwoFactorChallenge === 'function') {
                        try {
                            window.openTwoFactorChallenge(existingData.two_factor_type || 'totp', user.email);
                            challenged = true;
                        } catch (e) { }
                    }

                    if (!challenged) {
                        if (typeof window.enterCosmoCryptApp === 'function') {
                            window.enterCosmoCryptApp('account');
                            challenged = true;
                        } else if (typeof window.switchView === 'function') {
                            window.switchView('account');
                            challenged = true;
                        } else if (typeof window.openPremiumLoginModal === 'function') {
                            window.openPremiumLoginModal("Two-Factor Authentication required.");
                        }
                    }

                    if (typeof window.pop === 'function') {
                        window.pop("Two-Factor Authentication required to complete sign-in.", "info");
                    }

                    return {
                        success: false,
                        requireTwoFactor: true,
                        tempUid: user.uid,
                        email: user.email,
                        twoFactorType: existingData.two_factor_type || 'totp'
                    };
                }
            }

            // Only commit login updates when 2FA is verified or not required
            await updateDoc(userRef, updates);
            profileData = { ...existingData, ...updates };

            if (existingData.pdf_telemetry) {
                const rawDl = typeof existingData.pdf_telemetry.downloads_count === 'number' ? existingData.pdf_telemetry.downloads_count : parseInt(existingData.pdf_telemetry.downloads_count || '0', 10);
                const rawEnc = typeof existingData.pdf_telemetry.encrypted_downloads_count === 'number' ? existingData.pdf_telemetry.encrypted_downloads_count : parseInt(existingData.pdf_telemetry.encrypted_downloads_count || '0', 10);
                const curDl = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.downloads_count) || 0;
                const curEnc = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.encrypted_downloads_count) || 0;
                const telemState = {
                    downloads_count: Math.max(curDl, isNaN(rawDl) ? 0 : rawDl),
                    encrypted_downloads_count: Math.max(curEnc, isNaN(rawEnc) ? 0 : rawEnc),
                    last_updated: existingData.pdf_telemetry.last_updated || new Date().toISOString()
                };
                window._cosmoPdfTelemetryState = telemState;
                if (window.userData) window.userData.pdf_telemetry = telemState;
            }
        } else {
            const nameParts = user.displayName ? user.displayName.split(' ') : ["Social", "User"];
            profileData = {
                first: nameParts[0],
                last: nameParts.slice(1).join(' ') || "User",
                email: user.email,
                photoURL: gPhoto,
                phone: user.phoneNumber || "PENDING",
                joined: getStandard24HDateTime(),
                currentLogin: getStandard24HDateTime(),
                lastLogin: "FIRST SESSION",
                authProvider: 'google.com',
                social: true,
                isVerified: true,
                uid: user.uid,
                choices: localChoices,
                role: ownerEmails.includes(user.email.toLowerCase()) ? 'owner' : 'user'
            };
            if (localAppLock) {
                try { profileData.app_lock_config = JSON.parse(localAppLock); } catch (e) { }
            }
            await setDoc(userRef, profileData);
        }

        sessionStorage.setItem('__cosmo_session_active', 'true');
        sessionStorage.setItem('__cc_auth_provider', 'google_one_tap');
        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
        sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

        window.userData = profileData;
        if (window.parent && window.parent !== window) {
            try { window.parent.userData = profileData; } catch (e) { }
        }
        document.querySelectorAll('.view-layer iframe, iframe').forEach(iframe => {
            try {
                if (iframe.contentWindow) {
                    iframe.contentWindow.userData = profileData;
                    iframe.contentWindow.postMessage({ type: 'login-success', isLoggedIn: true, user: profileData }, '*');
                    if (typeof iframe.contentWindow.switchToProfilePanel === 'function') {
                        iframe.contentWindow.switchToProfilePanel(profileData);
                    }
                    if (typeof iframe.contentWindow.updateMomentumDash === 'function') {
                        iframe.contentWindow.updateMomentumDash();
                    }
                    if (typeof iframe.contentWindow.updateAvatarUI === 'function') {
                        iframe.contentWindow.updateAvatarUI();
                    }
                }
            } catch (e) { }
        });

        const _analyticsOneTap = window.cosmoAnalytics || window.cosmoAnalytics;
        if (_analyticsOneTap) {
            _analyticsOneTap.logAuthAction('login_success', 'google_one_tap');
        }

        if (typeof window.addSuiteNotification === 'function') {
            window.addSuiteNotification('account', 'New Login Detected', `Your account was accessed via Google One Tap from ${navigator.userAgent.includes('Windows') ? 'Windows' : 'Mobile Device'}.`, { device: navigator.userAgent.substring(0, 30), provider: 'Google One Tap', status: 'Success' });
        }

        if (typeof window.pop === 'function') {
            window.pop(`Welcome back, ${profileData.first || 'User'}! Signed in via Google One Tap.`, "success");
        }

        try {
            const loc = await detectLocation();
            const notifyLogin = httpsCallable(functions, 'onLoginDetected');
            notifyLogin({
                email: user.email,
                device: getCleanDeviceAndBrowser(navigator.userAgent),
                location: loc,
                name: profileData.first || 'User'
            });
        } catch (e) { console.warn("Email alert deferred."); }

        // 🔄 Force fresh device ID on every login so revoked IDs never persist
        getOrCreateDeviceId(true);
        if (typeof window.registerCurrentDeviceSession === 'function') {
            try { await window.registerCurrentDeviceSession(user.uid); } catch (e) { }
        }
        window._cosmoLoginInProgress = false;

        if (typeof window.syncProfileWithCloud === 'function') {
            window.syncProfileWithCloud();
        }

        return { success: true, user: profileData };
    } catch (error) {
        console.error("Google One Tap Error:", error);
        if (window.cosmoAnalytics) {
            window.cosmoAnalytics.logAuthAction('login_failure', 'google_one_tap');
            window.cosmoAnalytics.logError(error.message, 'google_one_tap_login');
        }
        if (typeof window.pop === 'function') {
            window.pop("Google One Tap login failed: " + error.message, "error");
        }
        return { success: false, error };
    }
}

function initGoogleOneTap() {
    if (auth.currentUser) return;
    if (localStorage.getItem('explicit_logout') === 'true') return;

    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        setTimeout(initGoogleOneTap, 1000);
        return;
    }

    try {
        window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleOneTapResponse,
            auto_select: false,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true
        });

        window.google.accounts.id.prompt();
    } catch (e) {
        console.warn("Google One Tap init error:", e);
    }
}

// 🔒 Programmatically dismiss Google One Tap without triggering user lockout / cooldown cookie (g_state)
function dismissGoogleOneTap() {
    try {
        if (window.google && window.google.accounts && window.google.accounts.id) {
            window.google.accounts.id.cancel();
        }
        const gsiContainer = document.getElementById('credential_picker_container') ||
            document.getElementById('credential_picker_iframe');
        if (gsiContainer) {
            gsiContainer.style.display = 'none';
        }
    } catch (e) {
        console.warn("Could not cancel Google One Tap:", e);
    }
}
window.dismissGoogleOneTap = dismissGoogleOneTap;

// Helper to construct secure GoogleAuthProvider with cryptographically random state parameter (anti-CSRF protection)
function createSecureGoogleProvider() {
    const provider = new GoogleAuthProvider();
    const stateToken = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : (Math.random().toString(36).substring(2) + Date.now().toString(36));
    try {
        sessionStorage.setItem('cosmo_oauth_state', stateToken);
    } catch (e) { }
    provider.setCustomParameters({
        prompt: 'select_account',
        state: stateToken
    });
    return provider;
}

// Global Bridge for Iframe Communication
window.suiteAuthBridge = {
    getAuthUser: () => auth.currentUser,
    googleLogin: async () => {
        try {
            try {
                localStorage.removeItem('explicit_logout');
                sessionStorage.removeItem('explicit_logout');
                window._userExplicitlyLoggedOut = false;
                window._cosmoLoginInProgress = true;
                const oldOverlay = document.getElementById('cosmo-deauth-overlay');
                if (oldOverlay) oldOverlay.remove();
            } catch (e) { }
            const result = await signInWithPopup(auth, createSecureGoogleProvider());
            const user = result.user;

            // Profile Sync
            const userRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userRef);
            let profileData;

            if (userSnap.exists()) {
                const existingData = userSnap.data();

                // [FIX] Proactively merge local state into the profile before saving
                const localChoices = JSON.parse(localStorage.getItem('user_choices') || '{}');
                const localAppLock = localStorage.getItem('\x61\x70\x70\x5f\x6c\x6f\x63\x6b\x5f\x63\x6f\x6e\x66\x69\x67');

                const gPhoto = user.photoURL || (user.providerData && user.providerData[0] && user.providerData[0].photoURL) || null;
                const updates = {
                    choices: { ...(existingData.choices || {}), ...localChoices },
                    lastLogin: existingData.currentLogin ? formatTo24H(existingData.currentLogin) : "FIRST SESSION",
                    currentLogin: getStandard24HDateTime()
                };
                if (gPhoto) {
                    updates.photoURL = gPhoto;
                }

                if (localAppLock && !existingData.app_lock_config) {
                    try { updates.app_lock_config = JSON.parse(localAppLock); } catch (e) { }
                }

                updateDoc(userRef, updates).catch(err => console.warn("Google login updateDoc notice:", err));
                profileData = { ...existingData, ...updates };

                if (existingData.pdf_telemetry) {
                    const rawDl = typeof existingData.pdf_telemetry.downloads_count === 'number' ? existingData.pdf_telemetry.downloads_count : parseInt(existingData.pdf_telemetry.downloads_count || '0', 10);
                    const rawEnc = typeof existingData.pdf_telemetry.encrypted_downloads_count === 'number' ? existingData.pdf_telemetry.encrypted_downloads_count : parseInt(existingData.pdf_telemetry.encrypted_downloads_count || '0', 10);
                    const curDl = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.downloads_count) || 0;
                    const curEnc = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.encrypted_downloads_count) || 0;
                    const telemState = {
                        downloads_count: Math.max(curDl, isNaN(rawDl) ? 0 : rawDl),
                        encrypted_downloads_count: Math.max(curEnc, isNaN(rawEnc) ? 0 : rawEnc),
                        last_updated: existingData.pdf_telemetry.last_updated || new Date().toISOString()
                    };
                    window._cosmoPdfTelemetryState = telemState;
                    if (window.userData) window.userData.pdf_telemetry = telemState;
                }

                // 🔐 2-STEP VERIFICATION GATE (GOOGLE AUTH)
                if (existingData.two_factor_enabled === true) {
                    sessionStorage.removeItem('__cc_2fa_passed_' + user.uid);
                    localStorage.removeItem('userStoreEnc');
                    window.__pending2FAUser = { user, profileData, updates, authProvider: 'google.com' };
                    return {
                        success: false,
                        requireTwoFactor: true,
                        tempUid: user.uid,
                        email: user.email,
                        twoFactorType: existingData.two_factor_type || 'totp'
                    };
                }
            } else {
                const nameParts = user.displayName ? user.displayName.split(' ') : ["Social", "User"];
                const gPhoto = user.photoURL || (user.providerData && user.providerData[0] && user.providerData[0].photoURL) || null;

                // [NEW] Bootstrap with any existing local choices
                const localChoices = JSON.parse(localStorage.getItem('user_choices') || '{}');
                const localAppLock = localStorage.getItem('\x61\x70\x70\x5f\x6c\x6f\x63\x6b\x5f\x63\x6f\x6e\x66\x69\x67');

                profileData = {
                    first: nameParts[0],
                    last: nameParts.slice(1).join(' ') || "User",
                    email: user.email,
                    photoURL: gPhoto,
                    phone: user.phoneNumber || "PENDING",
                    joined: getStandard24HDateTime(),
                    currentLogin: getStandard24HDateTime(),
                    lastLogin: "FIRST SESSION",
                    authProvider: 'google.com',
                    social: true,
                    isVerified: true,
                    uid: user.uid,
                    choices: localChoices,
                    role: ownerEmails.includes(user.email.toLowerCase()) ? 'owner' : 'user',
                    terms: 'ALLOW',
                    terms_accepted: true,
                    terms_accepted_at: getStandard24HDateTime(),
                    terms_statement: "I confirm that I am at least 18 years of age, agree to the Terms of Service, and understand that Cosmo Crypt uses Zero-Knowledge encryption. I am solely responsible for securing my master credentials, and Cosmo Crypt cannot recover lost passwords or vault keys.",
                    analytics: {
                        developer_endpoints: { count: 0 },
                        dns_management: { count: 0 },
                        encrypted_partitions_notes: { count: 0 },
                        financial_credentials: { count: 0 },
                        identity_vault_documents: { storage_usage_percent: "0%" }
                    },
                    pdf_telemetry: {
                        downloads_count: 0,
                        encrypted_downloads_count: 0,
                        last_updated: getStandard24HDateTime()
                    }
                };

                if (localAppLock) {
                    try { profileData.app_lock_config = JSON.parse(localAppLock); } catch (e) { }
                }

                await setDoc(userRef, profileData);
                try {
                    await setDoc(doc(db, "users", user.uid, "terms", "agreement"), {
                        status: "ALLOW",
                        accepted: true,
                        accepted_at: getStandard24HDateTime() || new Date().toISOString(),
                        statement: "I confirm that I am at least 18 years of age, agree to the Terms of Service, and understand that Cosmo Crypt uses Zero-Knowledge encryption. I am solely responsible for securing my master credentials, and Cosmo Crypt cannot recover lost passwords or vault keys."
                    });
                } catch (tErr) { }
            }

            window.userData = profileData;
            if (window.parent && window.parent !== window) {
                window.parent.userData = profileData;
            }

            // 🌟 Fetch template vaults subcollection in background (non-blocking)
            if (!profileData.template_vaults) profileData.template_vaults = {};
            (async () => {
                try {
                    const vaultsSnap = await getDocs(collection(db, "users", user.uid, "template_vaults"));
                    const decryptPromises = [];
                    vaultsSnap.forEach(vDoc => {
                        if (vDoc.data() && vDoc.data().data !== undefined) {
                            decryptPromises.push((async () => {
                                const rawVal = vDoc.data().data;
                                const decVal = await decryptVaultPayload(rawVal, user);
                                profileData.template_vaults[vDoc.id] = decVal;
                                if (typeof rawVal !== 'string' || (!rawVal.startsWith('cc_gcm256:') && !rawVal.startsWith('U2FsdGVkX1'))) {
                                    encryptVaultPayload(decVal, user).then(migratedEnc => {
                                        if (migratedEnc && typeof migratedEnc === 'string') {
                                            setDoc(doc(db, "users", user.uid, "template_vaults", vDoc.id), {
                                                data: migratedEnc,
                                                encrypted: true,
                                                cipher: 'AES-GCM-256',
                                                updatedAt: Date.now()
                                            }, { merge: true }).catch(() => { });
                                        }
                                    }).catch(() => { });
                                }
                            })());
                        }
                    });
                    await Promise.all(decryptPromises);
                } catch (vErr) {
                    console.warn("Template vaults pre-fetch warning:", vErr);
                }

                if (profileData.template_vaults && typeof profileData.template_vaults === 'object') {
                    const rootDecryptPromises = [];
                    for (const k of Object.keys(profileData.template_vaults)) {
                        rootDecryptPromises.push((async () => {
                            const rawVal = profileData.template_vaults[k];
                            const decVal = await decryptVaultPayload(rawVal, user);
                            profileData.template_vaults[k] = decVal;
                            if (typeof rawVal !== 'string' || (!rawVal.startsWith('cc_gcm256:') && !rawVal.startsWith('U2FsdGVkX1'))) {
                                encryptVaultPayload(decVal, user).then(migratedEnc => {
                                    if (migratedEnc && typeof migratedEnc === 'string') {
                                        setDoc(doc(db, "users", user.uid), {
                                            template_vaults: { [k]: migratedEnc }
                                        }, { merge: true }).catch(() => { });
                                    }
                                }).catch(() => { });
                            }
                        })());
                    }
                    await Promise.all(rootDecryptPromises);
                }

                if (window.CosmoTemplateStore && typeof window.CosmoTemplateStore.notifyUpdate === 'function') {
                    Object.keys(profileData.template_vaults).forEach(k => {
                        window.CosmoTemplateStore.notifyUpdate(k, profileData.template_vaults[k]);
                    });
                }
            })();

            window.userData = profileData;
            if (window.parent && window.parent !== window) {
                window.parent.userData = profileData;
            }

            sessionStorage.setItem('__cosmo_session_active', 'true');
            sessionStorage.setItem('__cc_auth_provider', 'google.com');
            // Encrypt and Persist
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
            sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

            // 📊 [ANALYTICS] Track Successful Auth
            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('login_success', 'google');
            }

            if (typeof window.addSuiteNotification === 'function') {
                window.addSuiteNotification('account', 'New Login Detected', `Your account was accessed via Google from ${navigator.userAgent.includes('Windows') ? 'Windows' : 'Mobile Device'}.`, { device: navigator.userAgent.substring(0, 30), provider: 'Google', status: 'Success' });
            }

            // 📧 [RESEND] Trigger Login Notification in background
            (async () => {
                try {
                    const loc = await detectLocation();
                    const notifyLogin = httpsCallable(functions, 'onLoginDetected');
                    notifyLogin({
                        email: user.email,
                        device: getCleanDeviceAndBrowser(navigator.userAgent),
                        location: loc,
                        name: profileData.first || 'User'
                    });
                } catch (e) { console.warn("Email alert deferred."); }
            })();

            // 🔄 Force fresh device ID on every login so revoked IDs never persist
            getOrCreateDeviceId(true);
            // 📱 Proactively Register and Synchronize Current Device Session in background
            if (typeof window.registerCurrentDeviceSession === 'function') {
                window.registerCurrentDeviceSession(user.uid).catch(devErr => {
                    console.warn("⚠️ Device session registration notice:", devErr);
                });
            }
            window._cosmoLoginInProgress = false;

            // [FIX] Do not synchronously clear the iframe or switch view here.
            // This allows the child iframe (LOGIN.html) to complete its Google Auth handler,
            // post the 'login-success' message, and navigate to the profile panel.
            return { success: true, user: profileData };
        } catch (error) {
            console.error("Firebase Bridge Error:", error);
            window._cosmoLoginInProgress = false;
            // 📊 [ANALYTICS] Track Auth Failure
            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('login_failure', 'google');
                window.cosmoAnalytics.logError(error.message, 'google_login');
            }
            return { success: false, error };
        }
    },
    initOneTap: () => initGoogleOneTap(),
    emailRegister: async (details) => {
        try {
            const cleanDetails = JSON.parse(JSON.stringify(details));
            if (!cleanDetails.password || cleanDetails.password.length < 6) {
                return { success: false, error: "Password must be at least 6 characters." };
            }
            const normalizedEmail = cleanDetails.email.toLowerCase();

            // 🚦 Signal to onAuthStateChanged bootstrap to stand down.
            // Without this, bootstrap fires concurrently, creates the Firestore doc
            // with a different 'joined' timestamp, and then emailRegister's setDoc
            // becomes an UPDATE that the joined-immutability rule rejects.
            window.isRegistering = true;
            window._cosmoLoginInProgress = true;

            // 🛑 Detach any previous active snapshot listeners to prevent permission collision
            if (window.userSnapshotListener) {
                try { window.userSnapshotListener(); } catch (e) { }
                window.userSnapshotListener = null;
            }
            if (auth.currentUser) {
                try { await signOut(auth); } catch (e) { }
            }

            const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, cleanDetails.password);
            const user = userCredential.user;

            // ✅ Build profileData with only truthy/defined values.
            // Firestore rules require phone/bio/age/etc to be string type —
            // writing null for these fields causes "phone is string" to fail.
            // Solution: only include optional fields when they have a real value.
            const profileData = {
                first: cleanDetails.first || '',
                last: cleanDetails.last || '',
                email: normalizedEmail,
                uid: user.uid,
                joined: getStandard24HDateTime(),
                currentLogin: getStandard24HDateTime(),
                lastLogin: 'FIRST SESSION',
                cloudSynced: true,
                isVerified: true,
                authProvider: 'password',
                role: ownerEmails.includes(normalizedEmail) ? 'owner' : 'user',
                terms: 'ALLOW',
                terms_accepted: true,
                terms_accepted_at: getStandard24HDateTime(),
                terms_statement: "I confirm that I am at least 18 years of age, agree to the Terms of Service, and understand that Cosmo Crypt uses Zero-Knowledge encryption. I am solely responsible for securing my master credentials, and Cosmo Crypt cannot recover lost passwords or vault keys.",
                analytics: {
                    developer_endpoints: { count: 0 },
                    dns_management: { count: 0 },
                    encrypted_partitions_notes: { count: 0 },
                    financial_credentials: { count: 0 },
                    identity_vault_documents: { storage_usage_percent: "0%" }
                },
                pdf_telemetry: {
                    downloads_count: 0,
                    encrypted_downloads_count: 0,
                    last_updated: getStandard24HDateTime()
                }
            };
            // Optional fields: only add when the user actually filled them in
            if (cleanDetails.phone) profileData.phone = String(cleanDetails.phone);
            if (cleanDetails.code) profileData.code = String(cleanDetails.code);
            if (cleanDetails.phone && cleanDetails.code) {
                const dial = String(cleanDetails.code).startsWith('+') ? String(cleanDetails.code) : ('+' + cleanDetails.code);
                const local = String(cleanDetails.phone).replace(/[^0-9]/g, '');
                profileData.phoneNumber = (dial + local).replace(/\s+/g, '');
            }
            if (cleanDetails.age) profileData.age = String(cleanDetails.age);
            if (cleanDetails.gender) profileData.gender = String(cleanDetails.gender);
            if (cleanDetails.dob) profileData.dob = String(cleanDetails.dob);
            if (cleanDetails.pin) profileData.pin = String(cleanDetails.pin);
            if (cleanDetails.address) profileData.address = String(cleanDetails.address);
            if (cleanDetails.occupation) profileData.occupation = String(cleanDetails.occupation);
            if (cleanDetails.bio) profileData.bio = String(cleanDetails.bio);

            // 🖼️ [CLOUD STORAGE] If custom avatar provided as base64, upload to Firebase Storage
            if (cleanDetails.avatarUrl && typeof cleanDetails.avatarUrl === 'string' && cleanDetails.avatarUrl.startsWith('data:image/')) {
                try {
                    const storageRef = ref(storage, `users/${user.uid}/avatar/profile.webp`);
                    await uploadString(storageRef, cleanDetails.avatarUrl, 'data_url', { contentType: 'image/webp' });
                    const downloadUrl = await getDownloadURL(storageRef);
                    profileData.avatarUrl = downloadUrl;
                    localStorage.setItem('userProfileImg', downloadUrl);
                } catch (stErr) {
                    console.warn("Storage upload failed during registration:", stErr);
                }
            } else if (cleanDetails.avatarUrl && cleanDetails.avatarUrl !== 'none') {
                profileData.avatarUrl = cleanDetails.avatarUrl;
            }

            const userDocRef = doc(db, "users", user.uid);
            const existingSnap = await getDoc(userDocRef);
            if (existingSnap.exists()) {
                // Doc already created by bootstrap — update with form data,
                // skip 'joined' (rules protect it from being changed)
                const updateData = { ...profileData };
                delete updateData.joined;
                await updateDoc(userDocRef, updateData);
            } else {
                await setDoc(userDocRef, profileData);
            }

            try {
                await setDoc(doc(db, "users", user.uid, "terms", "agreement"), {
                    status: "ALLOW",
                    accepted: true,
                    accepted_at: getStandard24HDateTime() || new Date().toISOString(),
                    statement: "I confirm that I am at least 18 years of age, agree to the Terms of Service, and understand that Cosmo Crypt uses Zero-Knowledge encryption. I am solely responsible for securing my master credentials, and Cosmo Crypt cannot recover lost passwords or vault keys."
                });
            } catch (tErr) { }

            // Stop local password persistence. Verification will be Cloud-only.
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
            sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

            // 📊 [ANALYTICS] Track Registration
            const _analytics = window.cosmoAnalytics || window.cosmoAnalytics;
            if (_analytics) {
                _analytics.logAuthAction('signup_success', 'email');
            }

            // 🔄 Force fresh device ID on every registration so revoked IDs never persist
            getOrCreateDeviceId(true);
            // 📱 Proactively Register and Synchronize Current Device Session
            if (typeof window.registerCurrentDeviceSession === 'function') {
                try { await window.registerCurrentDeviceSession(user.uid); } catch (devErr) {
                    console.warn("⚠️ Device session registration notice:", devErr);
                }
            }
            window._cosmoLoginInProgress = false;

            return { success: true, user: profileData };
        } catch (error) {
            console.error("Firebase Registration Error:", error);
            // 📊 [ANALYTICS] Track Registration Failure
            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('signup_failure', 'email');
                window.cosmoAnalytics.logError(error.message, 'email_signup');
            }
            return { success: false, error: error.message };
        } finally {
            // Always clear the flag so future logins trigger bootstrap normally
            window.isRegistering = false;
        }
    },
    updateProfile: async (data) => {
        const user = auth.currentUser;
        if (!user) return { success: false, error: "No user authenticated" };
        try {
            const cleanData = JSON.parse(JSON.stringify(data));
            delete cleanData.password;

            // 🖼️ [CLOUD STORAGE ONLY] Upload cropped avatar to Firebase Cloud Storage
            if (cleanData.avatarUrl && typeof cleanData.avatarUrl === 'string' && cleanData.avatarUrl.startsWith('data:image/')) {
                try {
                    const storageRef = ref(storage, `users/${user.uid}/avatar/profile.webp`);
                    await uploadString(storageRef, cleanData.avatarUrl, 'data_url', { contentType: 'image/webp' });
                    const downloadUrl = await getDownloadURL(storageRef);
                    cleanData.avatarUrl = downloadUrl;
                } catch (stErr) {
                    console.error("Firebase Storage avatar upload failed:", stErr);
                    // Do NOT fall back to saving base64 in Firestore to keep Firestore lean
                    delete cleanData.avatarUrl;
                }
            } else if (cleanData.avatarUrl === 'none') {
                try {
                    const storageRef = ref(storage, `users/${user.uid}/avatar/profile.webp`);
                    await deleteObject(storageRef).catch(() => { });
                } catch (delErr) { }
                cleanData.avatarUrl = 'none';
            } else if (cleanData.avatarUrl === null || data.avatarUrl === null) {
                try {
                    const storageRef = ref(storage, `users/${user.uid}/avatar/profile.webp`);
                    await deleteObject(storageRef).catch(() => { });
                } catch (delErr) { }
                delete cleanData.avatarUrl;
            }

            const settingsToSync = cleanData.settings;
            delete cleanData.settings;

            const updates = { ...cleanData };
            if (data.avatarUrl === null) {
                updates.avatarUrl = deleteField();
            }
            if (settingsToSync) {
                Object.entries(settingsToSync).forEach(([k, v]) => {
                    if (k === 'mobile') {
                        if (v && typeof v === 'object') {
                            Object.entries(v).forEach(([mk, mv]) => {
                                updates[`settings.${mk}`] = mv;
                            });
                        }
                    } else {
                        updates[`settings.${k}`] = v;
                    }
                });
            }

            await updateDoc(doc(db, "users", user.uid), updates);

            // Synchronize local cached state with the clean Cloud Storage URL
            if (cleanData.avatarUrl && cleanData.avatarUrl !== 'none') {
                localStorage.setItem('userProfileImg', cleanData.avatarUrl);
                if (window.userData) window.userData.avatarUrl = cleanData.avatarUrl;
            } else if (cleanData.avatarUrl === 'none') {
                localStorage.setItem('userProfileImg', 'none');
                if (window.userData) window.userData.avatarUrl = 'none';
            } else if (data.avatarUrl === null) {
                localStorage.removeItem('userProfileImg');
                if (window.userData) delete window.userData.avatarUrl;
            }

            return { success: true, avatarUrl: cleanData.avatarUrl || null };
        } catch (e) {
            console.error("Profile sync failed:", e);
            return { success: false, error: e.message };
        }
    },
    syncProfile: async () => {
        if (typeof window.syncProfileWithCloud === 'function') {
            await window.syncProfileWithCloud();
        }
    },
    isSocialUser: () => {
        const user = auth.currentUser;
        if (!user) return false;
        return user.providerData.some(p => p.providerId === 'google.com');
    },
    reverify: async (password) => {
        const user = auth.currentUser;
        if (!user) return { success: false, error: "No active session." };
        try {
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
            return { success: true };
        } catch (error) {
            console.error("Cloud Verification Failed:", error);
            let msg = "Incorrect password. Verification failed.";
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
                msg = "Incorrect password. Please verify and try again.";
            } else if (error.code === 'auth/user-mismatch') {
                msg = "Security: Credential mismatch. This password belongs to a different account.";
            } else if (error.code === 'auth/too-many-requests') {
                msg = "Security: Too many failed attempts. Account locked temporarily.";
                const limiter = window.CosmoSecurityLimiter || (window.parent && window.parent.CosmoSecurityLimiter);
                if (limiter) limiter.forceLock('account_verify', 5);
            }
            return { success: false, error: msg };
        }
    },
    changePassword: async (oldPassword, newPassword) => {
        const user = auth.currentUser;
        if (!user) return { success: false, error: "No active session." };
        if (!newPassword || newPassword.length < 6) {
            return { success: false, error: "Password must be at least 6 characters." };
        }
        try {
            const isGoogleUser = user.providerData && user.providerData.some(p => p.providerId === 'google.com');
            const hasPasswordProvider = user.providerData && user.providerData.some(p => p.providerId === 'password');

            // If Google user creating a password for the first time
            if (isGoogleUser && !hasPasswordProvider) {
                await updatePassword(user, newPassword);
                const userRef = doc(db, "users", user.uid);
                await updateDoc(userRef, { hasPasswordSet: true });
                return { success: true, message: "Password created successfully." };
            }

            if (!oldPassword) {
                return { success: false, error: "Current password is required." };
            }

            const credential = EmailAuthProvider.credential(user.email, oldPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);

            // 🔐 Dispatch Password Changed Security Notification Email
            try {
                let loc = null;
                try { loc = await detectLocation(); } catch (e) { }
                if (window.sendPasswordChangedEmail) {
                    await window.sendPasswordChangedEmail(user.email, loc);
                } else if (window.sendTemplateEmail) {
                    await window.sendTemplateEmail(user.email, 'password-changed', {
                        location: loc,
                        time: formatLoginTimeClient()
                    }, 'Password Changed Successfully — Cosmo Crypt Security');
                }
            } catch (mailErr) {
                console.warn("Failed to dispatch password changed security email:", mailErr);
            }

            return { success: true };
        } catch (error) {
            console.error("Cloud Password Update Failed:", error);
            return { success: false, error: error.message };
        }
    },
    setInitialPassword: async (newPassword) => {
        let user = auth.currentUser;
        if (!user) return { success: false, error: "No active session." };
        try {
            if (!newPassword || newPassword.length < 6) {
                return { success: false, error: "Password must be at least 6 characters." };
            }

            const isGoogleUser = user.providerData && user.providerData.some(p => p.providerId === 'google.com');
            const emailCred = EmailAuthProvider.credential(user.email, newPassword);

            const applyPassword = async (currentUser) => {
                const hasPasswordProvider = currentUser.providerData && currentUser.providerData.some(p => p.providerId === 'password');
                if (!hasPasswordProvider) {
                    try {
                        await linkWithCredential(currentUser, emailCred);
                    } catch (linkErr) {
                        if (linkErr.code === 'auth/provider-already-linked' || linkErr.code === 'auth/credential-already-in-use') {
                            await updatePassword(currentUser, newPassword);
                        } else {
                            throw linkErr;
                        }
                    }
                } else {
                    await updatePassword(currentUser, newPassword);
                }
            };

            try {
                await applyPassword(user);
            } catch (firstErr) {
                // If Firebase requires recent login, automatically trigger Google re-auth popup
                if (firstErr.code === 'auth/requires-recent-login' && isGoogleUser) {
                    console.log("Re-authenticating Google OAuth session for password update...");
                    const provider = createSecureGoogleProvider();
                    const reauthRes = await reauthenticateWithPopup(user, provider);
                    user = reauthRes.user || auth.currentUser;
                    await applyPassword(user);
                } else {
                    throw firstErr;
                }
            }

            const userRef = doc(db, "users", user.uid);
            await updateDoc(userRef, { hasPasswordSet: true });

            // 🔐 Dispatch Password Changed Security Email asynchronously in background (non-blocking)
            (async () => {
                try {
                    let loc = null;
                    try { loc = await detectLocation(); } catch (e) { }
                    if (window.sendPasswordChangedEmail) {
                        await window.sendPasswordChangedEmail(user.email, loc);
                    }
                } catch (mailErr) {
                    console.warn("Password notification email notice:", mailErr);
                }
            })().catch(() => { });

            return { success: true, message: "Master password created successfully!" };
        } catch (err) {
            console.error("Set initial password error:", err);
            if (err.code === 'auth/requires-recent-login') {
                return { success: false, error: "Recent login required. Please verify your Google account to set a password." };
            }
            if (err.code === 'auth/popup-closed-by-user') {
                return { success: false, error: "Google verification was cancelled." };
            }
            return { success: false, error: err.message || "Failed to set password." };
        }
    },
    sendGoogleUserPhoneOtp: async (phoneNumber, containerId = 'recaptcha-phone-box', localNumber = null) => {
        const user = auth.currentUser;
        if (!user) return { success: false, error: "No active session." };
        try {
            const cleanPhone = (phoneNumber || '').replace(/\s+/g, '');
            const cleanLocal = localNumber ? String(localNumber).replace(/[^0-9]/g, '') : cleanPhone.slice(-10);

            // 🛡️ 1. Pre-Check: Verify that this mobile number is not already registered with another account
            try {
                // Check by full international number (e.g. +919876543210)
                const qFull = query(collection(db, "users"), where("phoneNumber", "==", cleanPhone), limit(1));
                const snapFull = await getDocs(qFull);
                if (!snapFull.empty && snapFull.docs[0].id !== user.uid) {
                    return { success: false, error: "This mobile number is already linked to another Cosmo Crypt account. Please use a different number." };
                }

                // Check by local 10-digit number (e.g. 9876543210)
                if (cleanLocal && cleanLocal.length >= 7) {
                    const qLocal = query(collection(db, "users"), where("phone", "==", cleanLocal), limit(1));
                    const snapLocal = await getDocs(qLocal);
                    if (!snapLocal.empty && snapLocal.docs[0].id !== user.uid) {
                        return { success: false, error: "This mobile number is already linked to another Cosmo Crypt account. Please use a different number." };
                    }
                }
            } catch (checkErr) {
                console.warn("Phone uniqueness pre-check notice:", checkErr);
            }

            // 🛡️ 2. Clean up previous reCAPTCHA instance
            if (window.__googlePhoneRecaptcha) {
                try { window.__googlePhoneRecaptcha.clear(); } catch (e) { }
                window.__googlePhoneRecaptcha = null;
            }

            // Ensure a valid DOM element in current execution context
            let container = document.getElementById(containerId);
            if (container) {
                try { container.remove(); } catch (e) { }
            }
            container = document.createElement('div');
            container.id = containerId;
            document.body.appendChild(container);

            window.__googlePhoneRecaptcha = new RecaptchaVerifier(auth, container, {
                size: 'invisible',
                callback: () => { }
            });

            const phoneProvider = new PhoneAuthProvider(auth);
            const verificationId = await phoneProvider.verifyPhoneNumber(cleanPhone, window.__googlePhoneRecaptcha);
            window.__googlePhoneVerificationId = verificationId;
            if (window.parent) window.parent.__googlePhoneVerificationId = verificationId;

            return { success: true, message: `6-Digit SMS verification code dispatched to ${cleanPhone}.` };
        } catch (err) {
            console.error("Send Google Phone OTP error:", err);
            if (window.__googlePhoneRecaptcha) {
                try { window.__googlePhoneRecaptcha.clear(); } catch (e) { }
                window.__googlePhoneRecaptcha = null;
            }
            let errMsg = err.message || "Failed to dispatch SMS code.";
            if (err.code === 'auth/invalid-app-credential') {
                errMsg = "Firebase App Credential Error: reCAPTCHA verification failed or domain is not authorized. In Firebase Console, go to Authentication -> Settings -> Authorized Domains and ensure 'localhost' is listed, or test with a Test Phone Number (e.g. +91 9876543210 with code 123456).";
            } else if (err.code === 'auth/operation-not-allowed' || (errMsg && errMsg.includes('region enabled'))) {
                errMsg = "SMS region not enabled yet. Please allow your country (+91 India, +1 US, etc.) in Firebase Console -> Authentication -> Settings -> SMS Region Policy, or add your number as a Test Phone Number.";
            } else if (err.code === 'auth/invalid-phone-number') {
                errMsg = "Invalid phone number format. Please enter a valid mobile number.";
            } else if (err.code === 'auth/quota-exceeded') {
                errMsg = "Daily SMS quota exceeded. Please try again later.";
            }
            return { success: false, error: errMsg };
        }
    },
    confirmAndLinkGooglePhone: async (smsCode, phoneData) => {
        let user = auth.currentUser;
        if (!user) return { success: false, error: "No active session." };

        const verificationId = window.__googlePhoneVerificationId || (window.parent && window.parent.__googlePhoneVerificationId) || (window.__googlePhoneConfirmation && window.__googlePhoneConfirmation.verificationId);
        if (!verificationId) {
            return { success: false, error: "SMS session expired. Please request a new verification code." };
        }

        const cleanCode = (smsCode || '').trim();
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter the 6-digit verification code." };
        }

        try {
            const credential = PhoneAuthProvider.credential(verificationId, cleanCode);

            try {
                await linkWithCredential(user, credential);
            } catch (linkErr) {
                console.warn("Phone link credential notice:", linkErr);
                if (linkErr.code === 'auth/invalid-verification-code') {
                    return { success: false, error: "Invalid 6-digit verification code. Please enter the correct code." };
                }
                if (linkErr.code === 'auth/code-expired' || linkErr.code === 'auth/session-expired') {
                    return { success: false, error: "Verification code has expired. Please request a new SMS OTP." };
                }
                if (linkErr.code === 'auth/credential-already-in-use' || linkErr.code === 'auth/provider-already-linked') {
                    console.log("Phone credential verified; linking to user profile.");
                } else if (linkErr.code === 'auth/requires-recent-login') {
                    try {
                        const provider = createSecureGoogleProvider();
                        const reauthRes = await reauthenticateWithPopup(user, provider);
                        user = reauthRes.user || auth.currentUser;
                        await linkWithCredential(user, credential);
                    } catch (reauthErr) {
                        console.warn("Re-auth for phone link warning:", reauthErr);
                    }
                } else {
                    return { success: false, error: linkErr.message || "Failed to verify SMS code." };
                }
            }

            const cleanLocal = String(phoneData.localNumber || '').replace(/[^0-9]/g, '');
            const cleanDial = (phoneData.dialCode || '+91').trim();
            const cleanFull = (cleanDial + cleanLocal).replace(/\s+/g, '');
            const cleanFlag = phoneData.flag || '🌐';

            const userRef = doc(db, "users", user.uid);
            const updates = {
                phone: cleanLocal,
                code: cleanDial,
                phoneNumber: cleanFull,
                flag: cleanFlag,
                isPhoneVerified: true
            };
            await updateDoc(userRef, updates);

            window.__googlePhoneConfirmation = null;
            if (window.parent) window.parent.__googlePhoneConfirmation = null;

            return { success: true, message: "Phone number verified and linked successfully!" };
        } catch (err) {
            console.error("Confirm Google phone link error:", err);
            return { success: false, error: err.message || "Verification failed." };
        }
    },
    getAccountDiagnostics: async () => {
        const user = auth.currentUser;
        if (!user) return null;
        return {
            email: user.email,
            isSocial: user.providerData.some(p => p.providerId === 'google.com'),
            providers: user.providerData.map(p => p.providerId),
            uid: user.uid
        };
    },
    sendResetEmail: async (email) => {
        try {
            // 📧 [RESEND] Trigger custom HTML email template via Resend Cloud Function
            // (Avoids calling default client-side sendPasswordResetEmail which lands in spam)
            const customReset = httpsCallable(functions, 'sendPasswordResetEmail');
            await customReset({ email });

            return { success: true };
        } catch (error) {
            console.error("Resend Password Reset Error:", error);
            let userMsg = error.message || "Unable to send password reset email.";
            let isNotFound = false;
            if (error.code === 'functions/not-found' || error.message.includes('not-found') || error.message.includes('Account not found') || error.message.includes('user-not-found') || error.message.includes('INTERNAL ASSERT FAILED')) {
                userMsg = "Account not found! No account is registered with this email address. Please create a new account.";
                isNotFound = true;
            }
            return { success: false, error: userMsg, isNotFound: isNotFound };
        }
    },
    verifyPassword: async (identifier, password) => {
        try {
            const user = auth.currentUser;
            if (!user) return { success: false, error: "Session Expired: Please login again." };

            const email = user.email || identifier;
            const credential = EmailAuthProvider.credential(email, password);
            await reauthenticateWithCredential(user, credential);

            return { success: true };
        } catch (error) {
            console.error("🛡️ Cloud Verification: FAILED [No]", error.code);
            let msg = "Incorrect password. Please verify and try again.";
            if (error.code === 'auth/too-many-requests') {
                msg = "Security: Too many failed attempts. Account locked temporarily.";
            } else if (error.code === 'auth/user-mismatch') {
                msg = "Security: Credential mismatch. Please use the password for the current account.";
            }
            return { success: false, error: msg };
        }
    },
    pushSettingsToCloud: async () => {
        if (settingsSyncTimeout) {
            clearTimeout(settingsSyncTimeout);
            settingsSyncTimeout = null;
        }
        const user = auth.currentUser;
        if (!user) {
            isSettingsSyncPending = false;
            return;
        }

        isSettingsSyncPending = true;
        try {
            const settings = {};
            const keys = [
                'suite_sound_muted', 'suite_sound_volume',
                'suite_typing_enabled', 'suite_typing_volume', 'suite_typing_tune',
                'suite_bottom_nav_enabled', 'suite_bottom_nav_volume',
                'nav_anim_enabled',
                'session_protocol_enabled',
                'fullscreen_on_click',
                'cosmo_search_enabled'
            ];
            const defaults = {
                'suite_sound_muted': 'false',
                'suite_sound_volume': '0.7',
                'suite_typing_enabled': 'true',
                'suite_typing_volume': '0.5',
                'suite_typing_tune': 'TYPING_MECHANICAL',
                'suite_bottom_nav_enabled': 'true',
                'suite_bottom_nav_volume': '0.5',
                'nav_anim_enabled': 'true',
                'session_protocol_enabled': 'true',
                'fullscreen_on_click': 'false',
                'cosmo_search_enabled': 'false'
            };
            keys.forEach(k => {
                let v = localStorage.getItem(k);
                if (v === null && defaults[k] !== undefined) {
                    v = defaults[k];
                }
                if (v !== null) settings[k] = v;
            });

            const mode = localStorage.getItem('active_layer_mode') || '1';
            settings['active_layer_mode'] = mode;
            settings['font'] = FONT_MAP[mode] || "Space Grotesk";

            const cleanSettings = JSON.parse(JSON.stringify(settings));
            const updates = {
                "mobile": deleteField(),
                "settings.mobile": deleteField()
            };
            // Preserve card_mutation_config and secure_pdf_config specifically in Firestore settings map
            const currentUd = window.userData || (window.parent && window.parent.userData);
            if (currentUd && currentUd.settings) {
                if (currentUd.settings.card_mutation_config) {
                    updates["settings.card_mutation_config"] = currentUd.settings.card_mutation_config;
                }
                if (currentUd.settings.secure_pdf_config) {
                    updates["settings.secure_pdf_config"] = currentUd.settings.secure_pdf_config;
                }
            }
            // Use dot notation to avoid wiping out other nested settings (like card_mutation_config, secure_pdf_config)
            Object.entries(cleanSettings).forEach(([k, v]) => {
                updates[`settings.${k}`] = v;
            });
            // Mirror cosmo_search into choices.cosmo_search
            const isCosmoSearchActive = localStorage.getItem('cosmo_search_enabled') !== 'false';
            updates["choices.cosmo_search"] = isCosmoSearchActive;
            await updateDoc(doc(db, "users", user.uid), updates);
            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logSettingsUpdate(Object.keys(settings).length);
            }
        } catch (err) {
            console.warn("☁️ Cloud: Settings sync failed", err);
        } finally {
            isSettingsSyncPending = false;
        }
    },
    removeAppLockFromCloud: async () => {
        const user = auth.currentUser;
        if (!user) return;
        try {
            const userRef = doc(db, "users", user.uid);
            await updateDoc(userRef, {
                app_lock_config: deleteField()
            });
        } catch (err) {
            console.error("🛡️ Cloud: Failed to purge App Lock data", err);
        }
    },
    emailLogin: async (identifier, password) => {
        try {
            try {
                localStorage.removeItem('explicit_logout');
                sessionStorage.removeItem('explicit_logout');
                window._userExplicitlyLoggedOut = false;
                window._cosmoLoginInProgress = true;
                const oldOverlay = document.getElementById('cosmo-deauth-overlay');
                if (oldOverlay) oldOverlay.remove();
            } catch (e) { }
            let email = identifier.trim();

            const isPhone = /^[0-9+ \-]+$/.test(email) && !email.includes('@');
            let userCredential;

            if (!isPhone) {
                // ⚡ Concurrently verify lockout and credentials via Promise.all (saves 1500ms)
                const checkLockoutPromise = (async () => {
                    try {
                        const checkLockoutFn = httpsCallable(functions, 'checkLoginLockout');
                        const lockRes = await Promise.race([
                            checkLockoutFn({ identifier }),
                            new Promise(resolve => setTimeout(() => resolve({ timeout: true }), 1500))
                        ]);
                        return lockRes && lockRes.data;
                    } catch (e) {
                        return null;
                    }
                })();

                const signInPromise = (async () => {
                    try {
                        return await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
                    } catch (e1) {
                        if (e1.code === 'auth/invalid-credential' || e1.code === 'auth/user-not-found' || e1.code === 'auth/invalid-email') {
                            return await signInWithEmailAndPassword(auth, email, password);
                        }
                        throw e1;
                    }
                })();

                const [lockData, cred] = await Promise.all([checkLockoutPromise, signInPromise]);

                if (lockData && lockData.isLocked) {
                    window._cosmoLoginInProgress = false;
                    try { await signOut(auth); } catch (e) { }
                    return {
                        success: false,
                        isLocked: true,
                        remainingSec: lockData.remainingSec || 900,
                        lockoutUntil: lockData.lockoutUntil,
                        error: lockData.error || `Security Lock: Account temporarily locked due to multiple failed login attempts.`
                    };
                }
                userCredential = cred;
            } else {
                let foundEmail = null;
                const cleanPhone = email.trim();
                const noSpacePhone = cleanPhone.replace(/\s+/g, '');

                let localNum = cleanPhone;
                let inputCode = null;
                if (cleanPhone.startsWith('+')) {
                    const parts = cleanPhone.split(' ');
                    if (parts.length > 1) {
                        inputCode = parts[0];
                        localNum = parts.slice(1).join('').replace(/\s+/g, '');
                    } else {
                        const match = cleanPhone.match(/^(\+\d{1,4})(\d+)$/);
                        if (match) {
                            inputCode = match[1];
                            localNum = match[2];
                        }
                    }
                }

                // 🛡️ Resolve phone securely via Backend Cloud Function (NO client-side Firestore query!)
                try {
                    const resolvePhoneFn = httpsCallable(functions, 'resolvePhoneForLogin');
                    const res = await resolvePhoneFn({
                        phone: cleanPhone,
                        countryCode: inputCode
                    });

                    if (!res.data || !res.data.success) {
                        window._cosmoLoginInProgress = false;
                        return {
                            success: false,
                            error: (res.data && res.data.error) || "No account found with this phone number."
                        };
                    }

                    email = res.data.email;
                } catch (fnErr) {
                    window._cosmoLoginInProgress = false;
                    console.error("Phone resolution error:", fnErr);
                    return {
                        success: false,
                        error: "Unable to verify phone number. Please try again."
                    };
                }

                try {
                    userCredential = await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
                } catch (e1) {
                    if (e1.code === 'auth/invalid-credential' || e1.code === 'auth/user-not-found' || e1.code === 'auth/invalid-email') {
                        userCredential = await signInWithEmailAndPassword(auth, email, password);
                    } else {
                        throw e1;
                    }
                }
            }

            const user = userCredential.user;
            const userSnap = await getDoc(doc(db, "users", user.uid));
            let profileData;
            let isNewUser = false;
            if (userSnap.exists()) {
                const cloudData = userSnap.data();

                const localChoices = JSON.parse(localStorage.getItem('user_choices') || '{}');
                const localAppLock = localStorage.getItem('\x61\x70\x70\x5f\x6c\x6f\x63\x6b\x5f\x63\x6f\x6e\x66\x69\x67');

                const updates = {
                    choices: { ...(cloudData.choices || {}), ...localChoices },
                    lastLogin: cloudData.currentLogin ? formatTo24H(cloudData.currentLogin) : "FIRST SESSION",
                    currentLogin: getStandard24HDateTime()
                };

                if (!cloudData.code) {
                    updates.code = (typeof userDocCode !== 'undefined' && userDocCode) ? userDocCode : '+91';
                }

                if (localAppLock && !cloudData.app_lock_config) {
                    try { updates.app_lock_config = JSON.parse(localAppLock); } catch (e) { }
                }

                updateDoc(doc(db, "users", user.uid), updates).catch(err => console.warn("Email login updateDoc notice:", err));
                profileData = { ...cloudData, ...updates };

                if (cloudData.pdf_telemetry) {
                    const rawDl = typeof cloudData.pdf_telemetry.downloads_count === 'number' ? cloudData.pdf_telemetry.downloads_count : parseInt(cloudData.pdf_telemetry.downloads_count || '0', 10);
                    const rawEnc = typeof cloudData.pdf_telemetry.encrypted_downloads_count === 'number' ? cloudData.pdf_telemetry.encrypted_downloads_count : parseInt(cloudData.pdf_telemetry.encrypted_downloads_count || '0', 10);
                    const curDl = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.downloads_count) || 0;
                    const curEnc = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.encrypted_downloads_count) || 0;
                    const telemState = {
                        downloads_count: Math.max(curDl, isNaN(rawDl) ? 0 : rawDl),
                        encrypted_downloads_count: Math.max(curEnc, isNaN(rawEnc) ? 0 : rawEnc),
                        last_updated: cloudData.pdf_telemetry.last_updated || new Date().toISOString()
                    };
                    window._cosmoPdfTelemetryState = telemState;
                    if (window.userData) window.userData.pdf_telemetry = telemState;
                }

                // 🔐 2-STEP VERIFICATION GATE (PASSWORD AUTH)
                if (cloudData.two_factor_enabled === true) {
                    sessionStorage.removeItem('__cc_2fa_passed_' + user.uid);
                    localStorage.removeItem('userStoreEnc');
                    window.__pending2FAUser = { user, profileData, updates, authProvider: 'password' };
                    return {
                        success: false,
                        requireTwoFactor: true,
                        tempUid: user.uid,
                        email: user.email,
                        twoFactorType: cloudData.two_factor_type || 'totp'
                    };
                }
            } else {
                isNewUser = true;
                console.warn("🚨 Profile missing in Firestore. Creating default...");
                const nameParts = user.displayName ? user.displayName.split(' ') : ["Cloud", "User"];

                const localChoices = JSON.parse(localStorage.getItem('user_choices') || '{}');
                const localAppLock = localStorage.getItem('\x61\x70\x70\x5f\x6c\x6f\x63\x6b\x5f\x63\x6f\x6e\x66\x69\x67');

                profileData = {
                    first: nameParts[0],
                    last: nameParts.slice(1).join(' ') || "User",
                    email: user.email,
                    uid: user.uid,
                    joined: getStandard24HDateTime(),
                    currentLogin: getStandard24HDateTime(),
                    lastLogin: "RESTORED SESSION",
                    authProvider: 'password',
                    isVerified: true,
                    choices: localChoices,
                    role: ownerEmails.includes(user.email.toLowerCase()) ? 'owner' : 'user'
                };

                if (localAppLock) {
                    try { profileData.app_lock_config = JSON.parse(localAppLock); } catch (e) { }
                }
            }

            if (isNewUser) {
                await setDoc(doc(db, "users", user.uid), profileData);
            }

            delete profileData.password;
            delete profileData.confirm;

            window.userData = profileData;
            if (window.parent && window.parent !== window) {
                window.parent.userData = profileData;
            }

            // 🌟 Fetch template vaults subcollection in background (non-blocking)
            if (!profileData.template_vaults) profileData.template_vaults = {};
            (async () => {
                try {
                    const vaultsSnap = await getDocs(collection(db, "users", user.uid, "template_vaults"));
                    const decryptPromises = [];
                    vaultsSnap.forEach(vDoc => {
                        if (vDoc.data() && vDoc.data().data !== undefined) {
                            decryptPromises.push((async () => {
                                const rawVal = vDoc.data().data;
                                const decVal = await decryptVaultPayload(rawVal, user);
                                profileData.template_vaults[vDoc.id] = decVal;
                                if (typeof rawVal !== 'string' || (!rawVal.startsWith('cc_gcm256:') && !rawVal.startsWith('U2FsdGVkX1'))) {
                                    encryptVaultPayload(decVal, user).then(migratedEnc => {
                                        if (migratedEnc && typeof migratedEnc === 'string') {
                                            setDoc(doc(db, "users", user.uid, "template_vaults", vDoc.id), {
                                                data: migratedEnc,
                                                encrypted: true,
                                                cipher: 'AES-GCM-256',
                                                updatedAt: Date.now()
                                            }, { merge: true }).catch(() => { });
                                        }
                                    }).catch(() => { });
                                }
                            })());
                        }
                    });
                    await Promise.all(decryptPromises);
                } catch (vErr) {
                    console.warn("Template vaults pre-fetch warning:", vErr);
                }

                if (profileData.template_vaults && typeof profileData.template_vaults === 'object') {
                    const rootDecryptPromises = [];
                    for (const k of Object.keys(profileData.template_vaults)) {
                        rootDecryptPromises.push((async () => {
                            const rawVal = profileData.template_vaults[k];
                            const decVal = await decryptVaultPayload(rawVal, user);
                            profileData.template_vaults[k] = decVal;
                            if (typeof rawVal !== 'string' || (!rawVal.startsWith('cc_gcm256:') && !rawVal.startsWith('U2FsdGVkX1'))) {
                                encryptVaultPayload(decVal, user).then(migratedEnc => {
                                    if (migratedEnc && typeof migratedEnc === 'string') {
                                        setDoc(doc(db, "users", user.uid), {
                                            template_vaults: { [k]: migratedEnc }
                                        }, { merge: true }).catch(() => { });
                                    }
                                }).catch(() => { });
                            }
                        })());
                    }
                    await Promise.all(rootDecryptPromises);
                }

                if (window.CosmoTemplateStore && typeof window.CosmoTemplateStore.notifyUpdate === 'function') {
                    Object.keys(profileData.template_vaults).forEach(k => {
                        window.CosmoTemplateStore.notifyUpdate(k, profileData.template_vaults[k]);
                    });
                }
            })();

            window.userData = profileData;
            if (window.parent && window.parent !== window) {
                window.parent.userData = profileData;
            }

            sessionStorage.setItem('__cc_auth_provider', 'password');
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
            sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

            if (typeof window.loadLockoutFromCloud === 'function') {
                window.loadLockoutFromCloud().catch(() => { });
            }

            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('login_success', 'email');
            }

            if (typeof window.addSuiteNotification === 'function') {
                window.addSuiteNotification('account', 'New Login Detected', `Your account was accessed via Email from ${navigator.userAgent.includes('Windows') ? 'Windows' : 'Mobile Device'}.`, { device: navigator.userAgent.substring(0, 30), provider: 'Email', status: 'Success' });
            }

            // 📧 [RESEND] Trigger Login Notification in background
            (async () => {
                try {
                    const loc = await detectLocation();
                    const notifyLogin = httpsCallable(functions, 'onLoginDetected');
                    notifyLogin({
                        email: profileData.email,
                        device: getCleanDeviceAndBrowser(navigator.userAgent),
                        location: loc,
                        name: profileData.first || 'User'
                    });
                } catch (e) { }
            })();

            // 🔄 Force fresh device ID on every login so revoked IDs never persist
            getOrCreateDeviceId(true);
            // 📱 Proactively Register and Synchronize Current Device Session in background
            if (typeof window.registerCurrentDeviceSession === 'function') {
                window.registerCurrentDeviceSession(user.uid).catch(devErr => {
                    console.warn("⚠️ Device session registration notice:", devErr);
                });
            }
            window._cosmoLoginInProgress = false;

            // 🛡️ Reset server login attempts on successful authentication
            try {
                const recordAttemptFn = httpsCallable(functions, 'recordLoginAttempt');
                recordAttemptFn({ identifier, success: true }).catch(() => { });
            } catch (e) { }

            return { success: true, user: profileData };
        } catch (error) {
            console.error("Firebase Login Error:", error);
            window._cosmoLoginInProgress = false;
            let msg = error.message;
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
                msg = "Incorrect password. Please verify and try again.";
            } else if (error.code === 'auth/user-not-found') {
                msg = "Account not found. Please check the email spelling or register.";
            } else if (error.code === 'auth/too-many-requests') {
                msg = "Security block: Too many failed attempts. Try again later.";
            }

            let lockoutInfo = { isLocked: false };
            // 🛡️ Server-Enforced Failed Attempt Recording
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
                try {
                    const recordAttemptFn = httpsCallable(functions, 'recordLoginAttempt');
                    const recRes = await recordAttemptFn({ identifier, success: false });
                    if (recRes && recRes.data) {
                        lockoutInfo = recRes.data;
                        if (lockoutInfo.error) {
                            msg = lockoutInfo.error;
                        }
                    }
                } catch (recErr) {
                    console.warn("Could not record failed login attempt on server:", recErr);
                }
            }

            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('login_failure', 'email');
                window.cosmoAnalytics.logError(error.message, 'email_login');
            }
            return {
                success: false,
                error: msg,
                code: error.code,
                isLocked: lockoutInfo.isLocked || false,
                remainingSec: lockoutInfo.remainingSec || 0,
                lockoutUntil: lockoutInfo.lockoutUntil || null,
                attemptsRemaining: lockoutInfo.attemptsRemaining
            };
        }
    },
    // --- 2-STEP VERIFICATION (2FA & OTP) BRIDGE METHODS & RFC 6238 ENGINE ---
    setupTwoFactor: async () => {
        try {
            // Try cloud function first
            try {
                const genCall = httpsCallable(functions, 'generateTwoFactorSecret');
                const res = await genCall({});
                if (res && res.data && res.data.success) {
                    return res.data;
                }
            } catch (cloudErr) {
                console.info("Using resilient local 2FA generator:", cloudErr);
            }

            // Robust local generation fallback
            const user = auth.currentUser;
            const email = (user && user.email) || 'user@cosmocrypt.com';
            const uid = user ? user.uid : null;

            const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
            let secret = "";
            const randomBytes = new Uint8Array(16);
            crypto.getRandomValues(randomBytes);
            for (let i = 0; i < 16; i++) {
                secret += charset[randomBytes[i] % 32];
            }

            const otpauth = `otpauth://totp/Cosmo%20Crypt:${encodeURIComponent(email)}?secret=${secret}&issuer=Cosmo%20Crypt`;
            const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(otpauth)}&margin=10`;

            if (uid) {
                try {
                    await updateDoc(doc(db, "users", uid), {
                        temp_two_factor_secret: secret,
                        temp_two_factor_created: Date.now()
                    });
                } catch (e) {
                    console.warn("Firestore temp secret update notice:", e);
                }
            }

            window.__current2FASetupSecret = secret;

            return {
                success: true,
                secret: secret,
                qrCodeUrl: qrCodeUrl,
                otpauth: otpauth,
                email: email
            };
        } catch (err) {
            console.error("2FA Setup Error:", err);
            return { success: false, error: err.message || "Failed to generate 2FA secret." };
        }
    },
    confirmTwoFactorSetup: async (code, method = 'totp') => {
        const cleanCode = (code || '').trim();
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter a valid 6-digit confirmation code." };
        }

        try {
            // Try cloud function first
            try {
                const confirmCall = httpsCallable(functions, 'verifyAndEnableTwoFactor');
                const res = await confirmCall({ code: cleanCode, method });
                if (res && res.data && res.data.success) {
                    _updateLocal2FASession(method);
                    return res.data;
                }
            } catch (cloudErr) {
                console.info("Using local TOTP confirmation fallback:", cloudErr);
            }

            const user = auth.currentUser;
            const uid = user ? user.uid : null;
            let secret = window.__current2FASetupSecret;

            if (uid) {
                try {
                    const snap = await getDoc(doc(db, "users", uid));
                    if (snap.exists() && snap.data().temp_two_factor_secret) {
                        secret = snap.data().temp_two_factor_secret;
                    }
                } catch (e) { }
            }

            if (method === 'totp') {
                const isValid = await _verifyTotpToken(secret, cleanCode);
                if (!isValid) {
                    return { success: false, error: "Invalid 6-digit code. Please check your Authenticator app and try again." };
                }

                if (uid) {
                    await updateDoc(doc(db, "users", uid), {
                        two_factor_enabled: true,
                        two_factor_type: 'totp',
                        two_factor_secret: secret,
                        temp_two_factor_secret: deleteField(),
                        two_factor_updated: Date.now()
                    });
                }

                _updateLocal2FASession('totp');
                return { success: true, message: "Two-Step Verification (Google Authenticator) activated successfully!" };
            } else {
                // Method: Email OTP
                const email = (user && user.email ? user.email : '').toLowerCase();
                let isEmailOtpValid = false;

                if (email) {
                    try {
                        const otpSnap = await getDoc(doc(db, "email_otps", email));
                        if (otpSnap.exists()) {
                            const otpData = otpSnap.data();
                            if (Date.now() <= otpData.expiresAt && otpData.hashedCode === _hashEmailOtp(cleanCode)) {
                                isEmailOtpValid = true;
                                await deleteDoc(doc(db, "email_otps", email));
                            }
                        }
                    } catch (e) {
                        console.warn("Email OTP local check warning:", e);
                    }
                }

                if (!isEmailOtpValid) {
                    return { success: false, error: "Invalid 6-digit email code. Please enter the exact code sent to your email." };
                }

                if (uid) {
                    await updateDoc(doc(db, "users", uid), {
                        two_factor_enabled: true,
                        two_factor_type: 'email',
                        two_factor_updated: Date.now()
                    });
                }

                _updateLocal2FASession('email');
                return { success: true, message: "Two-Step Verification (Email OTP) activated successfully!" };
            }
        } catch (err) {
            console.error("2FA Confirmation Error:", err);
            return { success: false, error: err.message || "Failed to confirm 2FA setup." };
        }
    },
    disableTwoFactor: async (payload = '') => {
        let method = 'code';
        let cleanCode = '';

        if (typeof payload === 'string') {
            if (payload.toLowerCase() === 'passkey') {
                method = 'passkey';
            } else {
                cleanCode = payload.trim();
            }
        } else if (payload && typeof payload === 'object') {
            method = (payload.method || (payload.code ? 'code' : 'passkey')).toLowerCase();
            cleanCode = (payload.code || '').trim();
        }

        const user = auth.currentUser;
        const uid = user ? user.uid : null;
        if (!uid) {
            return { success: false, error: "User authentication required." };
        }

        // --- 1. Passkey Verification Path ---
        if (method === 'passkey') {
            let allowedIds = [];
            try {
                const snap = await getDoc(doc(db, "users", uid, "security", "passkeys"));
                if (snap.exists()) {
                    const data = snap.data();
                    Object.keys(data).forEach(k => {
                        if (k !== 'updatedAt' && data[k] && data[k].id) {
                            if (!allowedIds.includes(data[k].id)) allowedIds.push(data[k].id);
                        }
                    });
                }
            } catch (e) { }

            try {
                let challenge = null;
                try {
                    const getChallengeCall = httpsCallable(functions, 'generatePasskeyServerChallenge');
                    const cRes = await getChallengeCall({ uid: uid, email: user.email, purpose: 'disable_2fa' });
                    if (cRes && cRes.data && cRes.data.challenge) {
                        challenge = cRes.data.challenge;
                    }
                } catch (cErr) { }

                const assertion = await authenticatePasskey({
                    customChallenge: challenge,
                    allowedCredentialIds: allowedIds
                });

                if (!assertion || !assertion.id) {
                    return { success: false, error: "Passkey verification failed or was cancelled." };
                }

                // Verify signature on server
                try {
                    const verifyCall = httpsCallable(functions, 'verifyPasskeyTwoFactor');
                    const vRes = await verifyCall({
                        uid: uid,
                        email: user.email,
                        credentialId: assertion.id,
                        clientDataJSON: assertion.response ? assertion.response.clientDataJSON : null,
                        authenticatorData: assertion.response ? assertion.response.authenticatorData : null,
                        signature: assertion.response ? assertion.response.signature : null,
                        challenge: challenge
                    });
                    if (vRes && vRes.data && vRes.data.success === false) {
                        return { success: false, error: vRes.data.error || "Passkey signature verification failed on server." };
                    }
                } catch (verifyCloudErr) { }
            } catch (passErr) {
                console.error("Passkey verification error:", passErr);
                return { success: false, error: passErr.message || "Passkey verification cancelled." };
            }

            try {
                const disableCall = httpsCallable(functions, 'disableTwoFactor');
                const res = await disableCall({ method: 'passkey' });
                if (res && res.data && res.data.success) {
                    _clearLocal2FASession();
                    return res.data;
                }
            } catch (cloudErr) {
                console.info("Using local 2FA disable fallback for passkey:", cloudErr);
            }

            // Local fallback for passkey
            await updateDoc(doc(db, "users", uid), {
                two_factor_enabled: false,
                two_factor_type: deleteField(),
                two_factor_secret: deleteField(),
                two_factor_updated: Date.now()
            });

            _clearLocal2FASession();
            return { success: true, message: "Two-Step Verification disabled successfully via Hardware Passkey." };
        }

        // --- 2. 6-Digit Code Path (TOTP / Email OTP) ---
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter the 6-digit confirmation code to turn off 2FA." };
        }

        try {
            // Try cloud function first
            try {
                const disableCall = httpsCallable(functions, 'disableTwoFactor');
                const res = await disableCall({ code: cleanCode, method });
                if (res && res.data) {
                    if (res.data.success) {
                        _clearLocal2FASession();
                    }
                    return res.data;
                }
            } catch (cloudErr) {
                console.info("Using local 2FA disable fallback:", cloudErr);
            }

            const userSnap = await getDoc(doc(db, "users", uid));
            if (!userSnap.exists()) {
                return { success: false, error: "User record not found." };
            }

            const userData = userSnap.data();
            let isValid = false;

            // 1. Check TOTP Authenticator code
            if (userData.two_factor_secret) {
                if (await _verifyTotpToken(userData.two_factor_secret, cleanCode)) {
                    isValid = true;
                }
            }

            // 2. Check Email OTP
            const email = (user.email || userData.email || '').toLowerCase();
            if (!isValid && email) {
                try {
                    const otpSnap = await getDoc(doc(db, "email_otps", email));
                    if (otpSnap.exists()) {
                        const otpData = otpSnap.data();
                        if (Date.now() <= otpData.expiresAt && otpData.hashedCode === _hashEmailOtp(cleanCode)) {
                            isValid = true;
                            await deleteDoc(doc(db, "email_otps", email));
                        }
                    }
                } catch (e) { }
            }

            if (!isValid) {
                return { success: false, error: "Invalid 6-digit code. Please enter the code from Google Authenticator, your email, or verify with Passkey." };
            }

            await updateDoc(doc(db, "users", uid), {
                two_factor_enabled: false,
                two_factor_type: deleteField(),
                two_factor_secret: deleteField(),
                two_factor_updated: Date.now()
            });

            _clearLocal2FASession();
            return { success: true, message: "Two-Step Verification disabled successfully." };
        } catch (err) {
            console.error("2FA Disable Error:", err);
            return { success: false, error: err.message || "Failed to disable 2FA." };
        }
    },
    verifyTwoFactorChallenge: async (code, method = 'totp') => {
        const cleanCode = (code || '').trim();
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter a valid 6-digit passcode." };
        }

        try {
            const pending = window.__pending2FAUser;
            if (!pending || !pending.user) {
                return { success: false, error: "No pending 2FA login session found. Please log in again." };
            }

            let isValid = false;
            let lastErrorMessage = null;

            // Try cloud function first
            try {
                const verifyCall = httpsCallable(functions, 'verifyTwoFactorLogin');
                const res = await verifyCall({
                    uid: pending.user.uid,
                    email: pending.user.email,
                    code: cleanCode,
                    method: method
                });
                if (res && res.data && res.data.success) {
                    isValid = true;
                } else if (res && res.data && res.data.error) {
                    lastErrorMessage = res.data.error;
                }
            } catch (cloudErr) {
                console.info("Using local 2FA challenge verification fallback:", cloudErr);
                lastErrorMessage = cloudErr.message;
            }

            if (!isValid) {
                // Client-side fallback check (for Authenticator app TOTP secret only)
                try {
                    const snap = await getDoc(doc(db, "users", pending.user.uid));
                    if (snap.exists()) {
                        const data = snap.data();
                        if (data.two_factor_secret && await _verifyTotpToken(data.two_factor_secret, cleanCode)) {
                            isValid = true;
                        }
                    }
                } catch (e) { }
            }

            if (!isValid) {
                return { success: false, error: lastErrorMessage || "Invalid 6-digit verification code. Please check and try again." };
            }

            if (isValid) {
                const profileData = pending.profileData;
                delete profileData.password;
                delete profileData.confirm;

                // Commit deferred login updates (timestamps, choices, photoURL) atomically upon 2FA verification
                if (pending.pendingUpdates && Object.keys(pending.pendingUpdates).length > 0) {
                    try {
                        await updateDoc(doc(db, "users", pending.user.uid), pending.pendingUpdates);
                        Object.assign(profileData, pending.pendingUpdates);
                    } catch (e) {
                        console.warn("⚠️ Notice applying verified 2FA login updates:", e);
                    }
                }

                sessionStorage.setItem('__cc_2fa_passed_' + pending.user.uid, 'true');
                const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
                sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

                if (typeof window.loadLockoutFromCloud === 'function') {
                    await window.loadLockoutFromCloud();
                }

                if (window.cosmoAnalytics) {
                    window.cosmoAnalytics.logAuthAction('login_success', pending.authProvider || '2fa');
                }

                if (typeof window.addSuiteNotification === 'function') {
                    window.addSuiteNotification('account', 'New Login Detected', `Your account was accessed via ${pending.authProvider === 'google.com' ? 'Google' : 'Email'} (2FA Verified).`, { device: navigator.userAgent.substring(0, 30), provider: pending.authProvider || '2FA', status: 'Success' });
                }

                try {
                    const loc = await detectLocation();
                    const notifyLogin = httpsCallable(functions, 'onLoginDetected');
                    notifyLogin({
                        email: profileData.email,
                        device: getCleanDeviceAndBrowser(navigator.userAgent),
                        location: loc,
                        name: profileData.first || 'User'
                    });
                } catch (e) { }

                // 🔄 Force fresh device ID on every 2FA completion so revoked IDs never persist
                getOrCreateDeviceId(true);
                // 📱 Proactively Register and Synchronize Current Device Session
                if (typeof window.registerCurrentDeviceSession === 'function') {
                    try { await window.registerCurrentDeviceSession(pending.user.uid); } catch (devErr) {
                        console.warn("⚠️ Device session registration notice:", devErr);
                    }
                }
                window._cosmoLoginInProgress = false;

                window.__pending2FAUser = null;
                return { success: true, user: profileData };
            } else {
                return { success: false, error: "Invalid 6-digit verification passcode." };
            }
        } catch (err) {
            console.error("2FA Challenge Error:", err);
            return { success: false, error: err.message || "Failed to verify 2FA passcode." };
        }
    },
    registerPasskeyForUser: async (deviceName = '') => {
        try {
            const user = auth.currentUser;
            const uid = user ? user.uid : null;
            if (!uid) {
                return { success: false, error: "Please log in first to enroll a Passkey." };
            }

            const isSupported = await isPasskeySupported();
            if (!isSupported) {
                return { success: false, error: "Passkeys / WebAuthn are not supported on this browser or context." };
            }

            let challenge = null;
            try {
                const getChallengeCall = httpsCallable(functions, 'generatePasskeyServerChallenge');
                const cRes = await getChallengeCall({ uid: uid, email: user.email, purpose: 'register_passkey' });
                if (cRes && cRes.data && cRes.data.challenge) {
                    challenge = cRes.data.challenge;
                }
            } catch (cErr) { }

            const cred = await registerPasskey({
                userEmail: user.email || 'user@cosmocrypt.com',
                userId: uid,
                customChallenge: challenge,
                deviceName: deviceName || 'Security Key / Biometrics'
            });

            // 1. Store in local encrypted storage (offline-first & resilient)
            try {
                let localPasskeys = [];
                const stored = localStorage.getItem('__cc_passkeys_' + uid);
                if (stored) localPasskeys = JSON.parse(stored);
                localPasskeys.push(cred);
                localStorage.setItem('__cc_passkeys_' + uid, JSON.stringify(localPasskeys));
            } catch (e) { }

            // 2. Store in Firestore security sub-collection (clean production schema)
            try {
                await setDoc(doc(db, "users", uid, "security", "passkeys"), {
                    [cred.id]: {
                        id: cred.id,
                        publicKey: cred.publicKey,
                        counter: cred.counter || 0,
                        deviceName: cred.deviceName,
                        deviceType: cred.deviceType || "singleDevice",
                        backedUp: cred.backedUp || false,
                        transports: cred.transports || ["internal"],
                        createdAt: cred.createdAt,
                        lastUsedAt: cred.lastUsedAt || cred.createdAt
                    },
                    updatedAt: new Date().toISOString()
                }, { merge: true });

                // Also ensure the user profile reflects 2FA enabled
                await updateDoc(doc(db, "users", uid), {
                    two_factor_enabled: true,
                    two_factor_type: 'passkey',
                    two_factor_updated: Date.now()
                });
            } catch (fsErr) {
                console.warn("Firestore cloud passkey sync notice:", fsErr);
            }

            // 3. Update local session 2FA state
            _updateLocal2FASession('passkey');
            return { success: true, credential: cred, message: "Passkey registered and 2-Step Verification enabled!" };
        } catch (err) {
            console.error("Passkey registration failed:", err);
            return { success: false, error: err.message || "Failed to register Passkey." };
        }
    },
    verifyPasskeyChallenge: async () => {
        try {
            const pending = window.__pending2FAUser;
            if (!pending || !pending.user) {
                return { success: false, error: "No pending 2FA login session found. Please log in again." };
            }

            const uid = pending.user.uid;
            let allowedIds = [];

            // 1. Check local passkeys
            try {
                const stored = localStorage.getItem('__cc_passkeys_' + uid);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    parsed.forEach(pk => { if (pk && pk.id) allowedIds.push(pk.id); });
                }
            } catch (e) { }

            // 2. Check Firestore passkeys
            try {
                const snap = await getDoc(doc(db, "users", uid, "security", "passkeys"));
                if (snap.exists()) {
                    const data = snap.data();
                    Object.keys(data).forEach(k => {
                        if (k !== 'updatedAt' && data[k] && data[k].id) {
                            if (!allowedIds.includes(data[k].id)) allowedIds.push(data[k].id);
                        }
                    });
                }
            } catch (e) { }

            // 3. Obtain cryptographic challenge from server
            let challenge = null;
            try {
                const getChallengeCall = httpsCallable(functions, 'generatePasskeyServerChallenge');
                const cRes = await getChallengeCall({ uid: uid, email: pending.user.email, purpose: 'login_2fa' });
                if (cRes && cRes.data && cRes.data.challenge) {
                    challenge = cRes.data.challenge;
                }
            } catch (cErr) { }

            const assertion = await authenticatePasskey({
                customChallenge: challenge,
                allowedCredentialIds: allowedIds
            });

            if (!assertion || !assertion.id) {
                return { success: false, error: "Passkey verification failed or was cancelled." };
            }

            // 4. Perform Server-Side Cryptographic Signature Attestation & Verification
            try {
                const verifyCall = httpsCallable(functions, 'verifyPasskeyTwoFactor');
                const verifyRes = await verifyCall({
                    uid: uid,
                    email: pending.user.email,
                    credentialId: assertion.id,
                    clientDataJSON: assertion.response ? assertion.response.clientDataJSON : null,
                    authenticatorData: assertion.response ? assertion.response.authenticatorData : null,
                    signature: assertion.response ? assertion.response.signature : null,
                    challenge: challenge
                });

                if (verifyRes && verifyRes.data && verifyRes.data.success === false) {
                    return { success: false, error: verifyRes.data.error || "Server-side cryptographic signature verification failed." };
                }
            } catch (cloudErr) {
                // If offline or network issue, verify local assertion response format
                if (!assertion.response || !assertion.response.signature) {
                    return { success: false, error: "Invalid cryptographic assertion." };
                }
            }

            // Update lastUsedAt timestamp on successful assertion
            try {
                await setDoc(doc(db, "users", uid, "security", "passkeys"), {
                    [assertion.id]: {
                        lastUsedAt: new Date().toISOString()
                    },
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            } catch (e) { }

            // Complete login session
            const profileData = pending.profileData;
            delete profileData.password;
            delete profileData.confirm;

            // Commit deferred login updates (timestamps, choices, photoURL) atomically upon passkey 2FA verification
            if (pending.pendingUpdates && Object.keys(pending.pendingUpdates).length > 0) {
                try {
                    await updateDoc(doc(db, "users", uid), pending.pendingUpdates);
                    Object.assign(profileData, pending.pendingUpdates);
                } catch (e) {
                    console.warn("⚠️ Notice applying verified passkey 2FA login updates:", e);
                }
            }

            sessionStorage.setItem('__cc_2fa_passed_' + uid, 'true');
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
            sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

            if (typeof window.loadLockoutFromCloud === 'function') {
                await window.loadLockoutFromCloud();
            }

            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('login_success', 'passkey');
            }

            if (typeof window.addSuiteNotification === 'function') {
                window.addSuiteNotification('account', 'New Passkey Login Detected', `Your account was accessed via Hardware Passkey / Biometrics.`, { device: navigator.userAgent.substring(0, 30), provider: 'Passkey / FIDO2', status: 'Success' });
            }

            window.__pending2FAUser = null;
            return { success: true, user: profileData };
        } catch (err) {
            console.error("Passkey Challenge Error:", err);
            return { success: false, error: err.message || "Passkey verification failed." };
        }
    },
    listUserPasskeys: async () => {
        try {
            const user = auth.currentUser;
            if (!user) return [];
            const uid = user.uid;
            const list = [];

            try {
                const snap = await getDoc(doc(db, "users", uid, "security", "passkeys"));
                if (snap.exists()) {
                    const data = snap.data();
                    Object.keys(data).forEach(k => {
                        if (k !== 'updatedAt' && data[k]) list.push(data[k]);
                    });
                }
            } catch (e) { }

            if (list.length === 0) {
                try {
                    const stored = localStorage.getItem('__cc_passkeys_' + uid);
                    if (stored) return JSON.parse(stored);
                } catch (e) { }
            }

            return list;
        } catch (e) {
            console.warn("Could not list passkeys:", e);
            return [];
        }
    },
    sendEmailOtpForLogin: async (email) => {
        try {
            const targetEmail = (email || (window.__pending2FAUser && window.__pending2FAUser.user && window.__pending2FAUser.user.email) || '').trim().toLowerCase();
            if (!targetEmail || !targetEmail.includes('@')) return { success: false, error: "Valid email address not found." };
            try {
                const sendCall = httpsCallable(functions, 'sendEmailOtp');
                const res = await sendCall({ email: targetEmail, purpose: 'login_2fa' });
                if (res && res.data) return res.data;
            } catch (cloudErr) {
                console.info("Cloud email OTP call error:", cloudErr);
                if (cloudErr && cloudErr.message) {
                    return { success: false, error: cloudErr.message };
                }
            }
            return { success: false, error: "Failed to dispatch email verification code. Please try again." };
        } catch (err) {
            console.error("Send Email OTP Error:", err);
            return { success: false, error: err.message || "Failed to send OTP email." };
        }
    },
    checkRegistrationAvailability: async (email, dialCode, phoneNumber) => {
        try {
            // 1. Check Email Availability
            if (email) {
                const cleanEmail = String(email).toLowerCase().trim();

                // A. Check Firebase Auth methods
                try {
                    const methods = await fetchSignInMethodsForEmail(auth, cleanEmail);
                    if (methods && methods.length > 0) {
                        return {
                            success: false,
                            field: 'email',
                            error: `An account with ${cleanEmail} already exists. Please Sign In instead.`
                        };
                    }
                } catch (authErr) {
                    console.warn("fetchSignInMethodsForEmail notice:", authErr);
                }

                // B. Check Firestore users collection
                try {
                    const qEmail = query(collection(db, "users"), where("email", "==", cleanEmail), limit(1));
                    const emailSnap = await getDocs(qEmail);
                    if (!emailSnap.empty) {
                        return {
                            success: false,
                            field: 'email',
                            error: `An account with ${cleanEmail} already exists. Please Sign In instead.`
                        };
                    }
                } catch (dbErr) {
                    console.warn("Email Firestore check notice:", dbErr);
                }
            }

            // 2. Check Phone Availability scoped by Country Code
            if (phoneNumber && dialCode) {
                const rawDial = String(dialCode).trim();
                const cleanDial = rawDial.startsWith('+') ? rawDial : ('+' + rawDial);
                const rawPhone = String(phoneNumber).replace(/[^0-9]/g, '');
                const fullPhoneNoSpace = (cleanDial + rawPhone).replace(/\s+/g, '');
                const fullPhoneWithSpace = `${cleanDial} ${rawPhone}`;

                // A. Check by full compact E.164 string (e.g. +919616403177)
                try {
                    const q1 = query(collection(db, "users"), where("phone", "==", fullPhoneNoSpace), limit(1));
                    const s1 = await getDocs(q1);
                    if (!s1.empty) {
                        return {
                            success: false,
                            field: 'phone',
                            error: `Mobile number (${cleanDial} ${rawPhone}) is already registered. Please Sign In or use a different number.`
                        };
                    }
                } catch (e) { }

                try {
                    const q2 = query(collection(db, "users"), where("phoneNumber", "==", fullPhoneNoSpace), limit(1));
                    const s2 = await getDocs(q2);
                    if (!s2.empty) {
                        return {
                            success: false,
                            field: 'phone',
                            error: `Mobile number (${cleanDial} ${rawPhone}) is already registered. Please Sign In or use a different number.`
                        };
                    }
                } catch (e) { }

                // B. Check by full spaced format (e.g. +91 9616403177)
                try {
                    const q3 = query(collection(db, "users"), where("phone", "==", fullPhoneWithSpace), limit(1));
                    const s3 = await getDocs(q3);
                    if (!s3.empty) {
                        return {
                            success: false,
                            field: 'phone',
                            error: `Mobile number (${cleanDial} ${rawPhone}) is already registered. Please Sign In or use a different number.`
                        };
                    }
                } catch (e) { }

                // C. Check by local phone number AND matching country code
                try {
                    const q4 = query(collection(db, "users"), where("phone", "==", rawPhone), limit(1));
                    const s4 = await getDocs(q4);
                    if (!s4.empty) {
                        const existingUser = s4.docs[0].data();
                        let existingCode = existingUser.code || existingUser.countryCode;
                        if (!existingCode && existingUser.phoneNumber && String(existingUser.phoneNumber).startsWith('+')) {
                            const m = String(existingUser.phoneNumber).match(/^(\+\d{1,4})/);
                            if (m) existingCode = m[1];
                        }
                        if (!existingCode) existingCode = '+91';

                        const cleanExistingCode = String(existingCode).replace(/[^0-9]/g, '');
                        const cleanInputCode = cleanDial.replace(/[^0-9]/g, '');

                        // Only block if same country code!
                        if (cleanExistingCode === cleanInputCode) {
                            return {
                                success: false,
                                field: 'phone',
                                error: `Mobile number (${cleanDial} ${rawPhone}) is already registered. Please Sign In or use a different number.`
                            };
                        }
                    }
                } catch (e) { }

                // D. Check local encrypted cache fallback
                try {
                    const localRaw = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
                    if (localRaw) {
                        const u = JSON.parse(CryptoJS.AES.decrypt(localRaw, _getAppSecret()).toString(CryptoJS.enc.Utf8));
                        if (u) {
                            const uLocal = String(u.phone || '').replace(/[^0-9]/g, '');
                            let uCode = u.code || '+91';
                            const cleanUCode = String(uCode).replace(/[^0-9]/g, '');
                            const cleanInputCode = cleanDial.replace(/[^0-9]/g, '');
                            if (uLocal === rawPhone && cleanUCode === cleanInputCode) {
                                return {
                                    success: false,
                                    field: 'phone',
                                    error: `Mobile number (${cleanDial} ${rawPhone}) is already registered. Please Sign In or use a different number.`
                                };
                            }
                        }
                    }
                } catch (e) { }
            }

            return { success: true };
        } catch (err) {
            console.error("checkRegistrationAvailability error:", err);
            return { success: true };
        }
    },
    sendRegistrationPhoneOtp: async (fullPhoneNumber, containerId = 'regRecaptchaContainer', dialCode = null, localNumber = null) => {
        const cleanPhone = (fullPhoneNumber || '').replace(/\s+/g, '');
        if (!cleanPhone || cleanPhone.length < 7) {
            return { success: false, error: "Valid mobile phone number with country code is required." };
        }

        const lockKey = `_cosmo_phone_lock_${cleanPhone}`;
        try {
            localStorage.removeItem(lockKey);
            const rawLock = sessionStorage.getItem(lockKey) || (window._phoneLockMap && window._phoneLockMap[lockKey]);
            if (rawLock) {
                const lockData = typeof rawLock === 'string' ? JSON.parse(rawLock) : rawLock;
                if (lockData && lockData.lockoutUntil > Date.now()) {
                    const remainingHours = Math.ceil((lockData.lockoutUntil - Date.now()) / (1000 * 60 * 60));
                    return {
                        success: false,
                        error: `Security Lock: Maximum OTP attempts exceeded for this number. Blocked for ${remainingHours} hours.`,
                        isLocked: true,
                        lockoutUntil: lockData.lockoutUntil
                    };
                }
            }
        } catch (e) { }

        try {
            let container = document.getElementById(containerId);
            if (container) {
                try { container.remove(); } catch (e) { }
            }
            container = document.createElement('div');
            container.id = containerId;
            document.body.appendChild(container);

            if (window.__regPhoneRecaptcha) {
                try { window.__regPhoneRecaptcha.clear(); } catch (e) { }
                window.__regPhoneRecaptcha = null;
            }

            window.__regPhoneRecaptcha = new RecaptchaVerifier(auth, containerId, {
                size: 'invisible',
                callback: () => { }
            });

            const confirmationResult = await signInWithPhoneNumber(auth, cleanPhone, window.__regPhoneRecaptcha);
            window.__phoneConfirmationResult = confirmationResult;
            window.__pendingPhoneVerification = cleanPhone;

            return { success: true, message: `6-Digit SMS verification code dispatched to ${cleanPhone}.` };
        } catch (err) {
            console.error("Phone OTP dispatch error:", err);
            if (window.__regPhoneRecaptcha) {
                try { window.__regPhoneRecaptcha.clear(); } catch (e) { }
                window.__regPhoneRecaptcha = null;
            }
            let errMsg = err.message || "Failed to dispatch SMS verification code.";
            if (err.code === 'auth/invalid-app-credential') {
                errMsg = "Firebase App Credential Error: reCAPTCHA verification failed or domain is not authorized. In Firebase Console, go to Authentication -> Settings -> Authorized Domains and ensure 'localhost' is listed, or test with a Test Phone Number (e.g. +91 9876543210 with code 123456).";
            } else if (err.code === 'auth/operation-not-allowed' || (errMsg && errMsg.includes('region enabled'))) {
                errMsg = "SMS region not enabled yet. Please allow your country (+91 India, +1 US, etc.) in Firebase Console -> Authentication -> Settings -> SMS Region Policy, or add your number as a Test Phone Number.";
            } else if (err.code === 'auth/invalid-phone-number') {
                errMsg = "Invalid phone number format. Please enter a valid mobile number.";
            } else if (err.code === 'auth/quota-exceeded') {
                errMsg = "Daily SMS quota exceeded. Please try again later.";
            }
            return { success: false, error: errMsg };
        }
    },
    verifyRegistrationPhoneOtp: async (code, fullPhoneNumber) => {
        const cleanCode = (code || '').trim();
        const cleanPhone = (fullPhoneNumber || window.__pendingPhoneVerification || '').replace(/\s+/g, '');

        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter the 6-digit SMS verification code." };
        }

        const lockKey = `_cosmo_phone_lock_${cleanPhone}`;
        let attemptData = { attempts: 0, lockoutUntil: 0 };
        try {
            localStorage.removeItem(lockKey);
            const raw = sessionStorage.getItem(lockKey) || (window._phoneLockMap && window._phoneLockMap[lockKey]);
            if (raw) attemptData = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (e) { }

        if (attemptData.lockoutUntil > Date.now()) {
            const remainingHours = Math.ceil((attemptData.lockoutUntil - Date.now()) / (1000 * 60 * 60));
            return { success: false, error: `Security Lock: This number is blocked for ${remainingHours} hours.`, isLocked: true };
        }

        if (!window.__phoneConfirmationResult) {
            return { success: false, error: "SMS session expired. Please request a new SMS code." };
        }

        try {
            const result = await window.__phoneConfirmationResult.confirm(cleanCode);
            try {
                sessionStorage.removeItem(lockKey);
                localStorage.removeItem(lockKey);
                if (window._phoneLockMap) delete window._phoneLockMap[lockKey];
            } catch (e) { }
            // 🧹 Clean up temporary phone user from Firebase Auth so only the Email account is created
            if (result && result.user) {
                try {
                    await deleteUser(result.user);
                } catch (delErr) {
                    console.warn("Temporary phone user cleanup:", delErr);
                }
            }
            try { await signOut(auth); } catch (e) { }
            window.__pendingPhoneVerification = null;
            return { success: true, phone: cleanPhone };
        } catch (err) {
            attemptData.attempts = (attemptData.attempts || 0) + 1;
            if (!window._phoneLockMap) window._phoneLockMap = {};
            if (attemptData.attempts >= 3) {
                attemptData.lockoutUntil = Date.now() + (5 * 60 * 60 * 1000); // 5 hours
                try {
                    sessionStorage.setItem(lockKey, JSON.stringify(attemptData));
                    localStorage.removeItem(lockKey);
                    window._phoneLockMap[lockKey] = attemptData;
                } catch (e) { }
                return {
                    success: false,
                    error: "Action Blocked: Maximum OTP attempts exceeded for this number! Blocked for 5 hours.",
                    isLocked: true,
                    lockoutUntil: attemptData.lockoutUntil,
                    attempts: attemptData.attempts
                };
            } else {
                try {
                    sessionStorage.setItem(lockKey, JSON.stringify(attemptData));
                    localStorage.removeItem(lockKey);
                    window._phoneLockMap[lockKey] = attemptData;
                } catch (e) { }
                const left = 3 - attemptData.attempts;
                return {
                    success: false,
                    error: `Incorrect SMS code. ${left} attempt${left > 1 ? 's' : ''} remaining before 5-hour lockout.`,
                    attempts: attemptData.attempts
                };
            }
        }
    },
    sendRegistrationEmailOtp: async (email, userName = null) => {
        const cleanEmail = (email || '').trim().toLowerCase();
        if (!cleanEmail || !cleanEmail.includes('@')) {
            return { success: false, error: "Valid email address required." };
        }

        try {
            const sendCall = httpsCallable(functions, 'sendEmailOtp');
            const res = await sendCall({ email: cleanEmail, purpose: 'register', userName: userName || 'User' });
            if (res && res.data) {
                if (res.data.success === false) {
                    return res.data;
                }
                return res.data;
            }
            return { success: true, message: `Verification code sent to ${cleanEmail}.` };
        } catch (cloudErr) {
            console.error("Registration email OTP dispatch error:", cloudErr);
            return { success: false, error: cloudErr.message || "Failed to deliver verification email. Please try again." };
        }
    },
    verifyAndCompleteRegistration: async (code, details) => {
        const cleanCode = (code || '').trim();
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter the 6-digit verification code sent to your email." };
        }

        const normalizedEmail = (details && details.email ? details.email : '').toLowerCase().trim();
        if (!normalizedEmail) {
            return { success: false, error: "Registration details missing email." };
        }

        // 🔒 Server-Side Strict Verification via Cloud Function
        try {
            const verifyCall = httpsCallable(functions, 'verifyRegistrationOtp');
            const res = await verifyCall({ email: normalizedEmail, code: cleanCode });

            if (!res || !res.data || !res.data.success || !res.data.verified) {
                const errMsg = (res && res.data && res.data.error) || "Incorrect 6-digit verification code. Please check your email and try again.";
                return { success: false, error: errMsg };
            }
        } catch (err) {
            console.error("Registration OTP verification error:", err);
            const errMsg = err.message || "Verification failed. Please ensure the code is correct or request a new one.";
            return { success: false, error: errMsg };
        }

        // ✅ Only when strictly verified by the server, complete registration in Firebase Auth
        return await suiteAuthBridge.emailRegister(details);
    },
    cancelPending2FA: async () => {
        const uid = window.__pending2FAUser?.user?.uid || auth.currentUser?.uid;
        if (uid) sessionStorage.removeItem('__cc_2fa_passed_' + uid);
        localStorage.removeItem('userStoreEnc');
        window.__pending2FAUser = null;
        try {
            await signOut(auth);
        } catch (e) { }
        return { success: true };
    },
    sendDeletionEmailOtp: async (email = null, userName = null) => {
        const user = auth.currentUser;
        const targetEmail = (email || (user && user.email) || (user && user.providerData && user.providerData[0] ? user.providerData[0].email : null) || '').trim().toLowerCase();
        if (!targetEmail || !targetEmail.includes('@')) {
            return { success: false, error: "Valid email address required for deletion verification." };
        }

        try {
            const sendCall = httpsCallable(functions, 'sendEmailOtp');
            const res = await sendCall({ email: targetEmail, purpose: 'delete_account', userName: userName || (user && user.displayName) || '' });
            if (res && res.data) return res.data;
            return { success: false, error: "Failed to dispatch deletion confirmation code. Please try again." };
        } catch (cloudErr) {
            console.info("Deletion email OTP dispatch error:", cloudErr);
            return { success: false, error: cloudErr.message || "Failed to dispatch deletion confirmation code." };
        }
    },
    verifyDeletionOtp: async (code, email = null) => {
        const cleanCode = (code || '').trim();
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter the 6-digit confirmation code sent to your email." };
        }

        const user = auth.currentUser;
        const targetEmail = (email || (user && user.email) || (user && user.providerData && user.providerData[0] ? user.providerData[0].email : null) || '').trim().toLowerCase();

        try {
            const verifyCall = httpsCallable(functions, 'verifyDeletionOtp');
            const result = await verifyCall({ email: targetEmail, code: cleanCode });
            if (result && result.data) return result.data;
            return { success: false, error: "Failed to verify confirmation code. Please try again." };
        } catch (cloudErr) {
            console.error("Cloud function verifyDeletionOtp error:", cloudErr);
            return { success: false, error: cloudErr.message || "Failed to verify confirmation code." };
        }
    },
    confirmAndDeleteAccount: async (code, email = null, password = null) => {
        const cleanCode = (code || '').trim();
        if (!cleanCode || cleanCode.length !== 6) {
            return { success: false, error: "Please enter the 6-digit confirmation code sent to your email." };
        }

        const user = auth.currentUser;
        const targetEmail = (email || (user && user.email) || (user && user.providerData && user.providerData[0] ? user.providerData[0].email : null) || '').trim().toLowerCase();

        if (window.cosmoAnalytics) {
            window.cosmoAnalytics.logAuthAction('delete_account_confirm', 'n/a');
        }

        try {
            // 1. Call Cloud Callable
            const deleteCall = httpsCallable(functions, 'confirmAndDeleteAccount');
            const result = await deleteCall({ email: targetEmail, code: cleanCode, password: password });

            if (result && result.data && result.data.success) {
                // Wipe local sessions & client auth
                try { if (user) await deleteUser(user); } catch (e) { }
                try { await signOut(auth); } catch (e) { }
                localStorage.clear();
                sessionStorage.clear();
                return { success: true, message: "Account deleted successfully." };
            } else if (result && result.data && result.data.error) {
                return { success: false, error: result.data.error };
            }
        } catch (cloudErr) {
            console.warn("Cloud delete function fallback:", cloudErr);
            // Local fallback verification against Firestore email_otps
            try {
                const otpSnap = await getDoc(doc(db, "email_otps", targetEmail));
                if (!otpSnap.exists()) {
                    return { success: false, error: "No confirmation code requested for this email. Please request a new code." };
                }
                const otpData = otpSnap.data();
                if (Date.now() > otpData.expiresAt) {
                    return { success: false, error: "Confirmation code has expired (10 min limit). Please request a new code." };
                }
                if (otpData.hashedCode !== _hashEmailOtp(cleanCode)) {
                    return { success: false, error: "Invalid 6-digit confirmation code. Please check your email and try again." };
                }

                // If valid in fallback:
                if (user) {
                    await deleteDoc(doc(db, "users", user.uid));
                    await deleteDoc(doc(db, "email_otps", targetEmail));
                    try { await deleteUser(user); } catch (e) { }
                }
                localStorage.clear();
                sessionStorage.clear();
                return { success: true, message: "Account wiped successfully." };
            } catch (fallbackErr) {
                return { success: false, error: cloudErr.message || fallbackErr.message || "Failed to confirm account deletion." };
            }
        }
        return { success: false, error: "Unable to complete deletion." };
    },
    deleteAccount: async (password = null) => {
        if (window.cosmoAnalytics) {
            window.cosmoAnalytics.logAuthAction('delete_account_start', 'n/a');
        }
        try {
            const user = auth.currentUser;
            if (!user) {
                localStorage.clear();
                return { success: true };
            }

            const userEmail = (user.email || (user.providerData && user.providerData[0] ? user.providerData[0].email : null) || '').toLowerCase().trim();

            try {
                if (password) {
                    const credential = EmailAuthProvider.credential(userEmail, password);
                    await reauthenticateWithCredential(user, credential);
                } else {
                    const provider = createSecureGoogleProvider();
                    await reauthenticateWithPopup(user, provider);
                }
            } catch (reAuthErr) {
                console.error("Final Re-auth Failure:", reAuthErr);
                if (reAuthErr.code === 'auth/invalid-credential' && password) {
                    await signInWithEmailAndPassword(auth, userEmail, password);
                } else {
                    return { success: false, error: "Deletion verification failed. Please try logging in again first." };
                }
            }

            try {
                const storageRef = ref(storage, `users/${user.uid}/avatar/profile.webp`);
                await deleteObject(storageRef).catch(() => { });
            } catch (delErr) { }

            await deleteDoc(doc(db, "users", user.uid));
            await deleteUser(user);

            localStorage.clear();

            if (typeof switchView === 'function') switchView('about-w11', 0);
            setTimeout(() => window.location.reload(), 500);

            return { success: true };
        } catch (error) {
            console.error("CRITICAL DELETION ERROR:", error);
            return { success: false, error: error.message };
        }
    },
    logout: async () => {
        try {
            // 🛑 Detach snapshot listeners immediately to eliminate reactive write bursts
            if (window.userSnapshotListener) {
                try { window.userSnapshotListener(); } catch (e) { }
                window.userSnapshotListener = null;
            }
            if (window.parent && window.parent.userSnapshotListener) {
                try { window.parent.userSnapshotListener(); } catch (e) { }
                window.parent.userSnapshotListener = null;
            }

            const user = auth.currentUser;
            const currentUid = user ? user.uid : (window.userData ? window.userData.uid : localStorage.getItem('last_active_uid'));
            const currentDevId = localStorage.getItem('cosmo_device_id') ||
                (window.cosmoDeviceManager && window.cosmoDeviceManager.getDeviceId ? window.cosmoDeviceManager.getDeviceId() : null);
            if (currentUid && currentDevId && typeof db !== 'undefined' && typeof doc === 'function') {
                try {
                    const userDocRef = doc(db, "users", currentUid);
                    const userSnap = await getDoc(userDocRef);
                    if (userSnap.exists()) {
                        const data = userSnap.data();
                        if (Array.isArray(data.activeDevices)) {
                            const cleaned = data.activeDevices.filter(d => (d.id !== currentDevId && d.deviceId !== currentDevId));
                            await updateDoc(userDocRef, { activeDevices: cleaned });

                            if (window.userData) window.userData.activeDevices = cleaned;
                            if (window.parent && window.parent.userData) window.parent.userData.activeDevices = cleaned;

                            // 🚀 Broadcast real-time session removal to all frames & listeners
                            const devSyncDetail = { activeDevices: cleaned };
                            try { window.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                            try { window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                            if (window.parent && window.parent !== window) {
                                try { window.parent.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                                try { window.parent.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail })); } catch (e) { }
                            }
                            document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                                try {
                                    if (ifr.contentWindow) {
                                        if (ifr.contentWindow.userData) ifr.contentWindow.userData.activeDevices = cleaned;
                                        ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: devSyncDetail }));
                                        ifr.contentWindow.dispatchEvent(new CustomEvent('userDataUpdated', { detail: devSyncDetail }));
                                        ifr.contentWindow.postMessage({ type: 'session-update', activeDevices: cleaned }, '*');
                                        if (typeof ifr.contentWindow.initLiveSessions === 'function') {
                                            ifr.contentWindow.initLiveSessions();
                                        }
                                    }
                                } catch (e) { }
                            });
                        }
                    }
                } catch (devErr) {
                    console.warn("Active device removal on logout notice:", devErr);
                }
            }

            localStorage.setItem('explicit_logout', 'true');
            localStorage.removeItem('userStoreEnc');
            // 🔄 Clear device ID on logout so next login starts fresh
            try { localStorage.removeItem('cosmo_device_id'); } catch (e) { }
            window.userData = null;
            if (window.parent) window.parent.userData = null;

            await signOut(auth);
            if (window.cosmoAnalytics) {
                window.cosmoAnalytics.logAuthAction('logout', 'n/a');
            }
            if (typeof window.updateSettingsSecurePdfCardState === 'function') {
                window.updateSettingsSecurePdfCardState();
            }
            return { success: true };
        } catch (error) {
            console.error("Firebase Logout Error:", error);
            return { success: false, error: error.message };
        }
    }
};

// --- User Choice & Preference Engine ---
window.recordUserChoice = async (type, choice) => {
    let choices = JSON.parse(localStorage.getItem('user_choices') || '{}');
    const nowIso = new Date().toISOString();
    const choiceObj = (typeof choice === 'object' && choice !== null) ? {
        ...choice,
        timestamp: nowIso
    } : {
        choice: choice,
        timestamp: nowIso
    };
    if ('device' in choiceObj) {
        delete choiceObj.device;
    }
    choices[type] = choiceObj;
    localStorage.setItem('user_choices', JSON.stringify(choices));

    const currentUser = (typeof auth !== 'undefined' && auth.currentUser) || (window.suiteAuth && window.suiteAuth.currentUser);
    const dbRef = (typeof db !== 'undefined' && db) || window.suiteDb;
    const docFn = (typeof doc !== 'undefined' && doc) || window.suiteDoc;
    const setDocFn = (typeof setDoc !== 'undefined' && setDoc) || window.suiteSetDoc;

    if (currentUser && dbRef && docFn && setDocFn) {
        try {
            await setDocFn(docFn(dbRef, "users", currentUser.uid), { choices: { [type]: choiceObj } }, { merge: true });
        } catch (e) {
            console.warn("☁️ Cloud: Choice sync deferred.", e);
        }
    }
};

// Global Sync Flags
window.isCloudSyncing = true;
window.hasVerifiedThisSession = false;

// GLOBAL SLOT SYNC TO FIREBASE
window.syncUsedSlotsToFirebase = () => {
    // Reserved for future slot usage sync if needed
};

// REUSABLE CLOUD SYNC LOGIC
window.syncProfileWithCloud = async () => {
    const user = auth.currentUser;
    if (!user) {
        window.isCloudSyncing = false;
        if (typeof updateMomentumDash === 'function') updateMomentumDash();
        return;
    }

    if (window._isSyncProfileRunning) return;
    if (window._lastCloudSyncTime && (Date.now() - window._lastCloudSyncTime < 4000)) {
        return;
    }
    window._isSyncProfileRunning = true;
    window._lastCloudSyncTime = Date.now();

    if (!window.userData || !window.userData.isVerified) {
        window.isCloudSyncing = true;
        if (typeof updateMomentumDash === 'function') updateMomentumDash();
    }

    try {
        // Background Sync: No UI disruption/notification popup
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
            const profileData = userSnap.data();
            delete profileData.password;
            delete profileData.confirm;

            // Consolidate simultaneous profile updates (role + photoURL) into a single atomic write
            const consolidatedProfileUpdates = {};
            const correctRole = ownerEmails.includes(user.email.toLowerCase()) ? 'owner' : 'user';
            if (profileData.role !== correctRole) {
                profileData.role = correctRole;
                consolidatedProfileUpdates.role = correctRole;
            }

            profileData.isVerified = true;
            if (!profileData.template_vaults) profileData.template_vaults = {};

            // 🌟 Fetch template vaults subcollection in background (non-blocking)
            (async () => {
                try {
                    const vaultsSnap = await getDocs(collection(db, "users", user.uid, "template_vaults"));
                    const decryptPromises = [];
                    vaultsSnap.forEach(vDoc => {
                        if (vDoc.data() && vDoc.data().data !== undefined) {
                            decryptPromises.push((async () => {
                                const rawVal = vDoc.data().data;
                                const decVal = await decryptVaultPayload(rawVal, user);
                                profileData.template_vaults[vDoc.id] = decVal;
                                if (typeof rawVal !== 'string' || (!rawVal.startsWith('cc_gcm256:') && !rawVal.startsWith('U2FsdGVkX1'))) {
                                    encryptVaultPayload(decVal, user).then(migratedEnc => {
                                        if (migratedEnc && typeof migratedEnc === 'string') {
                                            setDoc(doc(db, "users", user.uid, "template_vaults", vDoc.id), {
                                                data: migratedEnc,
                                                encrypted: true,
                                                cipher: 'AES-GCM-256',
                                                updatedAt: Date.now()
                                            }, { merge: true }).catch(() => { });
                                        }
                                    }).catch(() => { });
                                }
                            })());
                        }
                    });
                    await Promise.all(decryptPromises);
                } catch (vErr) {
                    console.warn("☁️ Sub-collection template_vaults load fallback:", vErr);
                }

                if (profileData.template_vaults && typeof profileData.template_vaults === 'object') {
                    const rootDecryptPromises = [];
                    const vaultsToBatchMigrate = {};
                    for (const k of Object.keys(profileData.template_vaults)) {
                        rootDecryptPromises.push((async () => {
                            const rawVal = profileData.template_vaults[k];
                            const decVal = await decryptVaultPayload(rawVal, user);
                            profileData.template_vaults[k] = decVal;
                            if (typeof rawVal !== 'string' || (!rawVal.startsWith('cc_gcm256:') && !rawVal.startsWith('U2FsdGVkX1'))) {
                                try {
                                    const migratedEnc = await encryptVaultPayload(decVal, user);
                                    if (migratedEnc && typeof migratedEnc === 'string') {
                                        vaultsToBatchMigrate[k] = migratedEnc;
                                    }
                                } catch (e) { }
                            }
                        })());
                    }
                    await Promise.all(rootDecryptPromises);
                    if (Object.keys(vaultsToBatchMigrate).length > 0) {
                        try {
                            await setDoc(doc(db, "users", user.uid), {
                                template_vaults: vaultsToBatchMigrate
                            }, { merge: true });
                        } catch (e) { }
                    }
                }

                if (window.CosmoTemplateStore && typeof window.CosmoTemplateStore.notifyUpdate === 'function') {
                    Object.keys(profileData.template_vaults).forEach(k => {
                        window.CosmoTemplateStore.notifyUpdate(k, profileData.template_vaults[k]);
                    });
                }
            })();

            const gPhoto = user.photoURL || (user.providerData && user.providerData[0] && user.providerData[0].photoURL) || null;
            if (gPhoto && !profileData.photoURL) {
                profileData.photoURL = gPhoto;
                consolidatedProfileUpdates.photoURL = gPhoto;
            }

            if (Object.keys(consolidatedProfileUpdates).length > 0) {
                updateDoc(doc(db, "users", user.uid), consolidatedProfileUpdates).catch(err => {
                    console.warn("☁️ Consolidated profile update notice:", err);
                });
            }
            window.userData = profileData;
            if (window.parent && window.parent !== window) {
                window.parent.userData = profileData;
            }

            // Register/Heartbeat current device session
            // Skip if a login function is already handling device registration to avoid race conditions
            if (typeof window.registerCurrentDeviceSession === 'function' && !window._cosmoLoginInProgress) {
                window.registerCurrentDeviceSession(user.uid).catch(() => { });
            }

            // Broadcast updates after window.userData is assigned
            if (window.CosmoTemplateStore && typeof window.CosmoTemplateStore.notifyUpdate === 'function') {
                Object.keys(profileData.template_vaults).forEach(k => {
                    window.CosmoTemplateStore.notifyUpdate(k, profileData.template_vaults[k]);
                });
            }

            if (profileData.pdf_telemetry) {
                const rawDl = typeof profileData.pdf_telemetry.downloads_count === 'number' ? profileData.pdf_telemetry.downloads_count : parseInt(profileData.pdf_telemetry.downloads_count || '0', 10);
                const rawEnc = typeof profileData.pdf_telemetry.encrypted_downloads_count === 'number' ? profileData.pdf_telemetry.encrypted_downloads_count : parseInt(profileData.pdf_telemetry.encrypted_downloads_count || '0', 10);
                const curDl = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.downloads_count) || 0;
                const curEnc = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.encrypted_downloads_count) || 0;
                const finalDl = Math.max(curDl, isNaN(rawDl) ? 0 : rawDl);
                const finalEnc = Math.max(curEnc, isNaN(rawEnc) ? 0 : rawEnc);
                const telemState = {
                    downloads_count: finalDl,
                    encrypted_downloads_count: finalEnc,
                    last_updated: profileData.pdf_telemetry.last_updated || new Date().toISOString()
                };
                window._cosmoPdfTelemetryState = telemState;
                if (window.userData) window.userData.pdf_telemetry = telemState;
                if (window.parent && window.parent.userData) window.parent.userData.pdf_telemetry = telemState;
                if (window.parent) window.parent._cosmoPdfTelemetryState = telemState;
                if (window.top) window.top._cosmoPdfTelemetryState = telemState;

                const detail = { count: finalDl, encryptedCount: finalEnc };
                try { window.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail })); } catch (e) { }
                try { if (window.parent && window.parent !== window) window.parent.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail })); } catch (e) { }
                try {
                    document.querySelectorAll('iframe').forEach(iframe => {
                        if (iframe.contentWindow) {
                            iframe.contentWindow.userData = profileData;
                            iframe.contentWindow._cosmoPdfTelemetryState = telemState;
                            iframe.contentWindow.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail }));
                            if (typeof iframe.contentWindow.updateAnalyticsPdfDownloadsCount === 'function') {
                                iframe.contentWindow.updateAnalyticsPdfDownloadsCount(finalDl, finalEnc);
                            }
                        }
                    });
                } catch (e) { }
            }

            // --- TRACK USED SLOTS ---
            if (window.syncUsedSlotsToFirebase) {
                await window.syncUsedSlotsToFirebase();
            }
            // --- END TRACK USED SLOTS ---

            const localChoices = JSON.parse(localStorage.getItem('user_choices') || '{}');
            const cloudChoices = profileData.choices || {};

            // Migrate legacy mobile choices if they exist
            if (cloudChoices.mobile && cloudChoices.mobile.privacy) {
                cloudChoices.privacy = cloudChoices.mobile.privacy.choice || cloudChoices.mobile.privacy;
            }
            if (localChoices.mobile && localChoices.mobile.privacy) {
                localChoices.privacy = localChoices.mobile.privacy.choice || localChoices.mobile.privacy;
                delete localChoices.mobile;
                localStorage.setItem('user_choices', JSON.stringify(localChoices));
            }

            const mergedChoices = { ...cloudChoices, ...localChoices };
            if (mergedChoices.mobile) {
                delete mergedChoices.mobile;
            }

            if (JSON.stringify(cloudChoices) !== JSON.stringify(mergedChoices)) {
                updateDoc(doc(db, "users", user.uid), {
                    choices: mergedChoices,
                    "choices.mobile": deleteField()
                }).catch(() => {
                    setDoc(doc(db, "users", user.uid), { choices: mergedChoices }, { merge: true }).catch(() => { });
                });
                profileData.choices = mergedChoices;
                localStorage.setItem('user_choices', JSON.stringify(mergedChoices));
            }

            const privacyVal = mergedChoices.privacy ? (mergedChoices.privacy.choice || mergedChoices.privacy) : null;
            localStorage.removeItem('privacy_persistent_choice');
            if (privacyVal === 'allow' || privacyVal === 'deny') {
                if (typeof window.dismissPrivacyToast === 'function') {
                    window.dismissPrivacyToast();
                } else {
                    const toast = document.getElementById('privacy-toast');
                    const backdrop = document.getElementById('privacy-toast-backdrop');
                    if (toast) toast.style.display = 'none';
                    if (backdrop) backdrop.style.display = 'none';
                }
            }

            if (!profileData.code && profileData.phone) {
                let inferredCode = '+91';
                if (String(profileData.phone).startsWith('+')) {
                    const m = String(profileData.phone).match(/^(\+\d{1,4})/);
                    if (m) inferredCode = m[1];
                }
                updateDoc(doc(db, "users", user.uid), { code: inferredCode }).catch(err => {
                    console.warn("Could not backfill code field to Firestore:", err);
                });
                profileData.code = inferredCode;
            }

            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(profileData), _getAppSecret()).toString();
            sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

            // Sync custom avatar across all devices
            if (profileData.avatarUrl && profileData.avatarUrl !== 'none') {
                localStorage.setItem('userProfileImg', profileData.avatarUrl);
                if (typeof profileImgData !== 'undefined') profileImgData = profileData.avatarUrl;
            } else if (profileData.avatarUrl === 'none') {
                localStorage.setItem('userProfileImg', 'none');
                if (typeof profileImgData !== 'undefined') profileImgData = 'none';
            } else {
                localStorage.removeItem('userProfileImg');
                if (typeof profileImgData !== 'undefined') profileImgData = null;
            }

            const localAppLockRaw = safeStorage.getItem('app_lock_config');
            const cloudAppLock = profileData.app_lock_config;

            if (cloudAppLock) {
                const cloudConfigStr = JSON.stringify(cloudAppLock);
                if (localAppLockRaw !== cloudConfigStr) {
                    safeStorage.setItem('app_lock_config', cloudConfigStr);
                    if (window._0x83e2 && typeof window._0x83e2.checkAppLock === 'function') {
                        window._0x83e2.checkAppLock();
                    }
                }
            } else if (localAppLockRaw) {
                try {
                    const config = JSON.parse(localAppLockRaw);
                    updateDoc(doc(db, "users", user.uid), { app_lock_config: config }).catch(() => { });
                } catch (e) { }
            } else {
                if (localAppLockRaw) {
                    safeStorage.removeItem('app_lock_config');
                    if (window._0x83e2 && typeof window._0x83e2.checkAppLock === 'function') {
                        window._0x83e2.checkAppLock();
                    }
                }
            }

            // Fetch Mobile Settings from user profile document (migrated from settings.mobile or mobile)
            let ms = null;
            if (profileData.settings) {
                if (profileData.settings.mobile) {
                    ms = profileData.settings.mobile;
                } else {
                    const hasKeys = Object.keys(profileData.settings).some(k => k.startsWith('suite_') || k === 'active_layer_mode' || k === 'font');
                    if (hasKeys) {
                        ms = profileData.settings;
                    }
                }
            }
            if (!ms && profileData.mobile) {
                ms = profileData.mobile;
            }
            if (ms) {

                // 1. Resolve Font Mode / Font Name
                let fontMode = null;
                if (ms.active_layer_mode !== undefined && ms.active_layer_mode !== null) {
                    fontMode = String(ms.active_layer_mode);
                } else if (ms.font !== undefined && ms.font !== null) {
                    const fontVal = String(ms.font).trim();
                    if (FONT_MAP[fontVal]) {
                        fontMode = fontVal;
                    } else {
                        fontMode = REVERSE_FONT_MAP[fontVal] || "1";
                    }
                }

                if (fontMode) {
                    if (localStorage.getItem('active_layer_mode') !== fontMode) {
                        if (window.safeStorage) {
                            window.safeStorage.setItem('active_layer_mode', fontMode, true);
                        } else {
                            localStorage.setItem('active_layer_mode', fontMode);
                            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'active_layer_mode', val: fontMode, isRemote: true } }));
                        }
                    }
                    // Force apply the font mode to the UI
                    const parsedMode = parseInt(fontMode);
                    if (!isNaN(parsedMode)) {
                        window.syncFontMode(parsedMode);
                    }
                }

                // 2. Resolve all other keys
                Object.entries(ms).forEach(([key, val]) => {
                    if (key === 'font' || key === 'active_layer_mode') return; // Handled above
                    if (localStorage.getItem(key) !== String(val)) {
                        if (window.safeStorage) {
                            window.safeStorage.setItem(key, val, true);
                        } else {
                            localStorage.setItem(key, val);
                            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val, isRemote: true } }));
                        }
                    }
                });

                // 3. Proactively migrate and clean up root-level 'mobile' and nested 'settings.mobile' fields if they exist
                if (profileData.mobile || (profileData.settings && profileData.settings.mobile)) {
                    try {
                        const migrationUpdates = {
                            "mobile": deleteField(),
                            "settings.mobile": deleteField()
                        };
                        if (profileData.settings && profileData.settings.card_mutation_config) {
                            migrationUpdates["settings.card_mutation_config"] = profileData.settings.card_mutation_config;
                        }
                        if (profileData.settings && profileData.settings.secure_pdf_config) {
                            migrationUpdates["settings.secure_pdf_config"] = profileData.settings.secure_pdf_config;
                        }
                        Object.entries(ms).forEach(([k, v]) => {
                            if (k !== 'mobile') migrationUpdates[`settings.${k}`] = v;
                        });
                        updateDoc(doc(db, "users", user.uid), migrationUpdates).catch(e => {
                            console.warn("🧹 Cloud: Failed to delete legacy 'mobile' maps", e);
                        });
                    } catch (e) {
                        console.warn("🧹 Cloud: Failed to delete legacy 'mobile' maps", e);
                    }
                }

                // Check if any syncable key is missing in Firestore mobile settings
                const keys = [
                    'suite_sound_muted', 'suite_sound_volume',
                    'suite_typing_enabled', 'suite_typing_volume', 'suite_typing_tune',
                    'suite_bottom_nav_enabled', 'suite_bottom_nav_volume',
                    'nav_anim_enabled', 'active_layer_mode',
                    'session_protocol_enabled', 'fullscreen_on_click'
                ];
                const hasAllKeys = keys.every(k => ms[k] !== undefined && ms[k] !== null);
                if (!hasAllKeys) {
                    console.log("☁️ Cloud: Some mobile settings are missing. Pushing full configuration...");
                    window.suiteAuthBridge.pushSettingsToCloud();
                }
            } else {
                const settings = {};
                const keys = [
                    'suite_sound_muted', 'suite_sound_volume',
                    'suite_typing_enabled', 'suite_typing_volume', 'suite_typing_tune',
                    'suite_bottom_nav_enabled', 'suite_bottom_nav_volume',
                    'nav_anim_enabled',
                    'session_protocol_enabled',
                    'fullscreen_on_click',
                    'cosmo_search_enabled'
                ];
                const defaults = {
                    'suite_sound_muted': 'false',
                    'suite_sound_volume': '0.7',
                    'suite_typing_enabled': 'true',
                    'suite_typing_volume': '0.5',
                    'suite_typing_tune': 'TYPING_MECHANICAL',
                    'suite_bottom_nav_enabled': 'true',
                    'suite_bottom_nav_volume': '0.5',
                    'nav_anim_enabled': 'true',
                    'session_protocol_enabled': 'true',
                    'fullscreen_on_click': 'false',
                    'cosmo_search_enabled': 'false'
                };
                keys.forEach(k => {
                    let v = localStorage.getItem(k);
                    if (v === null && defaults[k] !== undefined) {
                        v = defaults[k];
                    }
                    if (v !== null) settings[k] = v;
                });
                const mode = localStorage.getItem('active_layer_mode') || '1';
                settings['active_layer_mode'] = mode;
                settings['font'] = FONT_MAP[mode] || "Space Grotesk";
                try {
                    const cleanSettings = JSON.parse(JSON.stringify(settings));
                    const bootstrapUpdates = {
                        "mobile": deleteField(),
                        "settings.mobile": deleteField()
                    };
                    if (profileData.settings && profileData.settings.card_mutation_config) {
                        bootstrapUpdates["settings.card_mutation_config"] = profileData.settings.card_mutation_config;
                    }
                    if (profileData.settings && profileData.settings.secure_pdf_config) {
                        bootstrapUpdates["settings.secure_pdf_config"] = profileData.settings.secure_pdf_config;
                    }
                    Object.entries(cleanSettings).forEach(([k, v]) => {
                        bootstrapUpdates[`settings.${k}`] = v;
                    });
                    updateDoc(doc(db, "users", user.uid), bootstrapUpdates).catch(err => {
                        console.warn("☁️ Cloud: Failed to bootstrap mobile settings", err);
                    });
                } catch (err) {
                    console.warn("☁️ Cloud: Failed to bootstrap mobile settings", err);
                }
            }


            // Background Sync: No UI disruption/notification popup
            // if (typeof pop === 'function') pop("Your profile changes have been securely saved.", "success");
        } else {
            // 🚦 Skip bootstrap write if emailRegister or phone verification is handling this new user.
            if (window.isRegistering || (!user.email && window.__pendingPhoneVerification)) return;

            console.info("ℹ️ No Firestore profile found — creating bootstrap record...");
            const nameParts = user.displayName ? user.displayName.split(' ') : ["Cloud", "User"];
            const recoveryData = {
                first: nameParts[0],
                last: nameParts.slice(1).join(' ') || "User",
                email: user.email || '',
                uid: user.uid,
                joined: getStandard24HDateTime(),
                currentLogin: getStandard24HDateTime(),
                lastLogin: "NEW_BOOTSTRAP",
                authProvider: user.providerData[0]?.providerId || 'unknown',
                isVerified: true,
                role: (user.email && ownerEmails.includes(user.email.toLowerCase())) ? 'owner' : 'user'
            };

            const bootstrapRef = doc(db, "users", user.uid);
            const bootstrapSnap = await getDoc(bootstrapRef);
            if (bootstrapSnap.exists()) {
                // Doc already created — update without overwriting joined
                const { joined, ...updateRecovery } = recoveryData;
                await updateDoc(bootstrapRef, updateRecovery);
            } else {
                await setDoc(bootstrapRef, recoveryData);
            }
            window.userData = recoveryData;
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(recoveryData), _getAppSecret()).toString();
            sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }
            // Silently bootstrap during fresh registration — emailRegister will show its own success toast
            // if (typeof pop === 'function') pop("Profile Bootstrapped to Cloud", "success");
        }
    } catch (e) {
        console.error("❌ Cloud Sync Failed:", e);
        if (typeof window.pop === 'function') window.pop("Cloud Sync Failed: " + e.message, "error");
    } finally {
        window.isCloudSyncing = false;
        window._isSyncProfileRunning = false;
        window.hasVerifiedThisSession = true;
        if (typeof updateMomentumDash === 'function') updateMomentumDash();
        if (typeof updateAvatarUI === 'function') updateAvatarUI();
        try {
            document.querySelectorAll('iframe').forEach(ifr => {
                try {
                    if (ifr.contentWindow && typeof ifr.contentWindow.updateAvatarUI === 'function') {
                        ifr.contentWindow.updateAvatarUI();
                    }
                } catch (err) { }
            });
        } catch (e) { }
    }
};

// ═══════════════════════════════════════════════════════════════════════
// 🔐 cosmo SUBSCRIPTION MANAGER — Firebase-Backed Plan Verification
// Source of Truth: Firestore users/{uid}/subscription
// Cache: localStoragecosmocrypt _sub_enc (AES encrypted)
// History: Firestore users/{uid}/subscriptionHistory (subcollection)
// Rule: If not logged in → always FOUNDATION. Login → sync from Firebase.
// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// 🔐 COSMO CRYPT SUBSCRIPTION MANAGER — Firebase-Backed Plan Verification
// Source of Truth: Firestore users/{uid}/subscription
// Cache: localStorage cosmocrypt_sub_enc (AES encrypted)
// History: Firestore users/{uid}/subscriptionHistory (subcollection)
// Rule: If not logged in → always ELEMENT. Login → sync from Firebase.
// ═══════════════════════════════════════════════════════════════════════
Object.defineProperty(window, 'CosmoCryptSubscription', {
    value: (function () {

        // ── Internal tier map ──
        const TIER_MAP = {
            'element': 'element', 'foundation': 'element', 'normal': 'element',
            'catalyst': 'catalyst', 'velocity': 'catalyst', 'max': 'catalyst',
            'matrix': 'matrix', 'pro': 'matrix',
            'quantum': 'quantum', 'ultra': 'quantum'
        };
        const TIER_TO_INTERNAL = {
            'element': 'element', 'foundation': 'element', 'normal': 'element',
            'catalyst': 'catalyst', 'velocity': 'catalyst', 'max': 'catalyst',
            'matrix': 'matrix', 'pro': 'matrix',
            'quantum': 'quantum', 'ultra': 'quantum'
        };
        const TIER_RANK = {
            'element': 0, 'foundation': 0, 'normal': 0,
            'catalyst': 1, 'velocity': 1, 'max': 1,
            'matrix': 2, 'pro': 2,
            'quantum': 3, 'ultra': 3
        };
        const CACHE_KEY = 'cosmocrypt_sub_enc';
        const PLAN_KEY = 'user_subscription';
        const HISTORY_CACHE_KEY = 'cosmocrypt_sub_history';
        const STORAGE_KEY = 'cosmo_crypt_purchase_history';

        // ── Helpers ──
        function _rawGetItem(key) {
            try {
                const getFn = (typeof window !== 'undefined' && window.originalGetItem) ? window.originalGetItem : Storage.prototype.getItem;
                return getFn.call(localStorage, key);
            } catch (e) { return null; }
        }

        function _rawSetItem(key, val) {
            try {
                const setFn = (typeof window !== 'undefined' && window.originalSetItem) ? window.originalSetItem : Storage.prototype.setItem;
                setFn.call(localStorage, key, val);
            } catch (e) { }
        }

        function _rawRemoveItem(key) {
            try {
                const remFn = (typeof window !== 'undefined' && window.originalRemoveItem) ? window.originalRemoveItem : Storage.prototype.removeItem;
                remFn.call(localStorage, key);
            } catch (e) { }
        }

        function _getUserId() {
            const user = _user();
            if (user && user.uid) return user.uid;
            try {
                const enc = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userStoreEnc') : null) || _rawGetItem('userStoreEnc');
                if (enc) {
                    const b = CryptoJS.AES.decrypt(enc, _getAppSecret());
                    const dec = JSON.parse(b.toString(CryptoJS.enc.Utf8));
                    if (dec && dec.uid) return dec.uid;
                }
            } catch (e) { }
            if (window.userData && window.userData.uid) return window.userData.uid;
            return null;
        }

        function _encrypt(obj) {
            try {
                const uid = _getUserId();
                if (!uid) return null;
                obj.uid = uid; // bind cache to the user's UID
                const signPayload = uid + obj.plan + (obj.expiresAtMs || 0) + _getAppSecret();
                obj.signature = CryptoJS.SHA256(signPayload).toString();
                return CryptoJS.AES.encrypt(JSON.stringify(obj), _getAppSecret()).toString();
            } catch (e) { return null; }
        }
        function _decrypt(str) {
            try {
                const b = CryptoJS.AES.decrypt(str, _getAppSecret());
                const obj = JSON.parse(b.toString(CryptoJS.enc.Utf8));
                const uid = _getUserId();
                if (uid && obj.uid !== uid) return null; // Reject if UID mismatch with active user session

                // Verify the signature if signature is present and uid exists
                if (obj.signature && uid) {
                    const signPayload = uid + obj.plan + (obj.expiresAtMs || 0) + _getAppSecret();
                    const expectedSignature = CryptoJS.SHA256(signPayload).toString();
                    if (obj.signature !== expectedSignature) {
                        console.warn('[CosmoCryptSub] Cache signature mismatch, rejecting cache');
                        return null; // Reject if tampered
                    }
                }
                return obj;
            } catch (e) { return null; }
        }
        function _now() { return Date.now(); }
        function _isoNow() { return new Date().toISOString(); }

        // ── Get current auth user ──
        function _user() { return auth && auth.currentUser ? auth.currentUser : null; }

        // ── In-memory verified plan cache (never written to plaintext localStorage) ──
        let _currentVerifiedPlan = null;

        // ── Build a clean subscription object from Firestore data ──
        function _buildSubObj(data) {
            const exMs = data.expiresAtMs || (data.expiresAt ? new Date(data.expiresAt).getTime() : 0);
            const purchMs = data.purchasedAtMs || (data.purchasedAt ? new Date(data.purchasedAt).getTime() : 0);
            const now = _now();
            const daysLeft = exMs ? Math.max(0, Math.ceil((exMs - now) / 86400000)) : 0;
            const internalPlan = TIER_TO_INTERNAL[data.plan] || data.plan || 'element';
            const isActive = (internalPlan === 'foundation' || internalPlan === 'normal' || internalPlan === 'element') ? true : (exMs > now && data.status !== 'cancelled' && data.status !== 'reset' && data.status !== 'expired_downgrade');
            const hoursLeft = exMs ? Math.max(0, Math.floor((exMs - now) / 3600000)) : 0;
            const minutesLeft = exMs ? Math.max(0, Math.floor(((exMs - now) % 3600000) / 60000)) : 0;
            const hadPremium = (data.status === 'reset' || data.status === 'cancelled') ? false : (
                data.had_premium_plan === true ||
                data.had_premium_plan === 'true' ||
                internalPlan !== 'element' ||
                Boolean(data.paymentId || data.orderId || data.legacyId)
            );
            return {
                plan: data.plan || 'element',
                planDisplay: (window.planFullData && window.planFullData[data.plan]) ? window.planFullData[data.plan].name : (data.plan || 'ELEMENT').toUpperCase(),
                status: isActive ? 'active' : 'expired',
                isActive,
                had_premium_plan: hadPremium,
                purchasedAt: data.purchasedAt || null,
                purchasedAtMs: purchMs || null,
                expiresAt: data.expiresAt || null,
                expiresAtMs: exMs || null,
                durationDays: data.durationDays || null,
                daysLeft,
                hoursLeft,
                minutesLeft,
                paymentId: data.paymentId || null,
                orderId: data.orderId || null,
                amount: data.amount || null,
                currency: data.currency || 'INR',
                gateway: data.gateway || null,
                lastVerifiedAt: _isoNow(),
                lastResetAt: data.lastResetAt || null,
                resetCount: data.resetCount || 0,
                // Extra properties required for the UI details modal:
                legacyId: data.legacyId || null,
                userName: data.userName || null,
                planNameDisplay: data.planNameDisplay || null,
                priceStr: data.priceStr || null,
                discount: data.discount !== undefined ? data.discount : 0,
                upgradeSequence: data.upgradeSequence || 1,
                elevationStatusDisplay: data.elevationStatusDisplay || null
            };
        }

        // ── Write encrypted cache to localStorage (Zero Plaintext LocalStorage) ──
        function _writeCache(subObj) {
            try {
                _currentVerifiedPlan = Object.freeze({ ...subObj });
                const enc = _encrypt(subObj);
                if (enc) {
                    try { sessionStorage.setItem(CACHE_KEY, enc); } catch (e) { }
                    _rawRemoveItem(CACHE_KEY);
                }
                // Proactively purge any unencrypted legacy keys from localStorage
                _rawRemoveItem(PLAN_KEY);
                _rawRemoveItem('user_subscription');
                _rawRemoveItem('last_notified_subscription');
                _rawRemoveItem('premiumActive');
                _rawRemoveItem('had_premium_plan');
            } catch (e) { }
        }

        // ── Read cache from localStorage ──
        function _readCache() {
            try {
                const enc = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(CACHE_KEY) : null) || _rawGetItem(CACHE_KEY);
                if (!enc) return null;
                return _decrypt(enc);
            } catch (e) { return null; }
        }

        // ── Clear cache ──
        function _clearCache() {
            try {
                _currentVerifiedPlan = null;
                localStorage.removeItem(CACHE_KEY);
                localStorage.removeItem(HISTORY_CACHE_KEY);
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem('cosmo_crypt_purchase_history');
                localStorage.removeItem('cosmo_user_plan');
                localStorage.removeItem('current_plan');
                localStorage.removeItem(PLAN_KEY);
                localStorage.removeItem('user_subscription');
                localStorage.removeItem('last_notified_subscription');
                localStorage.removeItem('premiumActive');
                localStorage.removeItem('had_premium_plan');
                if (typeof originalRemoveItem === 'function') {
                    originalRemoveItem.call(localStorage, CACHE_KEY);
                    originalRemoveItem.call(localStorage, HISTORY_CACHE_KEY);
                    originalRemoveItem.call(localStorage, STORAGE_KEY);
                    originalRemoveItem.call(localStorage, 'cosmo_crypt_purchase_history');
                    originalRemoveItem.call(localStorage, 'cosmo_user_plan');
                    originalRemoveItem.call(localStorage, 'current_plan');
                    originalRemoveItem.call(localStorage, PLAN_KEY);
                    originalRemoveItem.call(localStorage, 'user_subscription');
                    originalRemoveItem.call(localStorage, 'last_notified_subscription');
                    originalRemoveItem.call(localStorage, 'premiumActive');
                    originalRemoveItem.call(localStorage, 'had_premium_plan');
                }
            } catch (e) { }
        }

        // ── Apply plan to runtime and fire event ──
        function _applyPlan(subObj, reason) {
            _currentVerifiedPlan = Object.freeze({ ...subObj });
            const internalPlan = TIER_TO_INTERNAL[subObj.plan] || 'element';
            const oldPlan = window.__last_notified_subscription || 'element';
            const isPrem = subObj.isActive && internalPlan !== 'element';
            window.__last_notified_subscription = internalPlan;
            _writeCache(subObj);

            // Notify all listeners via secure custom events
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: PLAN_KEY, val: internalPlan, newValue: internalPlan } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: internalPlan, newValue: internalPlan } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: isPrem ? 'true' : 'false' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'had_premium_plan', val: subObj.had_premium_plan ? 'true' : 'false' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'last_notified_subscription', val: internalPlan } }));
            window.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: internalPlan, planObj: subObj, reason: reason || 'sync' } }));
            window.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: internalPlan, planObj: subObj, reason: reason || 'sync' } }));
            // Propagate to iframes
            document.querySelectorAll('iframe').forEach(iframe => {
                try {
                    if (iframe.contentWindow) {
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: PLAN_KEY, val: internalPlan, newValue: internalPlan } }));
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: internalPlan, newValue: internalPlan } }));
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: isPrem ? 'true' : 'false' } }));
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'had_premium_plan', val: subObj.had_premium_plan ? 'true' : 'false' } }));
                        iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'last_notified_subscription', val: internalPlan } }));
                        iframe.contentWindow.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: internalPlan, planObj: subObj, reason: reason || 'sync' } }));
                        iframe.contentWindow.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: internalPlan, planObj: subObj, reason: reason || 'sync' } }));
                    }
                } catch (e) { }
            });
            // Trigger UI refresh if plan changed
            if (oldPlan !== internalPlan) {
                if (typeof handlePlanChangeRefresh === 'function') {
                    setTimeout(handlePlanChangeRefresh, 100);
                }
            }
        }

        // ── Set element (free) plan ──
        function _applyFoundation(reason) {
            const elementObj = _buildSubObj({ plan: 'element', status: 'active', expiresAtMs: 0, purchasedAtMs: 0 });
            _applyPlan(elementObj, reason || 'no_login');
            try {
                localStorage.removeItem('cosmo_crypt_purchase_history');
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem('cosmo_user_plan');
                localStorage.removeItem('current_plan');
                if (typeof originalRemoveItem === 'function') {
                    originalRemoveItem.call(localStorage, 'cosmo_crypt_purchase_history');
                    originalRemoveItem.call(localStorage, STORAGE_KEY);
                    originalRemoveItem.call(localStorage, 'cosmo_user_plan');
                    originalRemoveItem.call(localStorage, 'current_plan');
                }
                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: STORAGE_KEY, val: null } }));
                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_crypt_purchase_history', val: null } }));
            } catch (e) { }
        }

        // ── Log to subscriptionHistory subcollection ──
        async function _logHistory(uid, action, subObj, previousPlan, note) {
            try {
                const historyRef = collection(db, 'users', uid, 'subscriptionHistory');

                // Only update and delete history when upgrading/purchasing a new paid running plan
                if (subObj && subObj.plan && subObj.plan !== 'foundation' && subObj.plan !== 'element' && subObj.plan !== 'normal') {
                    // Delete all previous history documents
                    const snap = await getDocs(historyRef);
                    for (const docSnap of snap.docs) {
                        try {
                            await deleteDoc(docSnap.ref);
                        } catch (delErr) {
                            console.warn('[CosmoCryptSub] Failed to delete previous history doc:', delErr);
                        }
                    }

                    const record = {
                        action,
                        plan: subObj.plan,
                        previousPlan: previousPlan || 'element',
                        timestamp: _isoNow(),
                        timestampMs: _now(),
                        paymentId: subObj.paymentId || null,
                        orderId: subObj.orderId || null,
                        amount: subObj.amount || null,
                        currency: subObj.currency || 'INR',
                        gateway: subObj.gateway || null,
                        purchasedAt: subObj.purchasedAt || null,
                        expiresAt: subObj.expiresAt || null,
                        durationDays: subObj.durationDays || null,
                        note: note || ''
                    };
                    await addDoc(historyRef, record);
                }
            } catch (e) {
                console.warn('[CosmoCryptSub] Failed to log history:', e);
            }
        }

        // ═══════════════════════════════
        // PUBLIC API
        // ═══════════════════════════════

        // Get current active plan tier (internal key: 'element', 'catalyst', 'matrix', 'quantum')
        function getActivePlan() {
            const user = _user();
            if (!user) return 'element';
            if (_currentVerifiedPlan) {
                const now = _now();
                const exMs = _currentVerifiedPlan.expiresAtMs || 0;
                const internal = TIER_TO_INTERNAL[_currentVerifiedPlan.plan] || 'element';
                const isActive = (internal === 'foundation' || internal === 'element' || internal === 'normal') ? true : (exMs > now && _currentVerifiedPlan.status !== 'cancelled' && _currentVerifiedPlan.status !== 'reset' && _currentVerifiedPlan.status !== 'expired_downgrade');
                if (isActive) return internal;
            }
            const cached = _readCache();
            if (cached) {
                const now = _now();
                const exMs = cached.expiresAtMs || 0;
                const internal = TIER_TO_INTERNAL[cached.plan] || 'element';
                const isActive = (internal === 'foundation' || internal === 'element' || internal === 'normal') ? true : (exMs > now && cached.status !== 'cancelled' && cached.status !== 'reset' && cached.status !== 'expired_downgrade');
                if (isActive) return internal;
            }
            return 'element';
        }

        // Get full subscription object
        function getPlanData() {
            const user = _user();
            if (!user) {
                return _buildSubObj({ plan: 'element', status: 'active', expiresAtMs: 0 });
            }
            if (_currentVerifiedPlan) return _currentVerifiedPlan;
            const cached = _readCache();
            if (cached) return cached;
            return _buildSubObj({ plan: 'element', status: 'active', expiresAtMs: 0 });
        }

        // Has user ever had or currently has a premium plan in Firebase?
        function hadPremiumPlan() {
            const user = _user();
            if (!user) return false;
            const data = getPlanData();
            return !!(data && data.had_premium_plan);
        }

        // Is current plan active (not expired)?
        function isPlanActive() {
            const data = getPlanData();
            return data.isActive;
        }

        // Days remaining on current plan
        function getDaysRemaining() {
            const data = getPlanData();
            return data.daysLeft || 0;
        }

        // Formatted time left string
        function getTimeLeftString() {
            const data = getPlanData();
            if (!data.expiresAtMs || data.plan === 'foundation' || data.plan === 'element' || data.plan === 'normal') return 'Lifetime';
            if (!data.isActive) return 'Expired';
            if (data.daysLeft > 1) return `${data.daysLeft}d left`;
            if (data.hoursLeft > 0) return `${data.hoursLeft}h ${data.minutesLeft}m left`;
            return `${data.minutesLeft}m left`;
        }

        window._cardLimitsSyncInProgress = false;
        window._lastCardLimitsHash = null;
        window._lastCardLimitsSyncTime = 0;

        window.calculateAndSyncCardLimits = async function (overrideUser) {
            // ── Debounce: skip if another sync ran within the last 2 seconds ──
            const now = Date.now();
            if (window._cardLimitsSyncInProgress || (now - window._lastCardLimitsSyncTime) < 2000) {
                return window.userData && window.userData.card_limits ? window.userData.card_limits : null;
            }
            window._cardLimitsSyncInProgress = true;
            window._lastCardLimitsSyncTime = now;

            try {
                const u = overrideUser || (typeof auth !== 'undefined' ? auth.currentUser : null) || (window.userData && window.userData.uid ? window.userData : null);

                const userTier = window.getUserActivePlanTier ? window.getUserActivePlanTier() : 'Element';
                const limitPerTemplate = userTier === 'Catalyst' ? 25 : (userTier === 'Matrix' ? 50 : (userTier === 'Quantum' ? 'unlimited' : 10));
                const totalAllowedAll = userTier === 'Catalyst' ? 275 : (userTier === 'Matrix' ? 550 : (userTier === 'Quantum' ? 'unlimited' : 110));

                function getStorageCount(key) {
                    try {
                        let val = null;
                        const store = (typeof window.CosmoTemplateStore !== 'undefined') ? window.CosmoTemplateStore : (window.parent && window.parent.CosmoTemplateStore ? window.parent.CosmoTemplateStore : null);
                        if (store && typeof store.get === 'function') {
                            val = store.get(key, null);
                        }
                        if (!val || (Array.isArray(val) && val.length === 0)) {
                            const ud = window.userData || (window.parent && window.parent.userData) || {};
                            const tv = ud.template_vaults || {};
                            const cleanK = store && typeof store.getCleanKey === 'function' ? store.getCleanKey(key) : key;
                            val = tv[cleanK] || tv[key];
                        }
                        if (!val || (Array.isArray(val) && val.length === 0)) {
                            let raw = null;
                            if (typeof window.safeStorage !== 'undefined' && window.safeStorage.getItem) {
                                raw = window.safeStorage.getItem(key);
                            }
                            if (!raw && typeof localStorage !== 'undefined') {
                                raw = localStorage.getItem(key);
                            }
                            if (raw) {
                                try { val = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { val = raw; }
                            }
                        }
                        if (!val) return 0;

                        if (typeof val === 'object' && val !== null && val.data !== undefined && !Array.isArray(val)) {
                            val = val.data;
                        }

                        if (Array.isArray(val)) {
                            let nested = 0;
                            val.forEach(sec => {
                                if (sec && Array.isArray(sec.subsections)) {
                                    sec.subsections.forEach(sub => {
                                        if (sub && Array.isArray(sub.cards)) nested += sub.cards.length;
                                    });
                                }
                            });
                            return Math.max(val.length, nested);
                        }
                        if (val && typeof val === 'object') {
                            if (Array.isArray(val.documents)) return val.documents.length;
                            if (Array.isArray(val.cards)) return val.cards.length;
                            if (Array.isArray(val.notes)) return val.notes.length;
                            return Object.keys(val).length;
                        }
                        return 0;
                    } catch (e) {
                        return 0;
                    }
                }

                const usedByTemplate = {
                    node_infrastructure: Math.max(getStorageCount('node_infrastructure'), getStorageCount('cosmo_vault_data')),
                    financial_credentials: Math.max(getStorageCount('financial_credentials'), getStorageCount('cosmo_banks_vault_data'), getStorageCount('cosmo_api_keys')),
                    payment_gateways: Math.max(getStorageCount('payment_gateways'), getStorageCount('cosmo_payment_keys')),
                    developer_endpoints: Math.max(getStorageCount('developer_endpoints'), getStorageCount('cosmo_developer_keys')),
                    dns_management: Math.max(getStorageCount('dns_management'), getStorageCount('cosmo_dns_keys')),
                    workspace_integration: Math.max(getStorageCount('workspace_integration'), getStorageCount('cosmo_workspace_keys')),
                    merchant_identities: Math.max(getStorageCount('merchant_identities'), getStorageCount('cosmo_merchant_keys')),
                    national_identity: Math.max(getStorageCount('national_identity'), getStorageCount('cosmo_national_vault_data')),
                    identity_vault: Math.max(getStorageCount('identity_vault_documents'), getStorageCount('identity_vault')),
                    encrypted_partitions: Math.max(getStorageCount('encrypted_partitions_notes'), getStorageCount('cosmo_encrypted_partitions_notes_v1')),
                    system_encryption: Math.max(getStorageCount('system_encryption'), getStorageCount('cosmo_system_locks'))
                };

                let totalCardsUsed = 0;
                Object.values(usedByTemplate).forEach(cnt => {
                    totalCardsUsed += (typeof cnt === 'number' ? cnt : 0);
                });

                const remainingTotal = totalAllowedAll === 'unlimited' ? 'unlimited' : Math.max(0, totalAllowedAll - totalCardsUsed);

                // ── Build a hash of the actual data (excluding last_updated) to detect real changes ──
                const dataHash = JSON.stringify({ active_plan: userTier, total_cards_used: totalCardsUsed, used_by_template: usedByTemplate });

                const cardLimitsSection = {
                    active_plan: userTier,
                    limit_per_template: limitPerTemplate,
                    total_templates: 11,
                    total_allowed_cards_all_templates: totalAllowedAll,
                    total_cards_used: totalCardsUsed,
                    remaining_cards_total: remainingTotal,
                    used_by_template: usedByTemplate,
                    last_updated: _isoNow()
                };

                // Always update in-memory so UI is fresh
                if (window.userData) {
                    window.userData.card_limits = cardLimitsSection;
                }
                if (window.parent && window.parent !== window && window.parent.userData) {
                    window.parent.userData.card_limits = cardLimitsSection;
                }

                // ── Only write to Firestore if card counts actually changed ──
                if (dataHash !== window._lastCardLimitsHash) {
                    window._lastCardLimitsHash = dataHash;
                    if (u && u.uid && typeof setDoc === 'function' && typeof doc === 'function' && typeof db !== 'undefined') {
                        await setDoc(doc(db, 'users', u.uid), { card_limits: cardLimitsSection }, { merge: true }).catch(err => console.warn(err));
                        await setDoc(doc(db, 'users', u.uid, 'card_limits', 'summary'), cardLimitsSection, { merge: true }).catch(err => console.warn(err));

                        // Trigger cloud sync of full analytics section instantly on card add/edit/delete
                        if (typeof window.syncAnalyticsToCloud === 'function') {
                            try {
                                window.syncAnalyticsToCloud();
                            } catch (anErr) { }
                        }
                    }
                }

                return cardLimitsSection;
            } catch (e) {
                console.warn('[CosmoCryptCardLimits] Sync error:', e);
            } finally {
                window._cardLimitsSyncInProgress = false;
            }
        };

        // ── Sync plan from Firestore (cloud-first verification) ──
        async function syncFromFirebase() {
            const user = _user();
            if (!user) {
                _applyFoundation('no_login');
                return { plan: 'element', source: 'no_login' };
            }
            try {
                const userSnap = await getDocFromServer(doc(db, 'users', user.uid));
                if (!userSnap.exists()) {
                    _applyFoundation('missing_profile');
                    return { plan: 'element', source: 'missing_profile' };
                }
                const userData = userSnap.data();
                let sub = userData.subscription;

                if (!sub) {
                    sub = {
                        plan: 'element',
                        status: 'active',
                        expiresAt: null,
                        expiresAtMs: null,
                        purchasedAt: null,
                        purchasedAtMs: null,
                        lastResetAt: null,
                        resetCount: 0,
                        lastVerifiedAt: _isoNow(),
                    };
                    try {
                        await updateDoc(doc(db, 'users', user.uid), { subscription: sub });
                    } catch (subInitErr) {
                        console.warn('[CosmoCryptSub] Failed to initialize subscription map in Firestore:', subInitErr);
                    }
                }

                // ONE-TIME CLEANUP & MIGRATION: If legacy flat fields exist at the root, migrate them to nested subscription map and purge root fields
                if (userData && userData.plan !== undefined) {
                    console.log('[CosmoCryptSub] Migrating legacy flat subscription fields to nested map...');
                    const prevResetCount = userData.resetCount !== undefined ? userData.resetCount : (sub && sub.resetCount !== undefined ? sub.resetCount : 0);
                    const prevLastResetAt = userData.lastResetAt !== undefined ? userData.lastResetAt : (sub && sub.lastResetAt !== undefined ? sub.lastResetAt : null);

                    if (userData.plan !== 'foundation' && userData.plan !== 'element' && userData.plan !== 'normal') {
                        sub = {
                            plan: userData.plan,
                            status: userData.status || 'active',
                            purchasedAt: userData.purchasedAt || null,
                            purchasedAtMs: userData.purchasedAtMs || null,
                            expiresAt: userData.expiresAt || null,
                            expiresAtMs: userData.expiresAtMs || null,
                            durationDays: userData.durationDays || null,
                            lastVerifiedAt: userData.lastVerifiedAt || null,
                            lastResetAt: prevLastResetAt,
                            resetCount: prevResetCount,
                            elevationStatusDisplay: userData.elevationStatusDisplay || null,
                            upgradeSequence: userData.upgradeSequence || 1,
                            userName: userData.userName || null,
                            planNameDisplay: userData.planNameDisplay || null,
                            amountPaidDisplay: userData.amountPaidDisplay || null,
                            priceStr: userData.priceStr || null,
                            priceAmount: userData.priceAmount || null,
                            upgradeDiscountDisplay: userData.upgradeDiscountDisplay || null,
                            discount: userData.discount || 0,
                            purchaseDateDisplay: userData.purchaseDateDisplay || null,
                            purchaseTimeDisplay: userData.purchaseTimeDisplay || null,
                            planExpiresOnDisplay: userData.planExpiresOnDisplay || null,
                            timeRemainingSnapshot: userData.timeRemainingSnapshot || null,
                            legacyId: userData.legacyId || null,
                            paymentId: userData.paymentId || null,
                            orderId: userData.orderId || null,
                            amount: userData.amount || null,
                            currency: userData.currency || 'INR',
                            gateway: userData.gateway || null,
                            premiumActive: userData.premiumActive || null,
                        };
                    } else {
                        sub = {
                            plan: 'element',
                            status: userData.status || (sub ? sub.status : 'active'),
                            expiresAt: userData.expiresAt || (sub ? sub.expiresAt : null),
                            expiresAtMs: userData.expiresAtMs || (sub ? sub.expiresAtMs : null),
                            purchasedAt: userData.purchasedAt || (sub ? sub.purchasedAt : null),
                            purchasedAtMs: userData.purchasedAtMs || (sub ? sub.purchasedAtMs : null),
                            lastResetAt: prevLastResetAt,
                            resetCount: prevResetCount,
                            lastVerifiedAt: userData.lastVerifiedAt || (sub ? sub.lastVerifiedAt : _isoNow()),
                        };
                    }

                    try {
                        await updateDoc(doc(db, 'users', user.uid), {
                            subscription: sub,
                            plan: deleteField(),
                            status: deleteField(),
                            purchasedAt: deleteField(),
                            purchasedAtMs: deleteField(),
                            expiresAt: deleteField(),
                            expiresAtMs: deleteField(),
                            durationDays: deleteField(),
                            lastVerifiedAt: deleteField(),
                            lastResetAt: deleteField(),
                            resetCount: deleteField(),
                            elevationStatusDisplay: deleteField(),
                            upgradeSequence: deleteField(),
                            userName: deleteField(),
                            planNameDisplay: deleteField(),
                            amountPaidDisplay: deleteField(),
                            priceStr: deleteField(),
                            priceAmount: deleteField(),
                            upgradeDiscountDisplay: deleteField(),
                            discount: deleteField(),
                            purchaseDateDisplay: deleteField(),
                            purchaseTimeDisplay: deleteField(),
                            planExpiresOnDisplay: deleteField(),
                            timeRemainingSnapshot: deleteField(),
                            legacyId: deleteField(),
                            paymentId: deleteField(),
                            orderId: deleteField(),
                            amount: deleteField(),
                            currency: deleteField(),
                            gateway: deleteField(),
                            premiumActive: deleteField()
                        });
                    } catch (cleanErr) {
                        console.warn('[CosmoCryptSub] Migration of legacy flat fields failed:', cleanErr);
                    }
                }

                if (!sub || !sub.plan || sub.plan === 'foundation' || sub.plan === 'element' || sub.plan === 'normal') {
                    // 🔍 Check if subscriptionHistory has an active purchased plan before defaulting to element
                    // ONLY if user hasn't explicitly reset or cancelled their subscription
                    let recovered = false;
                    const isExplicitResetOrCancelled = sub && (sub.status === 'reset' || sub.status === 'cancelled' || sub.status === 'expired_downgrade');
                    if (!isExplicitResetOrCancelled) {
                        try {
                            const histSnap = await getDocs(collection(db, 'users', user.uid, 'subscriptionHistory'));
                            const records = histSnap.docs.map(d => ({ docId: d.id, ...d.data() }));
                            records.sort((a, b) => (b.timestampMs || 0) - (a.timestampMs || 0));
                            const activeRec = records.find(r => r.plan && !['foundation', 'element', 'normal'].includes(r.plan.toLowerCase()) && (r.expiresAtMs ? r.expiresAtMs > _now() : (r.expiresAt ? new Date(r.expiresAt).getTime() > _now() : true)) && r.action !== 'reset' && r.action !== 'expired');
                            if (activeRec) {
                                sub = {
                                    ...activeRec,
                                    status: 'active',
                                    lastVerifiedAt: _isoNow()
                                };
                                try {
                                    await setDoc(doc(db, 'users', user.uid), { subscription: sub }, { merge: true });
                                } catch (setErr) { }
                                recovered = true;
                            }
                        } catch (hErr) { }
                    }

                    if (!recovered) {
                        _applyFoundation('no_subscription_field');
                        return { plan: 'element', source: 'no_subscription_field' };
                    }
                }

                const subObj = _buildSubObj(sub);

                // AUTO-EXPIRE: strictly only if plan expiration timestamp has elapsed
                const exMsCheck = subObj.expiresAtMs || (sub.expiresAt ? new Date(sub.expiresAt).getTime() : 0);
                const nowCheck = _now();
                if (exMsCheck > 0 && exMsCheck <= nowCheck && sub.plan !== 'foundation' && sub.plan !== 'element' && sub.plan !== 'normal') {
                    console.warn('[CosmoCryptSub] Plan expired — auto-downgrading to Element');
                    const previousPlan = sub.plan;
                    const newSubData = {
                        plan: 'element',
                        status: 'expired_downgrade',
                        expiresAt: sub.expiresAt || null,
                        expiresAtMs: sub.expiresAtMs || null,
                        purchasedAt: sub.purchasedAt || null,
                        purchasedAtMs: sub.purchasedAtMs || null,
                        lastResetAt: _isoNow(),
                        resetCount: (sub.resetCount || 0) + 1,
                    };
                    await setDoc(doc(db, 'users', user.uid), { subscription: newSubData }, { merge: true });
                    await _logHistory(user.uid, 'expired', subObj, previousPlan, 'Auto-downgraded on expiry');

                    if (typeof window.addSuiteNotification === 'function') {
                        window.addSuiteNotification('account', 'Subscription Expired', 'Your subscription expired and your plan has been downgraded to Element.', { 'New Plan': 'Element', 'Status': 'Expired' });
                    }

                    // Send subscription cancelled/expiry email
                    if (user && user.email) {
                        const emailName = subObj.userName || user.displayName || 'User';
                        const sendCancelEmail = window.sendSubscriptionCancelled || (window.parent && window.parent.sendSubscriptionCancelled);
                        if (typeof sendCancelEmail === 'function') {
                            sendCancelEmail(user.email, emailName)
                                .then(() => console.log('[CosmoCryptSub] Auto-expiry cancellation email sent successfully'))
                                .catch(err => console.warn('[CosmoCryptSub] Auto-expiry cancellation email failed:', err));
                        }
                    }

                    _applyFoundation('expired');
                    if (typeof window.pop === 'function') window.pop('Your plan expired. You have been moved to the Element plan.', 'warning');
                    return { plan: 'element', source: 'expired' };
                }

                // Plan is valid — apply it
                _applyPlan(subObj, 'firebase_sync');

                // ─── UPDATE DATABASE REMAINING TIME SNAPSHOT ───
                if (subObj.isActive && sub.plan !== 'foundation' && sub.plan !== 'element' && sub.plan !== 'normal') {
                    const now = _now();
                    const exMs = subObj.expiresAtMs;
                    if (exMs) {
                        const diffMs = Math.max(0, exMs - now);
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        const timeRemainingVal = diffDays > 0 ? `${diffDays}D ${diffHours}H LEFT` : `${diffHours}H ${diffMinutes}M LEFT`;

                        if (sub.timeRemainingSnapshot !== timeRemainingVal) {
                            sub.timeRemainingSnapshot = timeRemainingVal;
                            subObj.timeRemainingSnapshot = timeRemainingVal;
                        }
                    }
                }

                // ─── RESTORE ELEVATION DETAILS to localStorage from Firebase ───
                if (sub.legacyId || sub.purchasedAtMs || (sub.plan && sub.plan !== 'element')) {
                    _restoreHistoryEntryToLocal(sub);
                }
                restoreHistoryFromCloud().catch(() => { });

                // Sync dedicated card_limits section in Firestore user document (total allowed across all 11 templates e.g. 110 for Element, 275 for Catalyst, 550 for Matrix, unlimited for Quantum)
                await window.calculateAndSyncCardLimits(user);

                return { plan: subObj.plan, source: 'firebase', data: subObj };

            } catch (e) {
                console.warn('[CosmoCryptSub] Firebase sync failed, using cache:', e.message);
                const cached = _readCache();
                if (cached && cached.isActive) {
                    _applyPlan(cached, 'cache_fallback');
                    return { plan: cached.plan, source: 'cache' };
                }
                _applyFoundation('offline_no_cache');
                return { plan: 'element', source: 'offline_no_cache' };
            }
        }

        // ── Save a new subscription to Firebase after payment ──
        const PLAN_DISPLAY_MAP = {
            'element': 'Element', 'foundation': 'Element', 'normal': 'Element',
            'catalyst': 'Catalyst', 'velocity': 'Catalyst', 'max': 'Catalyst',
            'matrix': 'Matrix', 'pro': 'Matrix',
            'quantum': 'Quantum', 'ultra': 'Quantum'
        };

        async function savePlanToFirebase(paymentData) {
            const user = _user();
            try {
                const now = _now();
                const planKey = paymentData.plan || 'quantum';
                const durationDays = paymentData.durationDays || 365;
                const exMs = paymentData.expiresAtMs || (now + durationDays * 86400000);
                const subObj = _buildSubObj({
                    ...paymentData,
                    plan: planKey,
                    status: 'active',
                    purchasedAt: new Date(now).toISOString(),
                    purchasedAtMs: now,
                    expiresAt: new Date(exMs).toISOString(),
                    expiresAtMs: exMs,
                    durationDays: durationDays
                });
                _applyPlan(subObj, 'manual_save');
                _restoreHistoryEntryToLocal(subObj);
                return { success: true, plan: planKey, data: subObj };
            } catch (e) {
                return { success: false, error: e.message };
            }
        }

        // ── Helper: update in-memory purchase history from Firebase data (Zero LocalStorage) ──
        function _restoreHistoryEntryToLocal(subRecord) {
            try {
                const entryId = subRecord.legacyId || subRecord.purchasedAtMs || Date.now();
                const internalPlan = TIER_TO_INTERNAL[subRecord.plan] || subRecord.plan || 'element';
                const displayName = subRecord.planNameDisplay || (window.planFullData && window.planFullData[internalPlan] ? window.planFullData[internalPlan].name : (subRecord.plan || 'Element').toUpperCase());
                const entry = {
                    id: entryId,
                    userName: subRecord.userName || 'ELITE USER',
                    planName: displayName,
                    price: subRecord.priceStr || `₹${subRecord.priceAmount || 0}`,
                    discount: subRecord.discount || 0,
                    upgradeSequence: subRecord.upgradeSequence || 1,
                    purchaseDate: subRecord.purchasedAt || (subRecord.purchasedAtMs ? new Date(subRecord.purchasedAtMs).toISOString() : new Date().toISOString()),
                    expiryDate: subRecord.expiresAt || (subRecord.expiresAtMs ? new Date(subRecord.expiresAtMs).toISOString() : new Date().toISOString()),
                    isSynced: true,
                    fromCloud: true,
                    paymentId: subRecord.paymentId || null,
                    orderId: subRecord.orderId || null
                };

                const data = JSON.stringify([entry]);
                window._cosmoPurchaseHistoryState = [entry];
                if (window.userData) window.userData.subscription_history = [entry];
                if (window.parent && window.parent.userData) window.parent.userData.subscription_history = [entry];
                if (window.parent) window.parent._cosmoPurchaseHistoryState = [entry];
                if (window.top) window.top._cosmoPurchaseHistoryState = [entry];

                try {
                    localStorage.removeItem(STORAGE_KEY);
                    localStorage.removeItem('cosmo_crypt_purchase_history');
                } catch (e) { }

                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: STORAGE_KEY, val: data } }));
                if (typeof window.syncParentSubscription === 'function') window.syncParentSubscription();
            } catch (e) {
                console.warn('[CosmoCryptSub] _restoreHistoryEntryToLocal failed:', e);
            }
        }

        // ── Restore current active plan from Firebase (single plan only, Zero LocalStorage) ──
        async function restoreHistoryFromCloud() {
            const user = _user();
            if (!user) return;
            try {
                const histSnap = await getDocs(collection(db, 'users', user.uid, 'subscriptionHistory'));
                const records = histSnap.docs.map(d => ({ docId: d.id, ref: d.ref, ...d.data() }));

                records.sort((a, b) => (b.timestampMs || 0) - (a.timestampMs || 0));

                const purchaseRecords = records.filter(r => r.legacyId && (r.action === 'purchased' || r.action === 'upgraded'));

                if (purchaseRecords.length === 0) {
                    const userSnap = await getDocFromServer(doc(db, 'users', user.uid));
                    const userData = userSnap.data();
                    const sub = userData ? userData.subscription : null;
                    if (sub && sub.plan && sub.plan !== 'foundation' && sub.plan !== 'element' && sub.plan !== 'normal' && sub.legacyId) {
                        _restoreHistoryEntryToLocal(sub);
                    }
                    return;
                }

                const latest = purchaseRecords[0];

                for (const rec of records) {
                    if (rec.docId !== latest.docId) {
                        try {
                            await deleteDoc(rec.ref);
                        } catch (delErr) {
                            console.warn('[CosmoCryptSub] Cleanup: Failed to delete older history doc:', delErr);
                        }
                    }
                }

                const entry = {
                    id: latest.legacyId,
                    userName: latest.userName || 'ELITE USER',
                    planName: latest.planNameDisplay || latest.plan || 'Element',
                    price: latest.priceStr || `\u20b9${latest.priceAmount || 0}`,
                    discount: latest.discount || 0,
                    upgradeSequence: latest.upgradeSequence || 1,
                    purchaseDate: latest.purchasedAt || latest.timestamp || (latest.purchasedAtMs || latest.timestampMs ? new Date(latest.purchasedAtMs || latest.timestampMs).toISOString() : new Date().toISOString()),
                    expiryDate: latest.expiresAt || (latest.planExpiresOnDisplay ? new Date(latest.planExpiresOnDisplay).toISOString() : (latest.expiresAtMs ? new Date(latest.expiresAtMs).toISOString() : new Date(Date.now() + 30 * 86400000).toISOString())),
                    isSynced: true,
                    fromCloud: true,
                    paymentId: latest.paymentId || null,
                    orderId: latest.orderId || null
                };

                const data = JSON.stringify([entry]);
                window._cosmoPurchaseHistoryState = [entry];
                if (window.userData) window.userData.subscription_history = [entry];
                if (window.parent && window.parent.userData) window.parent.userData.subscription_history = [entry];
                if (window.parent) window.parent._cosmoPurchaseHistoryState = [entry];
                if (window.top) window.top._cosmoPurchaseHistoryState = [entry];

                try {
                    localStorage.removeItem(STORAGE_KEY);
                    localStorage.removeItem('cosmo_crypt_purchase_history');
                } catch (e) { }

                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: STORAGE_KEY, val: data } }));
                if (typeof window.syncParentSubscription === 'function') window.syncParentSubscription();
            } catch (e) {
                console.warn('[CosmoCryptSub] restoreHistoryFromCloud failed:', e);
            }
        }

        // ── Reset plan to Element in Firebase ──
        async function resetPlan(reason) {
            const user = _user();
            if (!user) {
                _clearCache();
                localStorage.setItem(PLAN_KEY, 'element');
                localStorage.removeItem('cosmo_crypt_purchase_history');
                localStorage.removeItem('cosmo_crypt_purchase_history');
                localStorage.removeItem('premiumActive');
                _applyFoundation('reset_no_login');
                return { success: true, source: 'local_only' };
            }
            try {
                const previousData = getPlanData();
                const previousPlan = previousData.plan;
                const userSnap = await getDocFromServer(doc(db, 'users', user.uid));
                const currentResetCount = (userSnap.exists() && userSnap.data().subscription) ? (userSnap.data().subscription.resetCount || 0) : 0;
                const resetRecord = {
                    plan: 'element',
                    status: 'reset',
                    purchasedAt: null,
                    purchasedAtMs: null,
                    expiresAt: null,
                    expiresAtMs: null,
                    durationDays: null,
                    paymentId: null,
                    orderId: null,
                    amount: null,
                    gateway: null,
                    lastResetAt: _isoNow(),
                    resetCount: currentResetCount + 1,
                    lastVerifiedAt: _isoNow(),
                };
                await setDoc(doc(db, 'users', user.uid), { subscription: resetRecord }, { merge: true });
                await _logHistory(user.uid, 'reset', _buildSubObj({ plan: 'element', status: 'active', expiresAtMs: 0 }), previousPlan, reason || 'Manual reset');

                // Invalidate past active records in subscriptionHistory collection
                try {
                    const histSnap = await getDocs(collection(db, 'users', user.uid, 'subscriptionHistory'));
                    for (const docSnap of histSnap.docs) {
                        try {
                            await deleteDoc(docSnap.ref);
                        } catch (delErr) { }
                    }
                } catch (delHistErr) { }

                if (typeof window.addSuiteNotification === 'function') {
                    window.addSuiteNotification('account', 'Subscription Downgraded', 'Your plan has been reset/downgraded to Element.', { 'New Plan': 'Element', 'Status': 'Active' });
                }

                // Clear local legacy data
                _clearCache();
                localStorage.removeItem('cosmo_crypt_purchase_history');
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem('premiumActive');
                localStorage.removeItem('had_premium_plan');
                localStorage.removeItem('user_subscription');
                localStorage.removeItem('last_notified_subscription');
                localStorage.removeItem(PLAN_KEY);
                if (typeof originalRemoveItem === 'function') {
                    originalRemoveItem.call(localStorage, 'cosmo_crypt_purchase_history');
                    originalRemoveItem.call(localStorage, STORAGE_KEY);
                    originalRemoveItem.call(localStorage, 'premiumActive');
                    originalRemoveItem.call(localStorage, 'had_premium_plan');
                    originalRemoveItem.call(localStorage, 'user_subscription');
                    originalRemoveItem.call(localStorage, 'last_notified_subscription');
                    originalRemoveItem.call(localStorage, PLAN_KEY);
                }
                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_crypt_purchase_history', val: null } }));
                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: STORAGE_KEY, val: null } }));
                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: 'element', newValue: 'element' } }));
                window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: 'false' } }));
                window.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: 'element' } }));
                window.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: 'element' } }));

                _applyFoundation('reset');
                if (typeof window.pop === 'function') window.pop('Plan reset to Element.', 'info');
                return { success: true };
            } catch (e) {
                console.error('[CosmoCryptSub] resetPlan failed:', e);
                return { success: false, error: e.message };
            }
        }

        // ── Fetch plan history from Firestore ──
        async function getHistory() {
            const user = _user();
            if (!user) return [];
            try {
                const histSnap = await getDocs(collection(db, 'users', user.uid, 'subscriptionHistory'));
                const records = histSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                records.sort((a, b) => (b.timestampMs || 0) - (a.timestampMs || 0));
                try { localStorage.setItem(HISTORY_CACHE_KEY, JSON.stringify(records)); } catch (e) { }
                return records;
            } catch (e) {
                console.warn('[CosmoCryptSub] Failed to fetch history:', e);
                try { return JSON.parse(localStorage.getItem(HISTORY_CACHE_KEY) || '[]'); } catch (e2) { return []; }
            }
        }

        // ── Main entry point: called on login / app start ──
        async function verifyAndApply() {
            const user = _user();
            if (user) {
                await syncFromFirebase();
            } else {
                _applyFoundation('no_login');
            }
        }

        // ── Clear local cache (on logout) ──
        async function clearLocalCache() {
            _clearCache();
            localStorage.removeItem(PLAN_KEY);
            localStorage.removeItem('premiumActive');
            localStorage.removeItem('had_premium_plan');
            localStorage.removeItem('vault_usage');
            localStorage.removeItem('user_choices');
            localStorage.removeItem('app_lock_config');
            localStorage.removeItem('last_notified_subscription');
            localStorage.removeItem('userStoreEnc');
            localStorage.removeItem('userStore');
            localStorage.removeItem('cosmo_crypt_purchase_history');
            localStorage.removeItem('cosmo_user_plan');
            localStorage.removeItem('current_plan');
            localStorage.removeItem('current_suite_view');
            localStorage.removeItem('user_subscription');
            try { sessionStorage.setItem('current_suite_view', 'landing'); } catch (e) { }

            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'vault_usage', val: '0' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: 'element', newValue: 'element' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: 'false' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'had_premium_plan', val: 'false' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'last_notified_subscription', val: 'element' } }));
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_crypt_purchase_history', val: null } }));
            window.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: 'element' } }));
            window.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: 'element' } }));

            if (typeof renderHistory === 'function') renderHistory();
            if (typeof renderVaultHistory === 'function') renderVaultHistory();
            if (typeof updateHistoryUI === 'function') updateHistoryUI();
        }

        // ── Expose public API ──
        return {
            getActivePlan,
            getPlanData,
            isPlanActive,
            hadPremiumPlan,
            getDaysRemaining,
            getTimeLeftString,
            syncFromFirebase,
            savePlanToFirebase,
            resetPlan,
            getHistory,
            restoreHistoryFromCloud,
            verifyAndApply,
            clearLocalCache
        };
    })(),
    writable: false, configurable: false
});

// Backward compatibility alias for cosmoSubscription
Object.defineProperty(window, 'cosmoSubscription', {
    get: function () { return window.CosmoCryptSubscription; },
    configurable: false
});

// Protect global plan references from console tampering
try {
    Object.defineProperty(window, 'COSMO_CRYPT_PLAN', {
        get: function () {
            return (window.CosmoCryptSubscription && typeof window.CosmoCryptSubscription.getPlanData === 'function')
                ? window.CosmoCryptSubscription.getPlanData()
                : { plan: 'element', status: 'active', isActive: true, had_premium_plan: false };
        },
        set: function () {
            console.warn('[CosmoCrypt Security] Mutation of COSMO_CRYPT_PLAN is strictly forbidden. Managed exclusively by Firebase.');
        },
        configurable: false
    });
    Object.defineProperty(window, 'cosmo_PLAN', {
        get: function () { return window.COSMO_CRYPT_PLAN; },
        set: function () {
            console.warn('[CosmoCrypt Security] Mutation of cosmo_PLAN is strictly forbidden. Managed exclusively by Firebase.');
        },
        configurable: false
    });
} catch (e) { }

// Auto verify and restore plan tier on load
try {
    if (window.CosmoCryptSubscription && typeof window.CosmoCryptSubscription.verifyAndApply === 'function') {
        window.CosmoCryptSubscription.verifyAndApply();
    }
} catch (e) { }

// Listen for plan changed events to sync legacy keys
window.addEventListener('cosmoCryptPlanChanged', (e) => {
    if (e.detail && e.detail.plan) {
        if (typeof window.syncParentSubscription === 'function') {
            window.syncParentSubscription();
        }
    }
});
window.addEventListener('cosmoPlanChanged', (e) => {
    if (e.detail && e.detail.plan) {
        if (typeof window.syncParentSubscription === 'function') {
            window.syncParentSubscription();
        }
    }
});

// 15-MINUTE SECURITY HEARTBEAT (also verifies subscription)
setInterval(() => {
    window.syncProfileWithCloud();
    if (window.cosmoSubscription && auth.currentUser) {
        window.cosmoSubscription.syncFromFirebase();
    }
}, 15 * 60 * 1000);

// Persistence & Sync Listener
onAuthStateChanged(auth, async (user) => {
    // Update desktop & mobile auth buttons text
    const desktopBtn = document.getElementById('landing-nav-signup');
    const mobileBtn = document.getElementById('mobile-nav-signup');
    if (user) {
        dismissGoogleOneTap();
        sessionStorage.setItem('__cosmo_session_active', 'true');
        if (user.providerData && user.providerData.length > 0) {
            const pId = user.providerData[0].providerId;
            if (pId === 'google.com' && !sessionStorage.getItem('__cc_auth_provider')) {
                sessionStorage.setItem('__cc_auth_provider', 'google.com');
            } else if (pId === 'password' && !sessionStorage.getItem('__cc_auth_provider')) {
                sessionStorage.setItem('__cc_auth_provider', 'password');
            } else if (pId === 'phone' && !sessionStorage.getItem('__cc_auth_provider')) {
                sessionStorage.setItem('__cc_auth_provider', 'phone');
            }
        }
        if (desktopBtn) desktopBtn.textContent = 'Account';
        if (mobileBtn) {
            const textEl = mobileBtn.querySelector('.id-text');
            const descEl = mobileBtn.querySelector('.id-desc');
            if (textEl) textEl.textContent = 'Account';
            if (descEl) descEl.textContent = 'Manage your credentials';
        }

        // 🌟 Adaptive Enclave Personalization Sync (with Custom Account Profile Priority)
        try {
            window.dispatchEvent(new CustomEvent('cosmo_auth_state_changed', { detail: { user, loggedIn: true } }));
            window.dispatchEvent(new CustomEvent('cosmoProfileUpdated', { detail: { user, loggedIn: true } }));
        } catch (e) { }
        if (typeof window.updateLandingPersonalization === 'function') {
            let cachedProfile = null;
            try {
                const rawStore = localStorage.getItem('userStore');
                if (rawStore) cachedProfile = JSON.parse(rawStore);
            } catch (e) { }
            window.updateLandingPersonalization(user, cachedProfile);
        }

        // 🛡️ Set panic ack in sessionStorage BEFORE lockout listener
        const earlyAck = Math.max(
            parseInt(sessionStorage.getItem('__cc_last_panic_ack') || '0', 10),
            window._lastPanicAck || 0,
            Date.now()
        ).toString();
        window._lastPanicAck = parseInt(earlyAck, 10);
        try { sessionStorage.setItem('__cc_last_panic_ack', earlyAck); } catch (e) { }
        try { localStorage.removeItem('__cc_last_panic_ack'); } catch (e) { }

        // 🔄 Reset panic state in Firestore on successful login so subsequent panic wipes can cleanly trigger globally
        try {
            updateDoc(doc(db, "users", user.uid), {
                panicWipeAt: 0,
                panicEpoch: 0,
                forceLogoutAll: false,
                lastLoginDevice: "Active Session"
            }).catch(() => { });
            setDoc(doc(db, "security_lockouts", user.uid), {
                panicWipeAt: 0,
                panicEpoch: 0,
                forceLogoutAll: false,
                reason: null
            }, { merge: true }).catch(() => { });
            setDoc(doc(db, "users", user.uid, "security", "lockout_status"), {
                panicWipeAt: 0,
                panicEpoch: 0,
                forceLogoutAll: false,
                reason: null
            }, { merge: true }).catch(() => { });
        } catch (e) { }

        // 🔄 Real-Time Cross-Device Security Lockout Sync
        if (typeof window.initLockoutRealtimeSync === 'function') {
            window.initLockoutRealtimeSync(user);
        }
        if (typeof window.loadLockoutFromCloud === 'function') {
            window.loadLockoutFromCloud();
        }
    } else {
        if (desktopBtn) desktopBtn.textContent = 'Sign Up';
        if (mobileBtn) {
            const textEl = mobileBtn.querySelector('.id-text');
            const descEl = mobileBtn.querySelector('.id-desc');
            if (textEl) textEl.textContent = 'Sign Up';
            if (descEl) descEl.textContent = 'Create registry credentials';
        }

        // 🌟 Reset Adaptive Enclave Personalization to Guest State
        try {
            window.dispatchEvent(new CustomEvent('cosmo_auth_state_changed', { detail: { user: null, loggedIn: false } }));
            window.dispatchEvent(new CustomEvent('cosmoProfileUpdated', { detail: { user: null, loggedIn: false } }));
        } catch (e) { }
        if (typeof window.updateLandingPersonalization === 'function') {
            window.updateLandingPersonalization(null, null);
        }

        if (typeof window.initLockoutRealtimeSync === 'function') {
            window.initLockoutRealtimeSync(null);
        }
    }

    if (user) {
        // 🔐 2-Step Verification Check: Prevent premature dashboard access if 2FA is active
        try {
            const userSnap = await getDoc(doc(db, "users", user.uid));
            if (userSnap.exists()) {
                const cloudData = userSnap.data();
                if (cloudData.two_factor_enabled === true) {
                    const is2FAVerified = sessionStorage.getItem('__cc_2fa_passed_' + user.uid) === 'true';
                    if (!is2FAVerified) {
                        window.__pending2FAUser = {
                            user,
                            profileData: cloudData,
                            authProvider: user.providerData[0]?.providerId || 'password'
                        };
                        localStorage.removeItem('userStoreEnc');

                        // Keep navigation buttons in signed-out / sign up state
                        if (desktopBtn) desktopBtn.textContent = 'Sign Up';
                        if (mobileBtn) {
                            const textEl = mobileBtn.querySelector('.id-text');
                            if (textEl) textEl.textContent = 'Sign Up';
                        }

                        // Inform Account Iframe to open 2FA Challenge immediately
                        const iframes = document.querySelectorAll('iframe');
                        let challenged = false;
                        iframes.forEach(ifr => {
                            try {
                                if (ifr.contentWindow && typeof ifr.contentWindow.openTwoFactorChallenge === 'function') {
                                    ifr.contentWindow.openTwoFactorChallenge(cloudData.two_factor_type || 'totp', user.email);
                                    challenged = true;
                                }
                            } catch (crossOriginErr) {
                                // Safely ignore cross-origin iframes (e.g. Google One Tap GSI iframe, reCAPTCHA)
                            }
                        });

                        if (!challenged && typeof window.openTwoFactorChallenge === 'function') {
                            try {
                                window.openTwoFactorChallenge(cloudData.two_factor_type || 'totp', user.email);
                                challenged = true;
                            } catch (e) { }
                        }

                        if (!challenged) {
                            if (typeof window.enterCosmoCryptApp === 'function') {
                                window.enterCosmoCryptApp('account');
                            } else if (typeof window.switchView === 'function') {
                                window.switchView('account');
                            }
                        }
                        return;
                    }
                }
            }
        } catch (e) {
            console.warn("2FA Session check notice:", e);
        }

        // 🔐 If 2-Step Verification challenge is pending, hold session and prevent premature dashboard unlock
        if (window.__pending2FAUser && window.__pending2FAUser.user && window.__pending2FAUser.user.uid === user.uid) {
            return;
        }

        // Ensure terms fields on main user doc AND terms subcollection document exist in Firestore
        try {
            const userDocRef = doc(db, "users", user.uid);
            getDoc(userDocRef).then((uSnap) => {
                if (uSnap.exists()) {
                    const uData = uSnap.data();
                    if (typeof window.updateLandingPersonalization === 'function') {
                        window.updateLandingPersonalization(user, uData);
                    }
                    if (!uData.terms || uData.terms !== 'ALLOW') {
                        updateDoc(userDocRef, {
                            terms: 'ALLOW',
                            terms_accepted: true,
                            terms_accepted_at: uData.joined || getStandard24HDateTime() || new Date().toISOString(),
                            terms_statement: "I confirm that I am at least 18 years of age, agree to the Terms of Service, and understand that Cosmo Crypt uses Zero-Knowledge encryption. I am solely responsible for securing my master credentials, and Cosmo Crypt cannot recover lost passwords or vault keys."
                        }).catch(() => { });
                    }
                }
            }).catch(() => { });

            const termsDocRef = doc(db, "users", user.uid, "terms", "agreement");
            getDoc(termsDocRef).then((termsSnap) => {
                if (!termsSnap.exists()) {
                    setDoc(termsDocRef, {
                        status: "ALLOW",
                        accepted: true,
                        accepted_at: getStandard24HDateTime() || new Date().toISOString(),
                        statement: "I confirm that I am at least 18 years of age, agree to the Terms of Service, and understand that Cosmo Crypt uses Zero-Knowledge encryption. I am solely responsible for securing my master credentials, and Cosmo Crypt cannot recover lost passwords or vault keys."
                    }).catch(() => { });
                }
            }).catch(() => { });
        } catch (tErr) { }

        // Check if account switch occurred
        const lastUid = localStorage.getItem('last_active_uid');
        if (lastUid && lastUid !== user.uid) {
            console.warn("⚠️ Sync Alert: User switch detected. Clearing previous user cache.");
            localStorage.removeItem('userStoreEnc');
            localStorage.removeItem('userProfileImg');
            localStorage.removeItem('cosmo_analytics');
            localStorage.removeItem('cosmo_pdf_downloads_count');
            localStorage.removeItem('cosmo_pdf_encrypted_downloads_count');
            window.userData = null;
            window._cosmoAnalyticsCloudState = null;
            window._cosmoPdfTelemetryState = null;
            if (window.parent) {
                window.parent.userData = null;
                window.parent._cosmoAnalyticsCloudState = null;
                window.parent._cosmoPdfTelemetryState = null;
            }
            if (window.cosmoSubscription) {
                await window.cosmoSubscription.clearLocalCache();
            }
            localStorage.removeItem('user_subscription');
            window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: 'element' } }));
        }
        // Always proactively zero-out any legacy localStorage keys and phone locks
        try {
            localStorage.removeItem('cosmo_analytics');
            localStorage.removeItem('cosmo_pdf_downloads_count');
            localStorage.removeItem('cosmo_pdf_encrypted_downloads_count');
            localStorage.removeItem('vault_usage');
            localStorage.removeItem('current_suite_view');
            localStorage.removeItem('cosmo_crypt_purchase_history');
            localStorage.removeItem('cosmocrypt_sub_history');
            localStorage.removeItem('__cc_last_panic_ack');
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const k = localStorage.key(i);
                if (k && (k.startsWith('_cosmo_phone_lock_') || k === '__cc_last_panic_ack')) {
                    localStorage.removeItem(k);
                }
            }
        } catch (e) { }
        localStorage.setItem('last_active_uid', user.uid);

        if (localStorage.getItem('explicit_logout') === 'true') {
            if (window.userSnapshotListener) {
                try { window.userSnapshotListener(); } catch (e) { }
                window.userSnapshotListener = null;
            }
            await signOut(auth);
            return;
        }

        // ── UNIVERSAL 18+ ELIGIBILITY CHECKER ──
        window.isUserEligibleAdult = function () {
            const ud = window.userData || (window.parent && window.parent.userData) || (window.top && window.top.userData);
            if (!ud) return true;
            if (ud.age !== undefined && ud.age !== null && ud.age !== '') {
                const a = parseFloat(ud.age);
                if (!isNaN(a) && a < 18) return false;
            }
            if (ud.dob) {
                const dobDate = new Date(ud.dob);
                if (!isNaN(dobDate.getTime())) {
                    const ageFromDob = (Date.now() - dobDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
                    if (ageFromDob < 18) return false;
                }
            }
            return true;
        };

        // ── COSMO TEMPLATE CLOUD STORE MANAGER (FIREBASE ONLY, ZERO LOCALSTORAGE) ──
        window.CosmoTemplateStore = {
            KEY_ALIAS_MAP: {
                // 1. Node Infrastructure (league)
                'cosmo_crypt_node_infrastructure_v1': 'node_infrastructure',
                'cosmo_crypt_node_infrastructure_favorites_v1': 'node_infrastructure_favorites',

                // 2. Financial Credentials (banks)
                'cosmo_crypt_financial_credentials_v1': 'financial_credentials',
                'cosmo_crypt_financial_credentials_favorites_v1': 'financial_credentials_favorites',
                'banking_credentials': 'financial_credentials',
                'banking_credentials_favorites': 'financial_credentials_favorites',

                // 3. Payment Gateways (payment)
                'cosmo_crypt_payment_gateways_v1': 'payment_gateways',
                'cosmo_crypt_payment_gateways_favorites_v1': 'payment_gateways_favorites',
                'payment_vault_credentials_v1': 'payment_gateways',
                'payment_vault_favorites_v1': 'payment_gateways_favorites',
                'cosmo_crypt_banks_v1': 'financial_credentials',
                'cosmo_crypt_banks_favorites_v1': 'financial_credentials_favorites',

                // 4. Developer Endpoints (api-keys)
                'cosmo_crypt_developer_endpoints_v1': 'developer_endpoints',
                'cosmo_crypt_developer_endpoints_favorites_v1': 'developer_endpoints_favorites',
                'cosmo_crypt_api_keys_v6': 'developer_endpoints',
                'cosmo_crypt_api_favorites_v6': 'developer_endpoints_favorites',
                'api_vault_credentials': 'developer_endpoints',
                'api_vault_favorites': 'developer_endpoints_favorites',

                // 5. DNS Management (domain)
                'cosmo_crypt_dns_management_v1': 'dns_management',
                'cosmo_crypt_dns_management_favorites_v1': 'dns_management_favorites',
                'cosmo_crypt_domain_vault_v1': 'dns_management',
                'security_keys': 'dns_management',

                // 6. Workspace Integration (google)
                'cosmo_crypt_workspace_integration_v1': 'workspace_integration',
                'cosmo_crypt_workspace_integration_favorites_v1': 'workspace_integration_favorites',
                'google_vault_credentials_v1': 'workspace_integration',
                'google_vault_favorites_v1': 'workspace_integration_favorites',

                // 7. Merchant Identities (apps)
                'cosmo_crypt_merchant_identities_v1': 'merchant_identities',
                'cosmo_crypt_merchant_identities_favorites_v1': 'merchant_identities_favorites',
                'cosmo_crypt_apps_v1': 'merchant_identities',
                'cosmo_crypt_apps_favorites_v1': 'merchant_identities_favorites',

                // 8. National Identity (national)
                'cosmo_crypt_national_identity_v1': 'national_identity',
                'cosmo_crypt_national_identity_favorites_v1': 'national_identity_favorites',
                'cosmo_crypt_vault_data_tabbed_v4': 'national_identity',
                'personal_identities': 'national_identity',
                'personal_identities_favorites': 'national_identity_favorites',
                'vault_favorites': 'national_identity_favorites',

                // 9. Personal Documents Vault / Identity Vault (generator, qr-creator)
                'cosmo_crypt_identity_vault_documents_v1': 'identity_vault_documents',
                'cosmo_crypt_identity_vault_categories_v1': 'identity_vault_categories',
                'payvault_documents': 'identity_vault_documents',
                'payvault_categories': 'identity_vault_categories',
                'environment_config_documents': 'identity_vault_documents',
                'environment_config_categories': 'identity_vault_categories',

                // 10. Encrypted Partitions (encrypted-partitions)
                'cosmo_crypt_encrypted_partitions_notes_v1': 'encrypted_partitions_notes',
                'cosmo_encrypted_partitions_notes_v1': 'encrypted_partitions_notes',
                'cosmo_crypt_encrypted_partitions_pin_v1': 'encrypted_partitions_pin',
                'cosmo_encrypted_partitions_pin_v1': 'encrypted_partitions_pin',
                'cosmo_crypt_encrypted_partitions_categories_v1': 'encrypted_partitions_categories',
                'cosmo_encrypted_partitions_categories_v1': 'encrypted_partitions_categories',

                // 11. System Encryption (locks, device, decoder)
                'cosmo_crypt_system_encryption_v1': 'system_encryption',
                'cosmo_crypt_system_encryption_favorites_v1': 'system_encryption_favorites',
                'locks_vault_credentials_v1': 'system_encryption',
                'locks_vault_favorites_v1': 'system_encryption_favorites'
            },
            getCleanKey(key) {
                return this.KEY_ALIAS_MAP[key] || key;
            },
            TEMPLATE_KEYS: [
                'node_infrastructure', 'node_infrastructure_favorites',
                'cosmo_crypt_node_infrastructure_v1', 'cosmo_crypt_node_infrastructure_favorites_v1',
                'financial_credentials', 'financial_credentials_favorites',
                'cosmo_crypt_financial_credentials_v1', 'cosmo_crypt_financial_credentials_favorites_v1',
                'payment_gateways', 'payment_gateways_favorites',
                'cosmo_crypt_payment_gateways_v1', 'cosmo_crypt_payment_gateways_favorites_v1',
                'developer_endpoints', 'developer_endpoints_favorites',
                'cosmo_crypt_developer_endpoints_v1', 'cosmo_crypt_developer_endpoints_favorites_v1',
                'dns_management', 'dns_management_favorites',
                'cosmo_crypt_dns_management_v1', 'cosmo_crypt_dns_management_favorites_v1',
                'workspace_integration', 'workspace_integration_favorites',
                'cosmo_crypt_workspace_integration_v1', 'cosmo_crypt_workspace_integration_favorites_v1',
                'merchant_identities', 'merchant_identities_favorites',
                'cosmo_crypt_merchant_identities_v1', 'cosmo_crypt_merchant_identities_favorites_v1',
                'national_identity', 'national_identity_favorites',
                'cosmo_crypt_national_identity_v1', 'cosmo_crypt_national_identity_favorites_v1',
                'identity_vault_documents', 'identity_vault_categories',
                'cosmo_crypt_identity_vault_documents_v1', 'cosmo_crypt_identity_vault_categories_v1',
                'encrypted_partitions_notes', 'encrypted_partitions_pin', 'encrypted_partitions_categories',
                'cosmo_crypt_encrypted_partitions_notes_v1', 'cosmo_crypt_encrypted_partitions_pin_v1', 'cosmo_crypt_encrypted_partitions_categories_v1',
                'system_encryption', 'system_encryption_favorites',
                'cosmo_crypt_system_encryption_v1', 'cosmo_crypt_system_encryption_favorites_v1',
                'preferences', 'preferences_favorites',
                'environment_config_documents', 'environment_config_categories',
                'security_keys', 'personal_identities', 'personal_identities_favorites',
                'banking_credentials', 'banking_credentials_favorites',
                'api_vault_credentials', 'api_vault_favorites',
                'payment_vault_credentials_v1', 'payment_vault_favorites_v1',
                'cosmo_crypt_domain_vault_v1',
                'cosmo_crypt_apps_v1', 'cosmo_crypt_apps_favorites_v1',
                'payvault_documents', 'payvault_categories',
                'cosmo_crypt_banks_v1', 'cosmo_crypt_banks_favorites_v1',
                'cosmo_crypt_api_keys_v6', 'cosmo_crypt_api_favorites_v6',
                'google_vault_credentials_v1', 'google_vault_favorites_v1',
                'cosmo_crypt_vault_data_tabbed_v4',
                'locks_vault_credentials_v1', 'locks_vault_favorites_v1',
                'vault_favorites'
            ],

            purgeLocalStorage() {
                try {
                    this.TEMPLATE_KEYS.forEach(k => localStorage.removeItem(k));
                    localStorage.removeItem('google_vault_credentials_v1');
                    localStorage.removeItem('google_vault_favorites_v1');
                    localStorage.removeItem('google_vault_keys_reset_v2');
                    localStorage.removeItem('cosmo_crypt_banks_reset_v4');
                    localStorage.removeItem('cosmo_workspace_integration_notes_v1');
                    localStorage.removeItem('cosmo_workspace_integration_pin_v1');
                    localStorage.removeItem('cosmo_workspace_integration_categories_v1');
                    for (let i = 1; i <= 40; i++) {
                        localStorage.removeItem('cosmo_crypt_vault_data_tabbed_v' + i);
                    }
                } catch (e) { }
                this.purgeLegacyCloudKeys();
            },
            async purgeLegacyCloudKeys() {
                const user = (typeof auth !== 'undefined' && auth.currentUser) ? auth.currentUser : null;
                if (!user || typeof deleteDoc !== 'function' || typeof doc !== 'function' || typeof db === 'undefined') return;

                const LEGACY_KEYS = [
                    'cosmo_workspace_integration_notes_v1',
                    'cosmo_workspace_integration_pin_v1',
                    'cosmo_workspace_integration_categories_v1'
                ];

                for (const lk of LEGACY_KEYS) {
                    try {
                        const legacyDocRef = doc(db, "users", user.uid, "template_vaults", lk);
                        await deleteDoc(legacyDocRef);
                        if (window.userData && window.userData.template_vaults) {
                            delete window.userData.template_vaults[lk];
                        }

                    } catch (e) { }
                }
            },
            get(key, defaultVal = null) {
                // In Duress Mode, return defaultVal so templates load safe default mock cards
                try {
                    if (sessionStorage.getItem('__cc_duress_active') === 'true' || window.__cc_is_duress === true) {
                        return defaultVal;
                    }
                } catch (e) { }

                const cleanKey = this.getCleanKey(key);
                const ud = window.userData || (window.parent && window.parent.userData) || (window.top && window.top.userData) || {};
                const tv = ud.template_vaults || {};

                const fallbacks = [cleanKey, key];
                if (cleanKey === 'dns_management') fallbacks.push('security_keys', 'cosmo_crypt_domain_vault_v1');
                if (cleanKey === 'national_identity') fallbacks.push('personal_identities', 'locks_vault_credentials_v1');
                if (cleanKey === 'developer_endpoints') fallbacks.push('api_vault_credentials', 'cosmo_crypt_api_keys_v6');
                if (cleanKey === 'payment_gateways') fallbacks.push('banking_credentials', 'cosmo_crypt_banks_v1');

                let val = null;
                for (const k of fallbacks) {
                    if (tv[k] !== undefined && tv[k] !== null) {
                        val = tv[k];
                        break;
                    }
                }

                if (val !== null) {
                    if (typeof val === 'string' && (val.startsWith('cc_gcm256:') || val.startsWith('U2FsdGVkX1'))) {
                        return defaultVal;
                    }
                    if (typeof val === 'object' && val !== null && val.data !== undefined && !Array.isArray(val)) {
                        val = val.data;
                    }
                    try {
                        return JSON.parse(JSON.stringify(val));
                    } catch (e) {
                        return val;
                    }
                }
                if (!window._isDefaultMockData) window._isDefaultMockData = {};
                window._isDefaultMockData[cleanKey] = true;
                return defaultVal;
            },
            async processStorageFiles(cleanKey, cleanData, userUid) {
                if (!window.suiteStorage || !window.suiteStorageMethods || !cleanData || typeof cleanData !== 'object') {
                    return cleanData;
                }
                const { ref, uploadString, getDownloadURL } = window.suiteStorageMethods;

                const processNode = async (node, pathStr) => {
                    if (!node || typeof node !== 'object') return node;
                    if (Array.isArray(node)) {
                        for (let i = 0; i < node.length; i++) {
                            node[i] = await processNode(node[i], `${pathStr}_${i}`);
                        }
                        return node;
                    }
                    for (const prop of Object.keys(node)) {
                        const val = node[prop];
                        if (typeof val === 'string' && val.startsWith('data:')) {
                            try {
                                const isPdf = val.includes('data:application/pdf');
                                const ext = isPdf ? 'pdf' : 'png';
                                const fileId = `${cleanKey}_${pathStr}_${prop}_${Date.now()}.${ext}`;
                                const storageRef = ref(window.suiteStorage, `users/${userUid}/files/${cleanKey}/${fileId}`);
                                await uploadString(storageRef, val, 'data_url');
                                const downloadUrl = await getDownloadURL(storageRef);
                                node[prop] = downloadUrl;
                                console.log(`☁️ [Firebase Storage] Uploaded file to Storage: ${downloadUrl}`);
                            } catch (uploadErr) {
                                console.warn(`⚠️ [Firebase Storage] Storage upload skipped, preserving data in Firestore:`, uploadErr);
                            }
                        } else if (typeof val === 'object' && val !== null) {
                            node[prop] = await processNode(val, `${pathStr}_${prop}`);
                        }
                    }
                    return node;
                };

                try {
                    return await processNode(cleanData, 'root');
                } catch (e) {
                    console.warn(`[CosmoTemplateStore] File processing fallback:`, e);
                    return cleanData;
                }
            },
            notifyUpdate(cleanKey, cleanData) {
                const detail = { key: cleanKey, data: cleanData };
                try { window.dispatchEvent(new CustomEvent('cosmoTemplateStoreUpdated', { detail })); } catch (e) { }
                try { if (window.parent && window.parent !== window) window.parent.dispatchEvent(new CustomEvent('cosmoTemplateStoreUpdated', { detail })); } catch (e) { }
                try {
                    document.querySelectorAll('iframe').forEach(iframe => {
                        if (iframe.contentWindow) iframe.contentWindow.dispatchEvent(new CustomEvent('cosmoTemplateStoreUpdated', { detail }));
                    });
                } catch (e) { }
            },
            async set(key, rawData, isExplicitUserAction = true) {
                // In Duress Mode, do NOT save to cloud or real user data!
                try {
                    if (sessionStorage.getItem('__cc_duress_active') === 'true' || window.__cc_is_duress === true) {
                        return;
                    }
                } catch (e) { }

                // 🚫 Universal 18+ Vault Write Shield (silent backend protection, UI toast shown on user click)
                if (typeof window.isUserEligibleAdult === 'function' && !window.isUserEligibleAdult()) {
                    return false;
                }
                const cleanKey = this.getCleanKey(key);

                if (!window._isDefaultMockData) window._isDefaultMockData = {};
                window._isDefaultMockData[cleanKey] = false;

                let cleanData;
                try {
                    cleanData = JSON.parse(JSON.stringify(rawData));
                } catch (e) {
                    cleanData = rawData;
                }

                if (window.userData) {
                    if (!window.userData.template_vaults) window.userData.template_vaults = {};
                    window.userData.template_vaults[cleanKey] = cleanData;
                }

                if (window.parent && window.parent !== window && window.parent.userData) {
                    if (!window.parent.userData.template_vaults) window.parent.userData.template_vaults = {};
                    window.parent.userData.template_vaults[cleanKey] = cleanData;
                }

                this.notifyUpdate(cleanKey, cleanData);

                // ── Instantly sync card_limits & analytics section to Firebase at 0ms ──
                const CARD_DATA_KEYS = [
                    'node_infrastructure', 'financial_credentials', 'payment_gateways',
                    'developer_endpoints', 'dns_management', 'workspace_integration',
                    'merchant_identities', 'national_identity', 'identity_vault_documents',
                    'encrypted_partitions_notes', 'system_encryption'
                ];
                const isCardDataKey = CARD_DATA_KEYS.includes(cleanKey);
                if (isCardDataKey) {
                    if (typeof window.calculateAndSyncCardLimits === 'function') {
                        try {
                            window.calculateAndSyncCardLimits();
                        } catch (e) { }
                    }
                    if (typeof window.syncAnalyticsToCloud === 'function') {
                        try {
                            window.syncAnalyticsToCloud();
                        } catch (e) { }
                    }
                }

                const user = (typeof auth !== 'undefined' && auth.currentUser) ? auth.currentUser : null;

                if (!user) {
                    return;
                }

                this.processStorageFiles(cleanKey, cleanData, user.uid).then(async (processedData) => {
                    if (window.userData) {
                        if (!window.userData.template_vaults) window.userData.template_vaults = {};
                        window.userData.template_vaults[cleanKey] = processedData;
                    }

                    if (window.parent && window.parent !== window && window.parent.userData) {
                        if (!window.parent.userData.template_vaults) window.parent.userData.template_vaults = {};
                        window.parent.userData.template_vaults[cleanKey] = processedData;
                    }

                    this.notifyUpdate(cleanKey, processedData);

                    if (typeof setDoc === 'function' && typeof doc === 'function' && typeof db !== 'undefined') {
                        try {
                            // 🔒 ZERO-KNOWLEDGE AES-GCM-256 CLOUD ENCRYPTION
                            // Plaintext never enters Firebase: encrypted via hardware-accelerated WebCrypto (<0.5ms)
                            const encryptedCiphertext = await encryptVaultPayload(processedData, user);

                            const subDocRef = doc(db, "users", user.uid, "template_vaults", cleanKey);
                            await setDoc(subDocRef, {
                                data: encryptedCiphertext,
                                encrypted: true,
                                cipher: 'AES-GCM-256',
                                updatedAt: Date.now()
                            }, { merge: true }).catch(err => console.warn(`[CosmoTemplateStore] Subdoc save warning:`, err));

                            await setDoc(doc(db, "users", user.uid), {
                                template_vaults: { [cleanKey]: encryptedCiphertext }
                            }, { merge: true }).catch(err => console.warn(`[CosmoTemplateStore] Root doc save warning:`, err));

                        } catch (err) {
                            console.warn(`[CosmoTemplateStore] Cloud save warning for '${cleanKey}':`, err);
                        }
                    }

                    // ── Re-sync card_limits after Firebase writes complete ──
                    if (isCardDataKey && typeof window.calculateAndSyncCardLimits === 'function') {
                        try {
                            await window.calculateAndSyncCardLimits(user);
                        } catch (e) {
                            console.warn('[CosmoTemplateStore] Post-save card_limits sync error:', e);
                        }
                    }
                });
            }
        };
        window.CosmoTemplateStore.purgeLocalStorage();

        if (!user.email && window.__pendingPhoneVerification) {
            console.info("ℹ️ Phone OTP verification in progress — skipping user snapshot listener.");
            return;
        }

        // Record the login moment — any panic older than this is stale
        const loginMoment = Date.now();
        const existingAck = sessionStorage.getItem('__cc_last_panic_ack') || (window._lastPanicAck ? window._lastPanicAck.toString() : '0');
        const safeAck = Math.max(parseInt(existingAck, 10) || 0, loginMoment).toString();
        window._lastPanicAck = parseInt(safeAck, 10);
        try { sessionStorage.setItem('__cc_last_panic_ack', safeAck); } catch (e) { }
        try { localStorage.removeItem('__cc_last_panic_ack'); } catch (e) { }

        window.syncProfileWithCloud();
        // Verify subscription from Firebase on every login
        if (window.cosmoSubscription) {
            window.cosmoSubscription.verifyAndApply();
        }

        if (window.userSnapshotListener) {
            try { window.userSnapshotListener(); } catch (e) { }
            window.userSnapshotListener = null;
        }
        let isInitialSnapshot = true;
        let lastSyncedSnapshotDataStr = null;
        window.userSnapshotListener = onSnapshot(doc(db, "users", user.uid), async (snapshot) => {
            if (snapshot.exists()) {
                const freshData = snapshot.data();
                delete freshData.password;
                delete freshData.confirm;

                // 🚨 Real-time Multi-Device Nuclear Panic Kill-Switch Listener
                // Only react to NEW panics that happened AFTER this login session started.
                // Stale panic fields from a previous wipe must not re-trigger on login.
                if (freshData.panicWipeAt || freshData.panicEpoch || freshData.forceLogoutAll || freshData.lastLoginDevice === 'Revoked (Panic Mode)') {
                    const incomingPanicTs = freshData.panicWipeAt || freshData.panicEpoch || 0;
                    // Skip if no real timestamp exists (boolean-only fields from old wipe)
                    if (incomingPanicTs > 0) {
                        const lastPanicAck = Math.max(
                            parseInt(sessionStorage.getItem('__cc_last_panic_ack') || '0', 10),
                            window._lastPanicAck || 0
                        );
                        if (incomingPanicTs > lastPanicAck) {
                            console.warn("🚨 [GLOBAL PANIC DETECTED] Executing instant local browser zeroization on this device!");
                            const ackStr = incomingPanicTs.toString();
                            window._lastPanicAck = incomingPanicTs;
                            try { sessionStorage.setItem('__cc_last_panic_ack', ackStr); } catch (e) { }
                            try { localStorage.removeItem('__cc_last_panic_ack'); } catch (e) { }
                            if (typeof window.executePanicWipe === 'function') {
                                window.executePanicWipe(true);
                            } else if (window.parent && typeof window.parent.executePanicWipe === 'function') {
                                window.parent.executePanicWipe(true);
                            } else if (typeof window.triggerPanicWipe === 'function') {
                                window.triggerPanicWipe(true);
                            } else {
                                try { localStorage.clear(); } catch (e) { }
                                try { sessionStorage.clear(); } catch (e) { }
                                try { indexedDB.deleteDatabase('cosmocryptDB'); } catch (e) { }
                                try { caches.keys().then(keys => keys.forEach(k => caches.delete(k))); } catch (e) { }
                                window.location.replace('/');
                            }
                            return;
                        }
                    }
                }

                // 📱 Real-time Remote Multi-Device Deauthorization Listener
                // Skip if user explicitly logged out or a login is in progress
                const isExplicitLogout = localStorage.getItem('explicit_logout') === 'true' || window._userExplicitlyLoggedOut;
                const localDevId = localStorage.getItem('cosmo_device_id') || (window.cosmoDeviceManager && window.cosmoDeviceManager.getDeviceId ? window.cosmoDeviceManager.getDeviceId() : null);
                const isExplicitlyRevoked = !isInitialSnapshot && !isExplicitLogout && !window._cosmoLoginInProgress && localDevId && Array.isArray(freshData.revokedDeviceIds) && freshData.revokedDeviceIds.includes(localDevId);

                if (isExplicitlyRevoked) {
                    console.warn("🚨 [REMOTE DEAUTHORIZATION] This device has been explicitly revoked remotely.");

                    // 1. Immediately kill snapshot listener to avoid further loops or permission denials
                    if (window.userSnapshotListener) {
                        try { window.userSnapshotListener(); } catch (e) { }
                        window.userSnapshotListener = null;
                    }

                    // 2. Wipe in-memory user objects immediately
                    window.userData = null;
                    if (window.parent) window.parent.userData = null;

                    // 3. Clear storage tokens
                    try { sessionStorage.setItem('__cosmo_remote_logout_reason', 'This device was logged out remotely from another session.'); } catch (e) { }
                    try { localStorage.removeItem('userStoreEnc'); } catch (e) { }
                    try { sessionStorage.removeItem('__cosmo_session_active'); } catch (e) { }
                    try { localStorage.setItem('explicit_logout', 'true'); } catch (e) { }
                    try { localStorage.removeItem('cosmo_device_id'); } catch (e) { }

                    // 4. Sign out from Firebase Auth
                    if (typeof signOut === 'function') {
                        try { signOut(auth); } catch (e) { }
                    }

                    // 5. Broadcast instant deauthorization across all windows and iframes
                    const deauthDetail = { isLoggedIn: false, reason: 'remote_deauthorization' };
                    try { window.dispatchEvent(new CustomEvent('cosmoProfileUpdated', { detail: deauthDetail })); } catch (e) { }
                    try { window.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: deauthDetail })); } catch (e) { }
                    try { window.dispatchEvent(new CustomEvent('cosmo_remote_logout', { detail: deauthDetail })); } catch (e) { }

                    document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                        try {
                            if (ifr.contentWindow) {
                                ifr.contentWindow.userData = null;
                                try { ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoProfileUpdated', { detail: deauthDetail })); } catch (e) { }
                                try { ifr.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: deauthDetail })); } catch (e) { }
                                try { ifr.contentWindow.dispatchEvent(new CustomEvent('cosmo_remote_logout', { detail: deauthDetail })); } catch (e) { }
                                ifr.contentWindow.postMessage({ type: 'cosmo-remote-logout', reason: 'revoked' }, '*');
                            }
                        } catch (e) { }
                    });

                    // Proactively close current view to avoid stale dashboard or profile
                    try { if (typeof window.closeSPA === 'function') window.closeSPA(); } catch (e) { }

                    // 6. Render top-level high-visibility deauthorization modal
                    try {
                        const existingBanner = document.getElementById('cosmo-deauth-overlay');
                        if (existingBanner) existingBanner.remove();

                        const overlay = document.createElement('div');
                        overlay.id = 'cosmo-deauth-overlay';
                        overlay.style.cssText = "position:fixed;inset:0;z-index:9999999;background:rgba(10,10,12,0.85);backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'Space Grotesk',sans-serif;";
                        overlay.innerHTML = `
                          <div style="background:rgba(28,28,32,0.96);border:1px solid rgba(239,68,68,0.35);border-radius:24px;padding:32px;max-width:440px;width:100%;text-align:center;box-shadow:0 24px 60px rgba(0,0,0,0.6);color:#fff;">
                            <div style="width:56px;height:56px;border-radius:50%;background:rgba(239,68,68,0.15);border:1px solid rgba(239,68,68,0.3);color:#ef4444;display:flex;align-items:center;justify-content:center;margin:0 auto 18px auto;">
                              <span class="material-symbols-outlined" style="font-size:30px;">devices_off</span>
                            </div>
                            <h2 style="font-size:20px;font-weight:700;margin-bottom:8px;color:#fff;letter-spacing:-0.01em;">Device Deauthorized</h2>
                            <p style="font-size:14px;color:#a1a1aa;line-height:1.55;margin-bottom:24px;">This device session was terminated remotely from another authorized device. All active encryption keys and local credentials have been cleared.</p>
                            <button id="cosmo-deauth-login-btn" style="width:100%;padding:12px 20px;border-radius:12px;background:#9d1c31;color:#fff;border:1px solid #7c1223;font-size:14px;font-weight:600;cursor:pointer;transition:all .2s ease;">Log In Again</button>
                          </div>
                        `;
                        document.body.appendChild(overlay);

                        const btn = overlay.querySelector('#cosmo-deauth-login-btn');
                        if (btn) {
                            btn.addEventListener('click', () => {
                                overlay.remove();
                                if (typeof window.closeSPA === 'function') window.closeSPA();
                                if (typeof window.switchView === 'function') {
                                    window.switchView('account');
                                } else if (window.parent && typeof window.parent.switchView === 'function') {
                                    window.parent.switchView('account');
                                } else {
                                    window.location.href = '/account';
                                }
                            });
                        }
                    } catch (e) { }

                    return;
                }

                // Prevent local optimistic role spoofing/flashes until server confirms it
                if (snapshot.metadata.hasPendingWrites && freshData.role === 'owner') {
                    const currentLocal = window.userData || {};
                    freshData.role = currentLocal.role || 'user';
                }

                freshData.isVerified = true;
                const gPhoto = user.photoURL || (user.providerData && user.providerData[0] && user.providerData[0].photoURL) || null;
                if (gPhoto && !freshData.photoURL) {
                    freshData.photoURL = gPhoto;
                }

                // 🔐 Real-time multi-device subscription synchronization
                if (window.cosmoSubscription) {
                    window.cosmoSubscription.syncFromFirebase().catch(() => { });
                }

                // Purge any legacy vault_usage from localStorage
                try { localStorage.removeItem('vault_usage'); } catch (e) { }

                const oldDataStr = lastSyncedSnapshotDataStr || JSON.stringify(window.userData || {});
                const newDataStr = JSON.stringify(freshData);

                if (freshData.template_vaults && typeof freshData.template_vaults === 'object') {
                    const vKeys = Object.keys(freshData.template_vaults);
                    await Promise.all(vKeys.map(async (k) => {
                        try {
                            freshData.template_vaults[k] = await decryptVaultPayload(freshData.template_vaults[k], user);
                        } catch (e) { }
                    }));
                }

                if (window.userData && window.userData.uid === user.uid && window.userData.template_vaults) {
                    freshData.template_vaults = { ...window.userData.template_vaults, ...(freshData.template_vaults || {}) };
                }

                // Preserve in-memory settings during snapshot updates so active state isn't lost
                const existingSettings = (window.userData && window.userData.uid === user.uid && window.userData.settings) ||
                    (window.parent && window.parent.userData && window.parent.userData.uid === user.uid && window.parent.userData.settings) ||
                    (window.top && window.top.userData && window.top.userData.uid === user.uid && window.top.userData.settings);
                if (existingSettings) {
                    if (!freshData.settings) freshData.settings = {};
                    if (existingSettings.card_mutation_config && (!freshData.settings.card_mutation_config || (!freshData.settings.card_mutation_config.password && existingSettings.card_mutation_config.password))) {
                        freshData.settings.card_mutation_config = existingSettings.card_mutation_config;
                    }
                    if (existingSettings.secure_pdf_config && (!freshData.settings.secure_pdf_config || (!freshData.settings.secure_pdf_config.password && existingSettings.secure_pdf_config.password))) {
                        freshData.settings.secure_pdf_config = existingSettings.secure_pdf_config;
                    }
                }

                // 🔐 Proactive auto-migration of legacy plaintext passwords to PBKDF2 in Firestore
                if (freshData.settings) {
                    const saltFn = window.generateAppLockSalt || (window.parent && window.parent.generateAppLockSalt);
                    const hashFn = window.hashAppLockSecret || (window.parent && window.parent.hashAppLockSecret);
                    if (saltFn && hashFn) {
                        if (freshData.settings.card_mutation_config && freshData.settings.card_mutation_config.password && !freshData.settings.card_mutation_config.password.startsWith('pbkdf2_')) {
                            const salt = saltFn();
                            hashFn(freshData.settings.card_mutation_config.password, salt).then(hashed => {
                                const finalHashed = `pbkdf2_${salt}_${hashed}`;
                                freshData.settings.card_mutation_config.password = finalHashed;
                                updateDoc(doc(db, "users", user.uid), { "settings.card_mutation_config.password": finalHashed }).catch(() => { });
                            }).catch(() => { });
                        }
                        if (freshData.settings.secure_pdf_config && freshData.settings.secure_pdf_config.password && !freshData.settings.secure_pdf_config.password.startsWith('pbkdf2_')) {
                            const salt = saltFn();
                            hashFn(freshData.settings.secure_pdf_config.password, salt).then(hashed => {
                                const finalHashed = `pbkdf2_${salt}_${hashed}`;
                                freshData.settings.secure_pdf_config.password = finalHashed;
                                updateDoc(doc(db, "users", user.uid), { "settings.secure_pdf_config.password": finalHashed }).catch(() => { });
                            }).catch(() => { });
                        }
                    }
                }

                window.userData = freshData;
                if (window.parent && window.parent !== window) {
                    window.parent.userData = freshData;
                }

                if (typeof window.updateAllSettingsAuthCards === 'function') {
                    window.updateAllSettingsAuthCards();
                }
                if (typeof window.updateSettingsSecurePdfCardState === 'function') {
                    window.updateSettingsSecurePdfCardState();
                }
                if (typeof window.updateSettingsCardMutationCardState === 'function') {
                    window.updateSettingsCardMutationCardState();
                }
                if (typeof window.updateSettingsDeviceLoginsCardState === 'function') {
                    window.updateSettingsDeviceLoginsCardState();
                }
                if (typeof window.initSecurePdfModalUI === 'function') {
                    window.initSecurePdfModalUI();
                }
                if (typeof window.initCardMutationModalUI === 'function') {
                    window.initCardMutationModalUI();
                }
                document.querySelectorAll('.view-layer iframe, iframe').forEach(iframe => {
                    try {
                        if (iframe.contentWindow) {
                            iframe.contentWindow.userData = freshData;
                            iframe.contentWindow.postMessage({ type: 'login-success', isLoggedIn: true, user: freshData }, '*');
                            if (typeof iframe.contentWindow.switchToProfilePanel === 'function') {
                                iframe.contentWindow.switchToProfilePanel(freshData);
                            }
                            iframe.contentWindow.postMessage({ type: 'login-success', isLoggedIn: true, user: freshData }, '*');
                            if (typeof iframe.contentWindow.updateAllSettingsAuthCards === 'function') {
                                iframe.contentWindow.updateAllSettingsAuthCards();
                            }
                            if (typeof iframe.contentWindow.updateSettingsSecurePdfCardState === 'function') {
                                iframe.contentWindow.updateSettingsSecurePdfCardState();
                            }
                            if (typeof iframe.contentWindow.updateSettingsCardMutationCardState === 'function') {
                                iframe.contentWindow.updateSettingsCardMutationCardState();
                            }
                            if (typeof iframe.contentWindow.initSecurePdfModalUI === 'function') {
                                iframe.contentWindow.initSecurePdfModalUI();
                            }
                            if (typeof iframe.contentWindow.initCardMutationModalUI === 'function') {
                                iframe.contentWindow.initCardMutationModalUI();
                            }
                            if (typeof iframe.contentWindow.updateUserLoginStatusCard === 'function') {
                                iframe.contentWindow.updateUserLoginStatusCard();
                            }
                            const snapDevDetail = { activeDevices: freshData.activeDevices || [], revokedDeviceIds: freshData.revokedDeviceIds || [] };
                            try { iframe.contentWindow.dispatchEvent(new CustomEvent('cosmoUserDataUpdated', { detail: snapDevDetail })); } catch (e) { }
                            try { iframe.contentWindow.dispatchEvent(new CustomEvent('userDataUpdated', { detail: snapDevDetail })); } catch (e) { }
                            iframe.contentWindow.postMessage({ type: 'session-update', activeDevices: freshData.activeDevices || [] }, '*');
                            if (typeof iframe.contentWindow.initLiveSessions === 'function') {
                                iframe.contentWindow.initLiveSessions();
                            }
                        }
                    } catch (e) { }
                });

                const encrypted = CryptoJS.AES.encrypt(JSON.stringify(freshData), _getAppSecret()).toString();
                sessionStorage.setItem('userStoreEnc', encrypted); try { localStorage.removeItem('userStoreEnc'); } catch (e) { }

                // Real-time Avatar sync across all devices
                if (freshData.avatarUrl && freshData.avatarUrl !== 'none') {
                    localStorage.setItem('userProfileImg', freshData.avatarUrl);
                    if (typeof profileImgData !== 'undefined') profileImgData = freshData.avatarUrl;
                } else if (freshData.avatarUrl === 'none') {
                    localStorage.setItem('userProfileImg', 'none');
                    if (typeof profileImgData !== 'undefined') profileImgData = 'none';
                } else {
                    localStorage.removeItem('userProfileImg');
                    if (typeof profileImgData !== 'undefined') profileImgData = null;
                }

                if (typeof updateMomentumDash === 'function') updateMomentumDash();
                if (typeof updateAvatarUI === 'function') updateAvatarUI();

                // Check inside settings.app_lock_config, settings.mobile.app_lock_config, or app_lock_config
                let cloudConfig = (freshData.settings && freshData.settings.app_lock_config) ||
                    (freshData.settings && freshData.settings.mobile && freshData.settings.mobile.app_lock_config) ||
                    freshData.app_lock_config || null;

                // If user has local app lock but no cloud app lock, sync local to cloud
                if (!cloudConfig && window._0x83e2 && window._0x83e2.config) {
                    try {
                        const localConfig = window._0x83e2.config;
                        // Merge it into freshData and trigger sync
                        freshData.app_lock_config = localConfig;
                        cloudConfig = localConfig;
                        window.syncProfileWithCloud();
                    } catch (e) {
                        console.error("Error migrating local App Lock config to cloud", e);
                    }
                }

                if (window._0x83e2) {
                    const configChanged = JSON.stringify(window._0x83e2.config) !== JSON.stringify(cloudConfig);
                    if (configChanged) {
                        window._0x83e2.config = cloudConfig;
                        window._0x83e2.checkAppLock();
                    }
                }
                if (cloudConfig) {
                    localStorage.removeItem('app_lock_config');
                }

                // Fetch Mobile Settings from user profile document (migrated from settings or settings.mobile or mobile)
                let ms = null;
                if (freshData.settings) {
                    if (freshData.settings.mobile) {
                        ms = freshData.settings.mobile;
                    } else {
                        const hasKeys = Object.keys(freshData.settings).some(k => k.startsWith('suite_') || k === 'active_layer_mode' || k === 'font');
                        if (hasKeys) {
                            ms = freshData.settings;
                        }
                    }
                }
                if (!ms && freshData.mobile) {
                    ms = freshData.mobile;
                }
                if (ms) {
                    // Only apply cloud settings if no local setting sync is active or pending write
                    const isPending = isSettingsSyncPending || (snapshot.metadata && snapshot.metadata.hasPendingWrites);
                    if (!isPending) {
                        // 1. Resolve Font Mode / Font Name
                        let fontMode = null;
                        if (ms.active_layer_mode !== undefined && ms.active_layer_mode !== null) {
                            fontMode = String(ms.active_layer_mode);
                        } else if (ms.font !== undefined && ms.font !== null) {
                            const fontVal = String(ms.font).trim();
                            if (FONT_MAP[fontVal]) {
                                fontMode = fontVal;
                            } else {
                                fontMode = REVERSE_FONT_MAP[fontVal] || "1";
                            }
                        }

                        if (fontMode) {
                            if (localStorage.getItem('active_layer_mode') !== fontMode) {
                                if (window.safeStorage) {
                                    window.safeStorage.setItem('active_layer_mode', fontMode, true);
                                } else {
                                    localStorage.setItem('active_layer_mode', fontMode);
                                    window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'active_layer_mode', val: fontMode, isRemote: true } }));
                                }
                            }
                            // Force apply the font mode to the UI
                            const parsedMode = parseInt(fontMode);
                            if (!isNaN(parsedMode)) {
                                window.syncFontMode(parsedMode);
                            }
                        }

                        // 2. Resolve all other keys
                        Object.entries(ms).forEach(([key, val]) => {
                            if (key === 'font' || key === 'active_layer_mode') return; // Handled above
                            if (localStorage.getItem(key) !== String(val)) {
                                if (window.safeStorage) {
                                    window.safeStorage.setItem(key, val, true);
                                } else {
                                    localStorage.setItem(key, val);
                                    window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key, val, isRemote: true } }));
                                }
                            }
                        });
                    }
                }

                // Maintain analytics telemetry and PDF download metrics purely in memory from Firestore user document (Zero LocalStorage)
                if (freshData.analytics) {
                    try {
                        window._cosmoAnalyticsCloudState = freshData.analytics;
                        if (window.userData) window.userData.analytics = freshData.analytics;
                        if (window.parent && window.parent.userData) window.parent.userData.analytics = freshData.analytics;
                        if (window.parent) window.parent._cosmoAnalyticsCloudState = freshData.analytics;
                        if (window.top) window.top._cosmoAnalyticsCloudState = freshData.analytics;

                        const detail = { analytics: freshData.analytics };
                        window.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail: freshData.analytics }));
                        try { if (window.parent && window.parent !== window) window.parent.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail: freshData.analytics })); } catch (e) { }
                        try { if (window.top && window.top !== window) window.top.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail: freshData.analytics })); } catch (e) { }
                        try {
                            document.querySelectorAll('iframe').forEach(iframe => {
                                if (iframe.contentWindow) iframe.contentWindow.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail: freshData.analytics }));
                            });
                        } catch (e) { }
                    } catch (anErr) { }
                }
                if (freshData.pdf_telemetry) {
                    try {
                        const curDl = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.downloads_count) || 0;
                        const curEnc = (window._cosmoPdfTelemetryState && window._cosmoPdfTelemetryState.encrypted_downloads_count) || 0;

                        const rawDl = typeof freshData.pdf_telemetry.downloads_count === 'number' ? freshData.pdf_telemetry.downloads_count : parseInt(freshData.pdf_telemetry.downloads_count || '0', 10);
                        const rawEnc = typeof freshData.pdf_telemetry.encrypted_downloads_count === 'number' ? freshData.pdf_telemetry.encrypted_downloads_count : parseInt(freshData.pdf_telemetry.encrypted_downloads_count || '0', 10);

                        const snapDl = isNaN(rawDl) ? 0 : rawDl;
                        const snapEnc = isNaN(rawEnc) ? 0 : rawEnc;

                        // Guarantee telemetry monotonically increments and never resets to 0
                        const finalDl = Math.max(curDl, snapDl);
                        const finalEnc = Math.max(curEnc, snapEnc);

                        const telemState = {
                            downloads_count: finalDl,
                            encrypted_downloads_count: finalEnc,
                            last_updated: freshData.pdf_telemetry.last_updated || new Date().toISOString()
                        };

                        window._cosmoPdfTelemetryState = telemState;
                        if (window.userData) window.userData.pdf_telemetry = telemState;
                        if (window.parent && window.parent.userData) window.parent.userData.pdf_telemetry = telemState;
                        if (window.parent) window.parent._cosmoPdfTelemetryState = telemState;
                        if (window.top) window.top._cosmoPdfTelemetryState = telemState;

                        const detail = {
                            count: telemState.downloads_count,
                            encryptedCount: telemState.encrypted_downloads_count
                        };
                        try { window.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail })); } catch (e) { }
                        try { if (window.parent && window.parent !== window) window.parent.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail })); } catch (e) { }
                        try { if (window.top && window.top !== window) window.top.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail })); } catch (e) { }
                        try {
                            document.querySelectorAll('iframe').forEach(iframe => {
                                if (iframe.contentWindow) {
                                    iframe.contentWindow.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail }));
                                    if (typeof iframe.contentWindow.updateAnalyticsPdfDownloadsCount === 'function') {
                                        iframe.contentWindow.updateAnalyticsPdfDownloadsCount(telemState.downloads_count, telemState.encrypted_downloads_count);
                                    }
                                }
                            });
                        } catch (e) { }
                    } catch (pdfErr) { }
                }

                const iframes = document.querySelectorAll('iframe');
                iframes.forEach(iframe => {
                    try {
                        if (iframe.contentWindow) {
                            iframe.contentWindow.userData = freshData;
                            iframe.contentWindow.postMessage({ type: 'login-success', isLoggedIn: true, user: freshData }, '*');
                            if (typeof iframe.contentWindow.switchToProfilePanel === 'function') {
                                iframe.contentWindow.switchToProfilePanel(freshData);
                            }

                            if (freshData.avatarUrl && freshData.avatarUrl !== 'none') {
                                try { iframe.contentWindow.localStorage.setItem('userProfileImg', freshData.avatarUrl); } catch (e) { }
                                if (typeof iframe.contentWindow.profileImgData !== 'undefined') iframe.contentWindow.profileImgData = freshData.avatarUrl;
                            } else if (freshData.avatarUrl === 'none') {
                                try { iframe.contentWindow.localStorage.setItem('userProfileImg', 'none'); } catch (e) { }
                                if (typeof iframe.contentWindow.profileImgData !== 'undefined') iframe.contentWindow.profileImgData = 'none';
                            } else {
                                try { iframe.contentWindow.localStorage.removeItem('userProfileImg'); } catch (e) { }
                                if (typeof iframe.contentWindow.profileImgData !== 'undefined') iframe.contentWindow.profileImgData = null;
                            }

                            if (typeof iframe.contentWindow.updateMomentumDash === 'function') {
                                iframe.contentWindow.updateMomentumDash();
                            }
                            if (typeof iframe.contentWindow.updateAvatarUI === 'function') {
                                iframe.contentWindow.updateAvatarUI();
                            }
                        }
                    } catch (e) { }
                });

                const hasCloudDataChanged = !isInitialSnapshot && oldDataStr && oldDataStr !== newDataStr;
                if (hasCloudDataChanged) {
                    const toastFn = window.pop || window.showToast || (window.parent && (window.parent.pop || window.parent.showToast));
                    if (typeof toastFn === 'function') {
                        toastFn("Security Sync: Profile Updated from Cloud", "success");
                    }
                }
                lastSyncedSnapshotDataStr = newDataStr;
                isInitialSnapshot = false;
            }
        }, (error) => {
            if (error && error.code === 'permission-denied') {
                if (window.userSnapshotListener) {
                    try { window.userSnapshotListener(); } catch (e) { }
                    window.userSnapshotListener = null;
                }
                return;
            }
            console.error("Snapshot error:", error);
        });
    } else {
        localStorage.removeItem('last_active_uid');
        sessionStorage.removeItem('__cosmo_session_active');
        localStorage.removeItem('userStoreEnc');
        localStorage.removeItem('userStore');
        localStorage.removeItem('cosmo_crypt_purchase_history');
        localStorage.removeItem('cosmo_user_plan');
        localStorage.removeItem('current_plan');
        localStorage.removeItem('cosmocrypt_sub_enc');
        localStorage.removeItem('cosmocrypt_sub_history');
        window.userData = null;
        if (window.parent) window.parent.userData = null;

        if (window.cosmoSubscription) {
            await window.cosmoSubscription.clearLocalCache();
        }
        localStorage.removeItem('user_subscription');
        localStorage.removeItem('premiumActive');
        localStorage.removeItem('had_premium_plan');
        localStorage.removeItem('last_notified_subscription');
        if (typeof originalRemoveItem === 'function') {
            originalRemoveItem.call(localStorage, 'user_subscription');
            originalRemoveItem.call(localStorage, 'premiumActive');
            originalRemoveItem.call(localStorage, 'had_premium_plan');
            originalRemoveItem.call(localStorage, 'last_notified_subscription');
        }
        window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: 'element', newValue: 'element' } }));
        window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: 'false' } }));
        window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_crypt_purchase_history', val: null } }));
        window.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: 'element' } }));
        window.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: 'element' } }));

        // Broadcast logout to all child iframes
        document.querySelectorAll('iframe').forEach(iframe => {
            try {
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage({ type: 'logout', isLoggedIn: false, plan: 'element' }, '*');
                    iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: 'element', newValue: 'element' } }));
                    iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: 'false' } }));
                    iframe.contentWindow.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_crypt_purchase_history', val: null } }));
                    iframe.contentWindow.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: 'element' } }));
                    iframe.contentWindow.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: 'element' } }));
                }
            } catch (e) { }
        });

        const isExplicitLogout = localStorage.getItem('explicit_logout') === 'true';
        if (isExplicitLogout) {
            console.warn("⚠️ Sync Alert: Cleaning up local session per logout request.");
            localStorage.removeItem('explicit_logout');
            if (typeof closeSPA === 'function') closeSPA();
        } else {
            setTimeout(initGoogleOneTap, 1000);
        }
    }
    if (typeof updateTelemetryStats === 'function') updateTelemetryStats();
    if (typeof loadDensityStats === 'function') loadDensityStats();
    if (typeof updateSandboxData === 'function') updateSandboxData();
});

// Bridge Logout Enhancement: Ensure persistence is cleared across reloads
const originalLogout = window.suiteAuthBridge.logout;
window.suiteAuthBridge.logout = async () => {
    localStorage.setItem('explicit_logout', 'true');
    const result = await originalLogout();
    if (result.success) {
        localStorage.removeItem('userStoreEnc');
        localStorage.removeItem('userStore');
        localStorage.removeItem('last_active_uid');
        localStorage.removeItem('cosmo_crypt_purchase_history');
        localStorage.removeItem('cosmo_user_plan');
        localStorage.removeItem('current_plan');
        localStorage.removeItem('cosmocrypt_sub_enc');
        localStorage.removeItem('cosmocrypt_sub_history');
        // 🔄 Clear device ID on logout so next login starts fresh
        localStorage.removeItem('cosmo_device_id');
        sessionStorage.clear();
        // Clear plan cache — user must re-login to restore plan
        if (window.cosmoSubscription) {
            await window.cosmoSubscription.clearLocalCache();
        }
        localStorage.removeItem('user_subscription');
        localStorage.removeItem('premiumActive');
        localStorage.removeItem('had_premium_plan');
        localStorage.removeItem('last_notified_subscription');
        if (typeof originalRemoveItem === 'function') {
            originalRemoveItem.call(localStorage, 'user_subscription');
            originalRemoveItem.call(localStorage, 'premiumActive');
            originalRemoveItem.call(localStorage, 'had_premium_plan');
            originalRemoveItem.call(localStorage, 'last_notified_subscription');
        }
        window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'user_subscription', val: 'element', newValue: 'element' } }));
        window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'premiumActive', val: 'false' } }));
        window.dispatchEvent(new CustomEvent('safeStorageChange', { detail: { key: 'cosmo_crypt_purchase_history', val: null } }));
        window.dispatchEvent(new CustomEvent('cosmoCryptPlanChanged', { detail: { plan: 'element' } }));
        window.dispatchEvent(new CustomEvent('cosmoPlanChanged', { detail: { plan: 'element' } }));
    }
    return result;
};

// GLOBAL DEBOUNCED SETTINGS SYNC FOR MOBILE
const SYNCCABLE_KEYS = [
    'suite_sound_muted', 'suite_sound_volume',
    'suite_typing_enabled', 'suite_typing_volume', 'suite_typing_tune',
    'suite_bottom_nav_enabled', 'suite_bottom_nav_volume',
    'nav_anim_enabled', 'active_layer_mode',
    'session_protocol_enabled', 'fullscreen_on_click'
];

window.addEventListener('safeStorageChange', (e) => {
    if (e.detail && e.detail.isRemote) return;

    if (e.detail && SYNCCABLE_KEYS.includes(e.detail.key)) {
        isSettingsSyncPending = true;
        if (settingsSyncTimeout) clearTimeout(settingsSyncTimeout);

        // Sync session protocol or cosmo search instantly
        if (e.detail.key === 'session_protocol_enabled' || e.detail.key === 'cosmo_search_enabled') {
            if (window.suiteAuthBridge && typeof window.suiteAuthBridge.pushSettingsToCloud === 'function') {
                window.suiteAuthBridge.pushSettingsToCloud();
            }
        } else {
            settingsSyncTimeout = setTimeout(async () => {
                settingsSyncTimeout = null;
                if (window.suiteAuthBridge && typeof window.suiteAuthBridge.pushSettingsToCloud === 'function') {
                    await window.suiteAuthBridge.pushSettingsToCloud();
                }
            }, 500);
        }
    }
});

// INSTANT FIREBASE FIRESTORE SYNC FOR ANALYTICS AND PDF TELEMETRY
window.suiteAuthBridge.syncAnalyticsToCloud = async (analyticsObj, pdfTelemetryObj) => {
    try {
        if (localStorage.getItem('explicit_logout') === 'true' || window._userExplicitlyLoggedOut) return false;
        const u = auth.currentUser;
        if (!u || !u.uid) return false;

        let finalAnalytics = analyticsObj;

        // Helper to retrieve template data directly from CosmoTemplateStore or window.userData
        const getModuleData = (key, legacyKey) => {
            const store = (typeof window.CosmoTemplateStore !== 'undefined') ? window.CosmoTemplateStore : (window.parent && window.parent.CosmoTemplateStore ? window.parent.CosmoTemplateStore : null);
            let d = store ? store.get(key, null) : null;
            if (!d && legacyKey) d = store ? store.get(legacyKey, null) : null;

            if (!d) {
                try {
                    const targets = [window.userData, (window.parent && window.parent !== window) ? window.parent.userData : null, (window.top && window.top !== window) ? window.top.userData : null];
                    for (const t of targets) {
                        if (t && t.template_vaults) {
                            const clean = store ? store.getCleanKey(key) : key;
                            if (t.template_vaults[clean]) { d = t.template_vaults[clean]; break; }
                            if (t.template_vaults[key]) { d = t.template_vaults[key]; break; }
                            if (legacyKey && t.template_vaults[legacyKey]) { d = t.template_vaults[legacyKey]; break; }
                        }
                    }
                } catch (e) { }
            }
            if (!d) {
                try {
                    const raw = localStorage.getItem(key) || (legacyKey ? localStorage.getItem(legacyKey) : null);
                    if (raw) d = JSON.parse(raw);
                } catch (e) { }
            }
            return d;
        };

        const countCardsFromData = (vaultData) => {
            if (!vaultData) return 0;
            let count = 0;
            const isNotTrash = (item) => {
                if (!item || typeof item !== 'object') return false;
                if (item.isTrash === true || item.isDeleted === true || item.inTrash === true || item.deleted === true || item.in_trash === true || item.trash === true) return false;
                if (item.status === 'trash' || item.status === 'deleted' || item.status === 'bin' || item.status === 'recycle_bin') return false;
                if (item.category === 'trash' || item.category === 'deleted' || item.category === 'bin' || item.category === 'Recycle Bin') return false;
                if (item.env === 'trash' || item.env === 'deleted' || item.env === 'bin') return false;
                return true;
            };

            if (Array.isArray(vaultData)) {
                if (vaultData.length === 0) return 0;
                const first = vaultData[0];
                if (first && typeof first === 'object' && (Array.isArray(first.subsections) || Array.isArray(first.cards))) {
                    vaultData.forEach(sec => {
                        if (sec && Array.isArray(sec.subsections)) {
                            sec.subsections.forEach(sub => {
                                if (sub && Array.isArray(sub.cards)) {
                                    sub.cards.forEach(c => { if (isNotTrash(c)) count++; });
                                }
                            });
                        } else if (sec && Array.isArray(sec.cards)) {
                            sec.cards.forEach(c => { if (isNotTrash(c)) count++; });
                        }
                    });
                } else {
                    vaultData.forEach(item => {
                        if (isNotTrash(item)) count++;
                    });
                }
            } else if (typeof vaultData === 'object') {
                const keys = ['documents', 'items', 'cards', 'apiKeys', 'notes', 'banks', 'vaultData'];
                keys.forEach(k => {
                    if (Array.isArray(vaultData[k])) {
                        vaultData[k].forEach(item => {
                            if (isNotTrash(item)) count++;
                        });
                    }
                });
            }
            return count;
        };

        const planName = (window.cosmoSubscription && window.cosmoSubscription.plan) ? window.cosmoSubscription.plan.toUpperCase() : 'ELEMENT';
        const limitVal = (window.cosmoSubscription && window.cosmoSubscription.limit) ? window.cosmoSubscription.limit : 10;
        const usedNodes = countCardsFromData(getModuleData('node_infrastructure', 'cosmo_crypt_node_infrastructure_v1'));
        const docData = getModuleData('identity_vault_documents', 'payvault_documents');
        const docCount = countCardsFromData(docData);

        const finCount = countCardsFromData(getModuleData('financial_credentials', 'cosmo_crypt_financial_credentials_v1'));
        const payCount = countCardsFromData(getModuleData('payment_gateways', 'cosmo_crypt_payment_gateways_v1'));
        const devCount = countCardsFromData(getModuleData('developer_endpoints', 'cosmo_crypt_developer_endpoints_v1'));
        const dnsCount = countCardsFromData(getModuleData('dns_management', 'cosmo_crypt_dns_management_v1'));
        const wsCount = countCardsFromData(getModuleData('workspace_integration', 'cosmo_crypt_workspace_integration_v1'));
        const merchCount = countCardsFromData(getModuleData('merchant_identities', 'cosmo_crypt_merchant_identities_v1'));
        const natCount = countCardsFromData(getModuleData('national_identity', 'cosmo_crypt_national_identity_v1'));
        const encCount = countCardsFromData(getModuleData('encrypted_partitions_notes', 'cosmo_encrypted_partitions_notes_v1'));
        const sysCount = countCardsFromData(getModuleData('system_encryption', 'cosmo_crypt_system_encryption_v1'));

        const usEastNodes = Math.ceil(usedNodes * 0.48);
        const apSouthNodes = Math.floor(usedNodes * 0.31);
        const euCentralNodes = Math.max(0, usedNodes - usEastNodes - apSouthNodes);

        const isUnlimited = (planName.includes('QUANTUM') || planName.includes('ULTRA') || limitVal === 'Unlimited');
        const cpuDisplay = isUnlimited ? 'Unlimited' : `${Math.min(100, Math.round((usedNodes / limitVal) * 100))}%`;
        const memDisplay = isUnlimited ? `${usedNodes} Cards / Unlimited` : `${usedNodes} Cards / ${limitVal} Cards`;
        const vaultStorageUsage = isUnlimited ? 'Unlimited' : `${Math.min(100, Math.round((docCount / limitVal) * 100))}%`;

        const liveAnalyticsObj = {
            node_infrastructure: {
                used_nodes: usedNodes,
                node_limit: limitVal,
                plan_name: planName,
                cpu_cluster_utilization: cpuDisplay,
                memory_allocation: memDisplay,
                regions: {
                    "us-east-1": usEastNodes,
                    "ap-south-1": apSouthNodes,
                    "eu-central-1": euCentralNodes
                }
            },
            identity_vault_documents: {
                total_documents: docCount,
                storage_usage_percent: vaultStorageUsage
            },
            financial_credentials: { count: finCount },
            payment_gateways: { count: payCount },
            developer_endpoints: { count: devCount },
            dns_management: { count: dnsCount },
            workspace_integration: { count: wsCount },
            merchant_identities: { count: merchCount },
            national_identity: { count: natCount },
            encrypted_partitions_notes: { count: encCount },
            system_encryption: { count: sysCount },
            last_updated: new Date().toISOString()
        };

        // Merge live calculated analytics with incoming analyticsObj (if provided)
        const mergedAnalytics = Object.assign({}, liveAnalyticsObj, finalAnalytics || {});

        // Process PDF Telemetry metrics in memory (Zero LocalStorage)
        const existingTelem = window._cosmoPdfTelemetryState ||
            (window.userData && window.userData.pdf_telemetry) ||
            (window.parent && window.parent._cosmoPdfTelemetryState) ||
            (window.parent && window.parent.userData && window.parent.userData.pdf_telemetry) ||
            (window.top && window.top._cosmoPdfTelemetryState) ||
            null;

        let dlCount = existingTelem ? Math.max(0, parseInt(existingTelem.downloads_count || '0', 10) || 0) : 0;
        let encCountPdf = existingTelem ? Math.max(0, parseInt(existingTelem.encrypted_downloads_count || '0', 10) || 0) : 0;

        let hasNewPdfUpdate = false;
        if (pdfTelemetryObj && typeof pdfTelemetryObj === 'object') {
            hasNewPdfUpdate = true;
            if (typeof pdfTelemetryObj.downloads_count === 'number' && !isNaN(pdfTelemetryObj.downloads_count)) {
                dlCount = Math.max(dlCount, pdfTelemetryObj.downloads_count);
            }
            if (typeof pdfTelemetryObj.encrypted_downloads_count === 'number' && !isNaN(pdfTelemetryObj.encrypted_downloads_count)) {
                encCountPdf = Math.max(encCountPdf, pdfTelemetryObj.encrypted_downloads_count);
            }
        }

        const finalPdfTelemetry = {
            downloads_count: dlCount,
            encrypted_downloads_count: encCountPdf,
            last_updated: (pdfTelemetryObj && pdfTelemetryObj.last_updated) ? pdfTelemetryObj.last_updated : ((existingTelem && existingTelem.last_updated) ? existingTelem.last_updated : new Date().toISOString())
        };

        // Only update in-memory state if an existing telemetry exists or an explicit update was provided
        if (existingTelem || hasNewPdfUpdate) {
            window._cosmoPdfTelemetryState = finalPdfTelemetry;
            if (window.userData) window.userData.pdf_telemetry = finalPdfTelemetry;
            if (window.parent && window.parent.userData) window.parent.userData.pdf_telemetry = finalPdfTelemetry;
            if (window.parent) window.parent._cosmoPdfTelemetryState = finalPdfTelemetry;
            if (window.top) window.top._cosmoPdfTelemetryState = finalPdfTelemetry;
        }

        window._cosmoAnalyticsCloudState = mergedAnalytics;
        if (window.userData) window.userData.analytics = mergedAnalytics;
        if (window.parent && window.parent.userData) window.parent.userData.analytics = mergedAnalytics;
        if (window.parent) window.parent._cosmoAnalyticsCloudState = mergedAnalytics;
        if (window.top) window.top._cosmoAnalyticsCloudState = mergedAnalytics;

        // Proactive purge from LocalStorage (Zero LocalStorage Policy)
        try {
            localStorage.removeItem('cosmo_analytics');
            localStorage.removeItem('cosmo_pdf_downloads_count');
            localStorage.removeItem('cosmo_pdf_encrypted_downloads_count');
        } catch (lsErr) { }

        const checkComparable = {
            analytics: Object.assign({}, mergedAnalytics),
            pdf_telemetry: (hasNewPdfUpdate || dlCount > 0) ? { downloads_count: dlCount, encrypted_downloads_count: encCountPdf } : null
        };
        delete checkComparable.analytics.last_updated;
        const newSyncStr = JSON.stringify(checkComparable);

        if (!pdfTelemetryObj && window._lastSyncedAnalyticsStr === newSyncStr) {
            return true;
        }
        window._lastSyncedAnalyticsStr = newSyncStr;

        // Dispatch cross-frame real-time event
        if (!window._isBroadcastingAnalyticsSync) {
            window._isBroadcastingAnalyticsSync = true;
            const detail = { analytics: mergedAnalytics };
            try { window.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail })); } catch (e) { }
            try { if (window.parent && window.parent !== window) window.parent.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail })); } catch (e) { }
            try { if (window.top && window.top !== window) window.top.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail })); } catch (e) { }
            try {
                document.querySelectorAll('iframe').forEach(iframe => {
                    try {
                        if (iframe.contentWindow) iframe.contentWindow.dispatchEvent(new CustomEvent('cosmo_analytics_updated', { detail }));
                    } catch (crossErr) { }
                });
            } catch (e) { }
            setTimeout(() => { window._isBroadcastingAnalyticsSync = false; }, 200);
        }

        if (hasNewPdfUpdate || dlCount > 0 || encCountPdf > 0) {
            const pdfDetail = { count: dlCount, encryptedCount: encCountPdf };
            try { window.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail: pdfDetail })); } catch (e) { }
            try { if (window.parent && window.parent !== window) window.parent.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail: pdfDetail })); } catch (e) { }
            try { if (window.top && window.top !== window) window.top.dispatchEvent(new CustomEvent('cosmo_pdf_download_updated', { detail: pdfDetail })); } catch (e) { }
        }

        // Build Payload for Firestore: ONLY include pdf_telemetry when explicitly modified or confirmed non-zero
        // This ensures analytics sync never overwrites existing Firestore telemetry with 0
        const payload = {};
        if (mergedAnalytics) {
            payload.analytics = JSON.parse(JSON.stringify(mergedAnalytics));
        }
        if (hasNewPdfUpdate || (dlCount > 0 && !pdfTelemetryObj)) {
            payload.pdf_telemetry = JSON.parse(JSON.stringify(finalPdfTelemetry));
        }

        // 3. Instantly write to Firebase Firestore (0ms cloud sync)
        if (typeof setDoc === 'function' && typeof doc === 'function' && typeof db !== 'undefined') {
            const cleanPayload = JSON.parse(JSON.stringify(payload));
            await setDoc(doc(db, 'users', u.uid), cleanPayload, { merge: true });
            return true;
        }
    } catch (e) {
        console.warn('[CosmoAnalyticsCloud] Instant Firebase sync warning:', e);
    }
    return false;
};

window.syncAnalyticsToCloud = window.suiteAuthBridge.syncAnalyticsToCloud;
